import { GameState, BusinessNode, formatZoneTag } from '../domain/types.ts';
import { CommandDispatcher } from '../app/commands.ts';
import { GAME_EVENTS } from '../simulation/eventEngine.ts';
import { LocationService } from '../map/locationService.ts';
import { SearchService } from '../map/searchService.ts';
import { SIDEWALK_MENU, getRegionalMenu, getRegionFromCoordinates } from '../config/menuConfig.ts';
import { GAME_CONFIG } from '../config/gameConfig.ts';
import { Camera } from '../render/camera.ts';
import { CryptoStore } from '../persistence/cryptoStore.ts';
import { SaveStore, PersistentSave } from '../persistence/saveStore.ts';
import { getApiUrl } from '../config/apiConfig.ts';
import { SURVIVAL_PERKS, getStallLevels } from '../config/progressionConfig.ts';
import { AudioService } from '../audio/audioService.ts';

function formatCompactAddress(rawLabel?: string): string {
  if (!rawLabel) return 'Đang tải địa bàn...';
  const parts = rawLabel.split(',').map((s) => s.trim()).filter(Boolean);
  if (parts.length <= 2) return rawLabel;
  const p1 = parts[0]!;
  const p2 = parts.find((p, idx) => idx > 0 && !/^\d{4,6}$/.test(p) && p !== 'Việt Nam' && p !== 'Vietnam') ?? parts[1]!;
  return `${p1}, ${p2}`;
}

export interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

export class UIManager {
  private container: HTMLElement;
  private dispatcher: CommandDispatcher;
  private camera?: Camera | undefined;

  private hudElement: HTMLElement;
  private actionSheetElement: HTMLElement;
  private eventBannerElement: HTMLElement;
  private locationModalElement: HTMLElement;
  private lobbyModalElement: HTMLElement;
  private menuModalElement: HTMLElement;
  private salesModalElement: HTMLElement;
  private renameModalElement: HTMLElement;
  private loadingOverlayElement: HTMLElement;
  private zoomControlsElement: HTMLElement;
  private dayReportElement: HTMLElement;
  private confirmModalElement: HTMLElement;
  private upgradeModalElement: HTMLElement;
  private milestoneElement: HTMLElement;
  private accountModalElement: HTMLElement;
  private celebrationModalElement: HTMLElement;
  private perksModalElement: HTMLElement;
  private pauseModalElement: HTMLElement;
  private installGuideModalElement: HTMLElement;
  private isPauseMenuOpen = false;
  private deferredInstallPrompt: BeforeInstallPromptEvent | null = null;

  private isMenuOpen = false;
  private isSalesOpen = false;
  private isUpgradeOpen = false;
  private isTopMenuOpen = false;
  private isHudInitialized = false;
  private popdownMenuElement: HTMLElement;
  private popdownBackdropElement: HTMLElement;
  private isLobbyRendered = false;
  private lobbyStep: number = 1;
  private lobbyLocation: { lat: number; lon: number; label: string } | null = null;
  private lobbyUsername: string = '';
  private lobbyStallName: string = '';
  private neighborhoodLoader?: ((lat: number, lon: number, label: string) => Promise<void>) | undefined;
  private lastWeatherType: string | null = null;
  private currentMilestoneId: string | null = null;
  private milestoneTimer: number | null = null;
  private lastState?: Readonly<GameState> | undefined;
  private renderedEventId: string | null = null;
  private mapErrorModalElement: HTMLElement;
  private saleToastElement: HTMLElement;
  private splashScreenElement: HTMLElement | null = null;
  private lastRenderedSaleTime = 0;
  private saleToastTimer: number | null = null;
  private isLoreDismissed = false;
  private lastQuote: string | null = null;
  private lastActionSheetKey: string | null = null;
  private lastMenuModalKey: string | null = null;
  private lastUpgradeModalKey: string | null = null;
  private lastSalesModalKey: string | null = null;
  private lastReportDayNumber: number = -1;
  private lastCelebrationId: string | null = null;
  private howToPlayModalElement: HTMLElement;
  private trendsModalElement: HTMLElement;
  private audioModalElement: HTMLElement;
  private currentHowToPlayTab: 'guide' | 'tips' | 'progression' = 'guide';

  constructor(container: HTMLElement, dispatcher: CommandDispatcher, camera?: Camera) {
    this.container = container;
    this.dispatcher = dispatcher;
    this.camera = camera;

    this.hudElement = document.createElement('div');
    this.hudElement.className = 'hud-container';

    this.eventBannerElement = document.createElement('div');
    this.eventBannerElement.className = 'event-banner-container';

    this.actionSheetElement = document.createElement('div');
    this.actionSheetElement.className = 'actionsheet-container';

    this.zoomControlsElement = document.createElement('div');
    this.zoomControlsElement.className = 'zoom-controls-container';

    this.locationModalElement = document.createElement('div');
    this.locationModalElement.className = 'modal-backdrop';
    this.locationModalElement.style.display = 'none';

    this.lobbyModalElement = document.createElement('div');
    this.lobbyModalElement.className = 'modal-backdrop lobby-backdrop';
    this.lobbyModalElement.style.display = 'none';

    this.menuModalElement = document.createElement('div');
    this.menuModalElement.className = 'modal-backdrop';
    this.menuModalElement.style.display = 'none';

    this.salesModalElement = document.createElement('div');
    this.salesModalElement.className = 'modal-backdrop';
    this.salesModalElement.style.display = 'none';

    this.renameModalElement = document.createElement('div');
    this.renameModalElement.className = 'modal-backdrop';
    this.renameModalElement.style.display = 'none';

    this.loadingOverlayElement = document.createElement('div');
    this.loadingOverlayElement.className = 'loading-overlay';
    this.loadingOverlayElement.style.display = 'none';

    this.dayReportElement = document.createElement('div');
    this.dayReportElement.className = 'modal-backdrop';
    this.dayReportElement.style.display = 'none';

    this.confirmModalElement = document.createElement('div');
    this.confirmModalElement.className = 'modal-backdrop';
    this.confirmModalElement.style.display = 'none';

    this.upgradeModalElement = document.createElement('div');
    this.upgradeModalElement.className = 'modal-backdrop';
    this.upgradeModalElement.style.display = 'none';

    this.milestoneElement = document.createElement('div');
    this.milestoneElement.className = 'milestone-container';
    this.milestoneElement.style.display = 'none';

    this.mapErrorModalElement = document.createElement('div');
    this.mapErrorModalElement.className = 'modal-backdrop';
    this.mapErrorModalElement.style.display = 'none';

    this.saleToastElement = document.createElement('div');
    this.saleToastElement.className = 'sale-toast-floating';
    this.saleToastElement.style.display = 'none';

    this.popdownBackdropElement = document.createElement('div');
    this.popdownBackdropElement.className = 'popdown-backdrop';
    this.popdownBackdropElement.style.display = 'none';

    this.popdownMenuElement = document.createElement('div');
    this.popdownMenuElement.className = 'hud-popdown-menu';
    this.popdownMenuElement.style.display = 'none';

    this.accountModalElement = document.createElement('div');
    this.accountModalElement.className = 'modal-backdrop';
    this.accountModalElement.style.display = 'none';

    this.celebrationModalElement = document.createElement('div');
    this.celebrationModalElement.className = 'modal-backdrop celebration-backdrop';
    this.celebrationModalElement.style.display = 'none';

    this.perksModalElement = document.createElement('div');
    this.perksModalElement.className = 'modal-backdrop';
    this.perksModalElement.style.display = 'none';

    this.howToPlayModalElement = document.createElement('div');
    this.howToPlayModalElement.className = 'modal-backdrop';
    this.howToPlayModalElement.style.display = 'none';

    this.trendsModalElement = document.createElement('div');
    this.trendsModalElement.className = 'modal-backdrop';
    this.trendsModalElement.id = 'modal-trends-feed';
    this.trendsModalElement.style.display = 'none';

    this.audioModalElement = document.createElement('div');
    this.audioModalElement.className = 'modal-backdrop';
    this.audioModalElement.id = 'modal-audio-settings';
    this.audioModalElement.style.display = 'none';

    this.pauseModalElement = document.createElement('div');
    this.pauseModalElement.className = 'modal-backdrop pause-backdrop';
    this.pauseModalElement.id = 'modal-pause-game';
    this.pauseModalElement.style.display = 'none';

    this.installGuideModalElement = document.createElement('div');
    this.installGuideModalElement.className = 'modal-backdrop';
    this.installGuideModalElement.id = 'modal-install-guide';
    this.installGuideModalElement.style.display = 'none';

    this.injectStyles();
    this.container.appendChild(this.hudElement);
    this.container.appendChild(this.eventBannerElement);
    this.container.appendChild(this.actionSheetElement);
    this.container.appendChild(this.zoomControlsElement);
    this.container.appendChild(this.locationModalElement);
    this.container.appendChild(this.lobbyModalElement);
    this.container.appendChild(this.menuModalElement);
    this.container.appendChild(this.salesModalElement);
    this.container.appendChild(this.renameModalElement);
    this.container.appendChild(this.loadingOverlayElement);
    this.container.appendChild(this.dayReportElement);
    this.container.appendChild(this.confirmModalElement);
    this.container.appendChild(this.upgradeModalElement);
    this.container.appendChild(this.milestoneElement);
    this.container.appendChild(this.mapErrorModalElement);
    this.container.appendChild(this.saleToastElement);
    this.container.appendChild(this.popdownBackdropElement);
    this.container.appendChild(this.popdownMenuElement);
    this.container.appendChild(this.accountModalElement);
    this.container.appendChild(this.celebrationModalElement);
    this.container.appendChild(this.perksModalElement);
    this.container.appendChild(this.howToPlayModalElement);
    this.container.appendChild(this.trendsModalElement);
    this.container.appendChild(this.audioModalElement);
    this.container.appendChild(this.pauseModalElement);
    this.container.appendChild(this.installGuideModalElement);

    this.renderZoomControls();

    if (typeof window !== 'undefined') {
      window.addEventListener('beforeinstallprompt', (e: Event) => {
        e.preventDefault();
        this.deferredInstallPrompt = e as BeforeInstallPromptEvent;
      });
    }

    this.splashScreenElement = document.getElementById('splash-screen');
    if (this.splashScreenElement) {
      const handleSplashClick = (e: Event) => {
        e.preventDefault();
        e.stopPropagation();
        if (this.lastState && this.lastState.phase !== 'BOOT') {
          return;
        }
        try {
          const audio = AudioService.getInstance();
          audio.init();
          audio.playTap();
        } catch (err) {
          console.warn('[UIManager] Audio unlock failed:', err);
        }
        this.isLobbyRendered = false;
        this.lobbyStep = 1;
        this.dispatcher.dispatch({ type: 'GO_TO_LOBBY' });
      };
      this.splashScreenElement.addEventListener('click', handleSplashClick);
    }

    // Backdrop click để đóng audio modal
    this.audioModalElement.addEventListener('click', (e: Event) => {
      const target = e.target as HTMLElement;
      if (target === this.audioModalElement || target.closest('#btn-close-audio-modal')) {
        e.preventDefault();
        e.stopPropagation();
        this.audioModalElement.style.display = 'none';
      }
    });

    // Backdrop click để đóng menu
    this.popdownBackdropElement.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.closeTopMenu();
    });

    // Backdrop click để đóng account modal
    this.accountModalElement.addEventListener('click', (e: Event) => {
      const target = e.target as HTMLElement;
      if (target === this.accountModalElement || target.closest('#btn-close-account')) {
        e.preventDefault();
        e.stopPropagation();
        this.accountModalElement.style.display = 'none';
      }
    });

    // Backdrop / close click cho perks modal
    this.perksModalElement.addEventListener('click', (e: Event) => {
      const target = e.target as HTMLElement;
      if (target === this.perksModalElement || target.closest('#btn-close-perks') || target.closest('#btn-confirm-perks')) {
        e.preventDefault();
        e.stopPropagation();
        this.perksModalElement.style.display = 'none';
      }
    });

    // Backdrop / close / tab switch click cho how to play modal
    this.howToPlayModalElement.addEventListener('click', (e: Event) => {
      const target = e.target as HTMLElement;
      if (target === this.howToPlayModalElement || target.closest('#btn-close-howtoplay') || target.closest('#btn-confirm-howtoplay')) {
        e.preventDefault();
        e.stopPropagation();
        this.howToPlayModalElement.style.display = 'none';
        return;
      }

      const tabBtn = target.closest('.howtoplay-tab-btn') as HTMLElement | null;
      if (tabBtn) {
        e.preventDefault();
        e.stopPropagation();
        const tab = tabBtn.getAttribute('data-tab') as 'guide' | 'tips' | 'progression';
        if (tab) this.switchHowToPlayTab(tab);
        return;
      }

      const accHead = target.closest('.acc-head') as HTMLElement | null;
      if (accHead) {
        e.preventDefault();
        e.stopPropagation();
        const accItem = accHead.closest('.acc-item') as HTMLElement | null;
        if (accItem) {
          accItem.classList.toggle('open');
        }
      }
    });

    // Backdrop / close click cho Google Trends feed modal
    this.trendsModalElement.addEventListener('click', (e: Event) => {
      const target = e.target as HTMLElement;
      if (
        target === this.trendsModalElement ||
        target.closest('#btn-close-trends') ||
        target.closest('#btn-dismiss-trends-modal')
      ) {
        e.preventDefault();
        e.stopPropagation();
        this.trendsModalElement.style.display = 'none';
      }
    });

    // Delegation cho celebration modal (Nhận quà, kích hoạt món, tiếp tục)
    this.celebrationModalElement.addEventListener('click', (e: Event) => {
      const target = e.target as HTMLElement;
      const enableBtn = target.closest('.btn-enable-unlocked') as HTMLElement | null;
      if (enableBtn) {
        e.preventDefault();
        e.stopPropagation();
        const itemId = enableBtn.getAttribute('data-item-id');
        if (itemId && !enableBtn.hasAttribute('disabled')) {
          this.dispatcher.dispatch({ type: 'ENABLE_UNLOCKED_ITEM', itemId });
          enableBtn.innerHTML = '✓ Đang Bán';
          enableBtn.setAttribute('disabled', 'true');
          enableBtn.style.backgroundColor = 'rgba(46, 125, 50, 0.4)';
          enableBtn.style.borderColor = '#81C784';
          enableBtn.style.color = '#C8E6C9';
        }
        return;
      }

      if (target.closest('#btn-dismiss-celebration') || target === this.celebrationModalElement) {
        e.preventDefault();
        e.stopPropagation();
        this.lastCelebrationId = null;
        this.dispatcher.dispatch({ type: 'DISMISS_CELEBRATION' });
        this.celebrationModalElement.style.display = 'none';
        return;
      }
    });

    // Event delegation riêng cho Popdown Menu
    this.popdownMenuElement.addEventListener('click', (e) => {
      const el = e.target as HTMLElement;
      if (el.closest('#btn-close-popdown')) {
        e.preventDefault();
        e.stopPropagation();
        this.closeTopMenu();
        return;
      }

      const btn = el.closest('button');
      if (!btn || !this.lastState) return;

      e.preventDefault();
      e.stopPropagation();

      if (btn.id === 'pop-item-profile') {
        this.closeTopMenu();
        this.openRenameModal(this.lastState);
        return;
      }
      if (btn.id === 'pop-item-menu') {
        this.closeTopMenu();
        this.isMenuOpen = true;
        this.renderMenuModal(this.lastState);
        return;
      }
      if (btn.id === 'pop-item-howtoplay') {
        this.closeTopMenu();
        this.openHowToPlayModal();
        return;
      }
      if (btn.id === 'pop-item-trends') {
        this.closeTopMenu();
        this.openTrendsModal();
        return;
      }
      if (btn.id === 'pop-item-perks') {
        this.closeTopMenu();
        this.openPerksModal(this.lastState);
        return;
      }
      if (btn.id === 'pop-item-sales') {
        this.closeTopMenu();
        this.isSalesOpen = true;
        this.renderSalesModal(this.lastState);
        return;
      }
      if (btn.id === 'pop-item-upgrade') {
        this.closeTopMenu();
        this.isUpgradeOpen = true;
        this.renderUpgradeModal(this.lastState);
        return;
      }
      if (btn.id === 'pop-item-staff') {
        this.closeTopMenu();
        this.dispatcher.dispatch({ type: 'TOGGLE_HIRE_STAFF' });
        return;
      }
      if (btn.id === 'pop-item-advance-day') {
        this.closeTopMenu();
        this.dispatcher.dispatch({ type: 'ADVANCE_DAY' });
        return;
      }
      if (btn.id === 'pop-item-change-loc') {
        this.closeTopMenu();
        this.dispatcher.dispatch({ type: 'SELECT_LOCATION', lat: 0, lon: 0 });
        return;
      }
      if (btn.id === 'pop-item-toggle-audio') {
        this.closeTopMenu();
        this.openAudioModal();
        return;
      }
      if (btn.id === 'pop-item-account') {
        this.closeTopMenu();
        void this.openAccountModal(this.lastState);
        return;
      }
      if (btn.id === 'pop-item-pause') {
        this.closeTopMenu();
        this.openPauseMenu(this.lastState);
        return;
      }
      if (btn.id === 'pop-item-install') {
        this.closeTopMenu();
        void this.triggerAddToHomeScreen();
        return;
      }
      if (btn.id === 'pop-item-reset') {
        this.closeTopMenu();
        this.showResetConfirmation();
        return;
      }
    });

    // Phím tắt M để Bật/Tắt âm thanh & nhạc nền nhanh
    window.addEventListener('keydown', (e: KeyboardEvent) => {
      if ((e.key === 'm' || e.key === 'M') && !['INPUT', 'TEXTAREA'].includes((e.target as HTMLElement)?.tagName)) {
        AudioService.getInstance().toggleMaster();
        const audioBtn = this.hudElement.querySelector('#btn-toggle-audio');
        if (audioBtn) audioBtn.textContent = AudioService.getInstance().getHudIcon();
      }
    });

    // Event delegation trên HUD
    const handleHudAction = (e: Event) => {
      const el = e.target as HTMLElement;

      // Đóng lore quote
      if (el.closest('#btn-dismiss-lore')) {
        e.preventDefault();
        e.stopPropagation();
        this.isLoreDismissed = true;
        const loreEl = this.hudElement.querySelector('#hud-lore') as HTMLElement | null;
        if (loreEl) loreEl.style.display = 'none';
        return;
      }

      // Chạm vào lore để xem chi tiết tin nóng Google Trends
      if (el.closest('#hud-lore') && this.lastState) {
        e.preventDefault();
        e.stopPropagation();
        this.openTrendsModal();
        return;
      }

      // Chạm vào link Xem Hướng Dẫn trên guide banner
      if (el.closest('#link-guide-banner')) {
        e.preventDefault();
        e.stopPropagation();
        this.openHowToPlayModal('guide');
        return;
      }

      const target = el.closest('button');
      if (!target || !this.lastState) return;

      // Mở Sổ tay khởi nghiệp / Hướng dẫn
      if (target.id === 'btn-hud-howtoplay' || target.closest('#btn-hud-howtoplay')) {
        e.preventDefault();
        e.stopPropagation();
        this.openHowToPlayModal('guide');
        return;
      }

      // Bật/tắt Popdown Menu
      if (target.id === 'btn-toggle-top-menu') {
        e.preventDefault();
        e.stopPropagation();
        this.toggleTopMenu(this.lastState);
        return;
      }

      // Về Sảnh Chính (Home / Lobby)
      if (target.id === 'btn-hud-home' || target.closest('#btn-hud-home')) {
        e.preventDefault();
        e.stopPropagation();
        this.closeTopMenu();
        this.isLobbyRendered = false;
        this.lobbyStep = 1;
        this.dispatcher.dispatch({ type: 'GO_TO_LOBBY' });
        return;
      }



      // Bật/tắt Bán Ca Đêm
      if (target.id === 'btn-toggle-overtime' || target.closest('#btn-toggle-overtime')) {
        e.preventDefault();
        e.stopPropagation();
        this.dispatcher.dispatch({ type: 'TOGGLE_OVERTIME' });
        return;
      }
    };

    this.hudElement.addEventListener('click', handleHudAction);

    // Event delegation vĩnh viễn trên Event Banner để các option luôn click/touch ăn 100%
    const handleEventAction = (e: Event) => {
      const target = (e.target as HTMLElement).closest('.btn-event-opt');
      if (!target || !this.lastState || !this.lastState.activeEvent) return;

      e.preventDefault();
      e.stopPropagation();

      const optId = target.getAttribute('data-opt');
      if (optId) {
        this.dispatcher.dispatch({
          type: 'HANDLE_EVENT',
          eventId: this.lastState.activeEvent.id,
          optionId: optId,
        });
        this.eventBannerElement.style.display = 'none';
        this.renderedEventId = null;
      }
    };

    this.eventBannerElement.addEventListener('click', handleEventAction);
    this.eventBannerElement.addEventListener('touchend', handleEventAction);

    // 1. Permanent Delegation trên ActionSheet (Đóng, Mua Node, Nâng ghế)
    this.actionSheetElement.addEventListener('click', (e: Event) => {
      const target = e.target as HTMLElement;
      if (!this.lastState) return;

      const closeBtn = target.closest('#btn-close-inspect');
      if (closeBtn) {
        e.preventDefault();
        e.stopPropagation();
        this.lastActionSheetKey = null;
        if (this.lastState.inspectRivalId) {
          this.dispatcher.dispatch({ type: 'INSPECT_RIVAL', rivalId: null });
        } else {
          this.dispatcher.dispatch({ type: 'INSPECT_NODE', nodeId: null });
        }
        return;
      }

      const buyBtn = target.closest('#btn-buy-node') as HTMLElement | null;
      if (buyBtn && !buyBtn.hasAttribute('disabled')) {
        e.preventDefault();
        e.stopPropagation();
        if (this.lastState.inspectNodeId) {
          this.dispatcher.dispatch({
            type: 'BUY_NODE',
            nodeId: this.lastState.inspectNodeId,
          });
        }
        return;
      }

      const upgradeBtn = target.closest('#btn-upgrade-chair') as HTMLElement | null;
      if (upgradeBtn && !upgradeBtn.hasAttribute('disabled')) {
        e.preventDefault();
        e.stopPropagation();
        const stallId = upgradeBtn.getAttribute('data-stall-id');
        if (stallId) {
          this.dispatcher.dispatch({
            type: 'UPGRADE_STALL',
            stallId,
            upgradeType: 'chairs',
          });
        }
        return;
      }
    });

    // 2. Permanent Delegation trên Menu Modal (Đóng, Toggle món)
    this.menuModalElement.addEventListener('click', (e: Event) => {
      const target = e.target as HTMLElement;
      if (target.closest('#btn-close-menu')) {
        e.preventDefault();
        e.stopPropagation();
        this.isMenuOpen = false;
        this.menuModalElement.style.display = 'none';
        this.lastMenuModalKey = null;
        return;
      }
      const toggleBtn = target.closest('.btn-toggle-menu') as HTMLElement | null;
      if (toggleBtn) {
        e.preventDefault();
        e.stopPropagation();
        const itemId = toggleBtn.getAttribute('data-id');
        if (itemId) {
          AudioService.getInstance().playTap();
          this.dispatcher.dispatch({ type: 'TOGGLE_MENU_ITEM', itemId });
        }
        return;
      }
    });

    // 3. Permanent Delegation trên Upgrade Modal (Đóng, Mua nâng cấp)
    this.upgradeModalElement.addEventListener('click', (e: Event) => {
      const target = e.target as HTMLElement;
      if (target.closest('#btn-close-upgrade')) {
        e.preventDefault();
        e.stopPropagation();
        this.isUpgradeOpen = false;
        this.upgradeModalElement.style.display = 'none';
        this.lastUpgradeModalKey = null;
        return;
      }
      const buyBtn = target.closest('.btn-buy-upgrade') as HTMLElement | null;
      if (buyBtn && !buyBtn.hasAttribute('disabled')) {
        e.preventDefault();
        e.stopPropagation();
        const upgId = buyBtn.getAttribute('data-upgrade') as import('../domain/types.ts').UpgradeId;
        if (upgId) {
          AudioService.getInstance().playUpgrade();
          this.dispatcher.dispatch({ type: 'BUY_UPGRADE', upgradeId: upgId });
        }
        return;
      }
    });

    // 4. Permanent Delegation trên Sổ Bán Hàng (Đóng)
    this.salesModalElement.addEventListener('click', (e: Event) => {
      const target = (e.target as HTMLElement).closest('#btn-close-sales');
      if (target) {
        e.preventDefault();
        e.stopPropagation();
        this.isSalesOpen = false;
        this.salesModalElement.style.display = 'none';
        this.lastSalesModalKey = null;
        return;
      }
    });

    // 5. Permanent Delegation trên Tổng Kết Ngày (Sang ngày mới)
    this.dayReportElement.addEventListener('click', (e: Event) => {
      const target = (e.target as HTMLElement).closest('#btn-next-day');
      if (target) {
        e.preventDefault();
        e.stopPropagation();
        this.dispatcher.dispatch({ type: 'DISMISS_DAY_REPORT' });
        this.dayReportElement.style.display = 'none';
        this.lastReportDayNumber = -1;
        return;
      }

      const relocateBtn = (e.target as HTMLElement).closest('#btn-suggest-relocate');
      if (relocateBtn) {
        e.preventDefault();
        e.stopPropagation();
        this.dispatcher.dispatch({ type: 'DISMISS_DAY_REPORT' });
        this.dayReportElement.style.display = 'none';
        this.lastReportDayNumber = -1;
        this.dispatcher.dispatch({
          type: 'SET_NARRATIVE',
          quote: '👵 Bà Trà Đá: "Sếp hãy chạm vào các dấu ($) trên bản đồ để tìm điểm mở quán đắc địa hơn nhé!"',
        });
        return;
      }
    });

    // 6. Permanent Delegation trên Pause Modal
    this.pauseModalElement.addEventListener('click', (e: Event) => {
      const target = e.target as HTMLElement;

      if (target === this.pauseModalElement || target.closest('#btn-resume-game')) {
        e.preventDefault();
        e.stopPropagation();
        this.closePauseMenu();
        return;
      }

      if (target.closest('#btn-pause-restart-day')) {
        e.preventDefault();
        e.stopPropagation();
        this.closePauseMenu();
        this.dispatcher.dispatch({ type: 'RESTART_CURRENT_DAY' });
        return;
      }

      if (target.closest('#btn-pause-lobby')) {
        e.preventDefault();
        e.stopPropagation();
        this.closePauseMenu();
        this.isLobbyRendered = false;
        this.lobbyStep = 1;
        this.dispatcher.dispatch({ type: 'GO_TO_LOBBY' });
        return;
      }

      if (target.closest('#btn-pause-sync')) {
        e.preventDefault();
        e.stopPropagation();
        this.closePauseMenu();
        void this.openAccountModal(this.lastState);
        return;
      }

      if (target.closest('#btn-pause-install')) {
        e.preventDefault();
        e.stopPropagation();
        void this.triggerAddToHomeScreen();
        return;
      }

      if (target.closest('#btn-pause-reset')) {
        e.preventDefault();
        e.stopPropagation();
        this.closePauseMenu();
        this.showResetConfirmation();
        return;
      }
    });

    // 7. Permanent Delegation trên Install Guide Modal
    this.installGuideModalElement.addEventListener('click', (e: Event) => {
      const target = e.target as HTMLElement;
      if (
        target === this.installGuideModalElement ||
        target.closest('#btn-close-install-guide') ||
        target.closest('#btn-got-it-install')
      ) {
        e.preventDefault();
        e.stopPropagation();
        this.installGuideModalElement.style.display = 'none';
      }
    });
  }

  public setNeighborhoodLoader(fn: (lat: number, lon: number, label: string) => Promise<void>): void {
    this.neighborhoodLoader = fn;
  }

  public render(state: Readonly<GameState>): void {
    this.lastState = state;

    if (!this.splashScreenElement) {
      this.splashScreenElement = document.getElementById('splash-screen');
    }

    if (state.phase === 'BOOT') {
      if (this.splashScreenElement) {
        this.splashScreenElement.style.display = 'flex';
      }
      this.lobbyModalElement.style.display = 'none';
      this.hudElement.style.display = 'none';
      this.actionSheetElement.style.display = 'none';
      this.eventBannerElement.style.display = 'none';
      this.zoomControlsElement.style.display = 'none';
      this.dayReportElement.style.display = 'none';
      this.upgradeModalElement.style.display = 'none';
      this.milestoneElement.style.display = 'none';
      this.saleToastElement.style.display = 'none';
      this.celebrationModalElement.style.display = 'none';
      this.perksModalElement.style.display = 'none';
      return;
    }

    if (this.splashScreenElement) {
      this.splashScreenElement.style.display = 'none';
    }

    if (state.phase === 'LOBBY') {
      this.renderLobby(state);
      this.hudElement.style.display = 'none';
      this.actionSheetElement.style.display = 'none';
      this.eventBannerElement.style.display = 'none';
      this.zoomControlsElement.style.display = 'none';
      this.dayReportElement.style.display = 'none';
      this.upgradeModalElement.style.display = 'none';
      this.milestoneElement.style.display = 'none';
      this.saleToastElement.style.display = 'none';
      this.celebrationModalElement.style.display = 'none';
      this.perksModalElement.style.display = 'none';
      return;
    }

    this.lobbyModalElement.style.display = 'none';
    this.hudElement.style.display = 'flex';
    this.actionSheetElement.style.display = 'block';
    this.eventBannerElement.style.display = 'flex';
    this.zoomControlsElement.style.display = 'flex';

    this.renderHUD(state);
    this.renderEventBanner(state);
    this.renderActionSheet(state);

    if (this.isMenuOpen) this.renderMenuModal(state);
    if (this.isSalesOpen) this.renderSalesModal(state);
    if (this.isUpgradeOpen) this.renderUpgradeModal(state);

    if (state.pendingCelebration) {
      this.renderCelebrationModal(state);
    } else {
      this.celebrationModalElement.style.display = 'none';
    }

    if (state.showDayReport) {
      this.renderDayReportModal(state);
    } else {
      this.dayReportElement.style.display = 'none';
    }

    this.renderMilestoneToast(state);
    this.renderSaleToast(state);

    if (state.phase === 'PLAYING' && state.weather && state.weather.type !== this.lastWeatherType) {
      if (this.lastWeatherType !== null) {
        this.showWeatherToast(state.weather);

        if (state.weather.type === 'rainy' || state.weather.type === 'hot') {
          const currentMenu = state.neighborhood
            ? getRegionalMenu(state.neighborhood.center.lat, state.neighborhood.center.lon, state.neighborhood.label)
            : SIDEWALK_MENU;
          const freshActiveCount = currentMenu.filter(
            (m) => (state.player.activeMenu ?? []).includes(m.id) && m.shelfLife === 'perishable_drink'
          ).length;
          if (freshActiveCount >= 2) {
            this.dispatcher.dispatch({
              type: 'SET_NARRATIVE',
              quote: `👵 Bà Trà Đá: "Trời đang ${state.weather.name} rồi đấy, sếp nhớ tắt bớt nước pha sẵn ⏱️ trong Thực Đơn kẻo cuối ngày ế thiu phải đổ đi phí lắm!"`,
            });
          }
        }
      }
      this.lastWeatherType = state.weather.type;
    }

    if (state.loadingMessage) {
      this.loadingOverlayElement.style.display = 'flex';
      this.loadingOverlayElement.innerHTML = `
        <div class="loading-card">
          <div class="hourglass-anim" style="font-size: 38px; margin-bottom: 12px; filter: drop-shadow(0 2px 8px rgba(255,213,79,0.5));">⏳</div>
          <p style="margin: 0; font-size: 13px; font-weight: 500; color: #FFF; line-height: 1.5;">${state.loadingMessage}</p>
        </div>
      `;
    } else {
      this.loadingOverlayElement.style.display = 'none';
    }
  }

  private renderLobby(state: Readonly<GameState>): void {
    this.lobbyModalElement.style.display = 'flex';
    if (this.isLobbyRendered) return;
    this.isLobbyRendered = true;

    const hasStall =
      state.player.ownedStalls.length > 0 ||
      (Boolean(state.player.stallName) && (state.player.totalRevenue ?? 0) > 0);

    if (hasStall && this.lobbyStep === 1) {
      this.renderLobbyDashboard(state);
    } else if (this.lobbyStep === 1) {
      this.renderLobbyStep1(state);
    } else {
      this.renderLobbyStep2(state);
    }
  }

  private renderLobbyDashboard(state: Readonly<GameState>): void {
    const stallName = state.player.stallName || 'Trà Cóc Vỉa Hè';
    const areaName = state.neighborhood
      ? state.neighborhood.label.split(',')[0]?.trim() || 'Vỉa hè'
      : 'Hà Nội';
    const weather = state.weather;
    const weatherDesc = weather
      ? `${weather.emoji} <strong>${weather.name}</strong> — ${weather.description}`
      : '☀️ <strong>Nắng Đẹp</strong> — Thời tiết đẹp, khách dạo phố đông vui!';
    const moneyStr = state.player.money.toLocaleString('vi-VN') + '₫';
    const totalRevStr = (state.player.totalRevenue ?? 0).toLocaleString('vi-VN') + '₫';
    const totalSales = state.player.totalSales ?? 0;
    const isAudioOn = AudioService.getInstance().isEnabled();

    this.lobbyModalElement.innerHTML = `
      <div class="lobby-card lobby-dashboard-card">
        <div class="dash-top-bar">
          <button id="btn-dash-audio" class="dash-pill" title="Bật/Tắt âm thanh (Phím M)">
            ${isAudioOn ? '🔊 Âm thanh: BẬT' : '🔇 Âm thanh: TẮT'}
          </button>
          <div class="dash-pill" style="border-color: rgba(255, 213, 79, 0.3); color: #FFD54F;">
            🏪 ${state.player.ownedStalls.length || 1} Quán • ⭐ Lv.${state.player.stallLevel ?? 1}
          </div>
          <div class="dash-pill" style="color: #81C784;">
            💰 ${moneyStr}
          </div>
        </div>

        <div class="dash-hero">
          <div class="dash-squircle">
            <span class="dash-squircle-icon">🧋</span>
          </div>
          <div class="dash-title-row">
            <h2 class="dash-title">${stallName}</h2>
            <button id="btn-dash-rename" class="dash-btn-pen" title="Đổi tên quán">✏️</button>
          </div>
          <div class="dash-tagline">
            Bá Chủ Vỉa Hè • ${areaName} • Ngày ${state.dayNumber}
          </div>
        </div>

        <div class="dash-weather-card">
          <div style="font-size: 20px; flex-shrink: 0;">${weather ? weather.emoji : '☀️'}</div>
          <div style="font-size: 11px; color: #CFD8DC; line-height: 1.35; flex: 1;">
            ${weatherDesc}
          </div>
        </div>

        <div class="dash-stats-grid">
          <div class="dash-stat-item">
            <span class="dash-stat-label">Vốn Hiện Có</span>
            <span class="dash-stat-val">${moneyStr}</span>
            <span class="dash-stat-sub">Tiền mặt</span>
          </div>
          <div class="dash-stat-item">
            <span class="dash-stat-label">Thời Điểm</span>
            <span class="dash-stat-val">Ngày ${state.dayNumber}</span>
            <span class="dash-stat-sub">${state.dayOfWeek || 'Thứ Hai'}</span>
          </div>
          <div class="dash-stat-item">
            <span class="dash-stat-label">Tổng Doanh Thu</span>
            <span class="dash-stat-val">${totalRevStr}</span>
            <span class="dash-stat-sub">${totalSales} đơn đã bán</span>
          </div>
        </div>

        <div class="dash-actions-grid">
          <button id="btn-dash-menu" class="dash-action-btn" title="Thực đơn quán">
            <span class="dash-action-icon">📋</span>
            <span>Thực Đơn</span>
          </button>
          <button id="btn-dash-upgrade" class="dash-action-btn" title="Nâng cấp quán">
            <span class="dash-action-icon">🔧</span>
            <span>Nâng Cấp</span>
          </button>
          <button id="btn-dash-sales" class="dash-action-btn" title="Sổ bán hàng">
            <span class="dash-action-icon">🧾</span>
            <span>Sổ Sách</span>
          </button>
          <button id="btn-dash-perks" class="dash-action-btn" title="Đồ nghề vỉa hè">
            <span class="dash-action-icon">🎒</span>
            <span>Đồ Nghề</span>
          </button>
          <button id="btn-dash-account" class="dash-action-btn" title="Sao lưu & Đồng bộ">
            <span class="dash-action-icon">☁️</span>
            <span>Sao Lưu</span>
          </button>
          <button id="btn-dash-install" class="dash-action-btn" title="Đưa ra màn hình chính">
            <span class="dash-action-icon">📲</span>
            <span>Cài App</span>
          </button>
        </div>

        <button id="btn-dash-continue" class="dash-continue-btn">
          ▶ Tiếp Tục Bán Hàng (Ngày ${state.dayNumber})
        </button>

        <div class="dash-secondary-row">
          <button id="btn-dash-change-loc" class="dash-secondary-btn">
            📍 Đổi Địa Bàn Mở Quán
          </button>
          <button id="btn-dash-howtoplay" class="dash-secondary-btn">
            📖 Sổ Tay Hướng Dẫn
          </button>
          <button id="btn-dash-reset" class="dash-secondary-btn" style="color: #EF5350; border-color: rgba(239, 83, 80, 0.3);">
            🗑️ Chơi Lại
          </button>
        </div>
      </div>
    `;

    document.getElementById('btn-dash-audio')?.addEventListener('click', () => {
      AudioService.getInstance().toggleMaster();
      const btn = document.getElementById('btn-dash-audio');
      if (btn) {
        btn.textContent = AudioService.getInstance().isEnabled() ? '🔊 Âm thanh: BẬT' : '🔇 Âm thanh: TẮT';
      }
    });

    document.getElementById('btn-dash-rename')?.addEventListener('click', () => {
      this.openRenameModal(state);
    });

    document.getElementById('btn-dash-menu')?.addEventListener('click', () => {
      this.isMenuOpen = true;
      this.renderMenuModal(state);
    });

    document.getElementById('btn-dash-upgrade')?.addEventListener('click', () => {
      this.isUpgradeOpen = true;
      this.renderUpgradeModal(state);
    });

    document.getElementById('btn-dash-sales')?.addEventListener('click', () => {
      this.isSalesOpen = true;
      this.renderSalesModal(state);
    });

    document.getElementById('btn-dash-perks')?.addEventListener('click', () => {
      this.openPerksModal(state);
    });

    document.getElementById('btn-dash-account')?.addEventListener('click', () => {
      void this.openAccountModal(state);
    });

    document.getElementById('btn-dash-install')?.addEventListener('click', () => {
      void this.triggerAddToHomeScreen();
    });

    document.getElementById('btn-dash-continue')?.addEventListener('click', () => {
      this.dispatcher.dispatch({ type: 'START_GAME' });
      this.dispatcher.dispatch({ type: 'RESUME_GAME' });
      this.lobbyModalElement.style.display = 'none';
      this.isLobbyRendered = false;
    });

    document.getElementById('btn-dash-change-loc')?.addEventListener('click', () => {
      this.lobbyStep = 2;
      this.isLobbyRendered = false;
      this.render(state);
    });

    document.getElementById('btn-dash-howtoplay')?.addEventListener('click', () => {
      this.openHowToPlayModal('guide');
    });

    document.getElementById('btn-dash-reset')?.addEventListener('click', () => {
      this.showResetConfirmation();
    });
  }

  private renderLobbyStep1(state: Readonly<GameState>): void {
    this.lobbyModalElement.innerHTML = `
      <div class="lobby-card">
        <div class="lobby-header">
          <span class="lobby-icon">🏪</span>
          <h2>BÁ CHỦ VỈA HÈ</h2>
          <p class="lobby-tagline">Arcade Tycoon — Khởi nghiệp từ quầy nước cóc vỉa hè</p>
        </div>

        <div class="lobby-steps-indicator">
          <div class="step-pill active">Bước 1: Đặt Tên Quán</div>
          <div class="step-arrow">→</div>
          <div class="step-pill">Bước 2: Chọn Địa Bàn</div>
        </div>

        <div class="lobby-form">
          <div class="form-group">
            <label>👤 Tên bạn (Biệt danh chủ quán):</label>
            <input type="text" id="inp-lobby-user" maxlength="20" placeholder="Ví dụ: Nam Trà Đá, Cô Lan..." value="${this.lobbyUsername || state.player.username || ''}" />
          </div>

          <div class="form-group">
            <label>🏪 Tên quán nước vỉa hè của bạn:</label>
            <input type="text" id="inp-lobby-stall" maxlength="30" placeholder="Ví dụ: Trà Chanh Chém Gió..." value="${this.lobbyStallName || state.player.stallName || ''}" />
          </div>

          <div class="form-group">
            <div class="chips-label">Gợi ý tên hay cho quán cóc:</div>
            <div class="preset-chips">
              <button type="button" class="chip-btn">Trà Cóc Vỉa Hè 🧋</button>
              <button type="button" class="chip-btn">Trà Chanh Chém Gió 🍋</button>
              <button type="button" class="chip-btn">Cà Phê Bệt Phố Cổ ☕</button>
              <button type="button" class="chip-btn">Nước Mía Siêu Sạch 🎋</button>
              <button type="button" class="chip-btn">Trà Đào Cam Sả 🍑</button>
              <button type="button" class="chip-btn">Quán Nước Cây Đa 🌳</button>
            </div>
          </div>

          <div class="lobby-capital">
            <span>Vốn khởi nghiệp ban đầu:</span>
            <strong>500.000₫</strong>
          </div>

          <button id="btn-lobby-step2" class="btn btn-primary btn-large">Tiếp Theo: Chọn Địa Bàn →</button>
          <button type="button" id="btn-lobby-howtoplay" class="btn-outline-restore" style="margin-top: 6px;">
            📖 Sổ Tay Khởi Nghiệp: Hướng Dẫn Cách Chơi ➔
          </button>
          <button type="button" id="btn-lobby-restore" class="btn-outline-restore">
            🔄 Đã có quán cũ? Khôi phục dữ liệu ➔
          </button>
        </div>
      </div>
    `;

    const userInp = document.getElementById('inp-lobby-user') as HTMLInputElement;
    const stallInp = document.getElementById('inp-lobby-stall') as HTMLInputElement;

    this.lobbyModalElement.querySelectorAll('.chip-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (stallInp) stallInp.value = btn.textContent?.replace(/[^\p{L}\p{N}\s]/gu, '').trim() || '';
      });
    });

    document.getElementById('btn-lobby-howtoplay')?.addEventListener('click', () => {
      this.openHowToPlayModal('guide');
    });

    document.getElementById('btn-lobby-restore')?.addEventListener('click', () => {
      void this.openAccountModal(state);
    });

    document.getElementById('btn-lobby-step2')?.addEventListener('click', () => {
      this.lobbyUsername = userInp?.value.trim() || 'Chủ Quán';
      this.lobbyStallName = stallInp?.value.trim() || 'Trà Cóc Vỉa Hè';
      this.lobbyStep = 2;
      this.isLobbyRendered = false;
      this.render(state);
    });
  }

  private renderRecentSearchesHtml(prefix: string): string {
    const recent = SearchService.getRecentSearches();
    if (recent.length === 0) return '';

    return `
      <div class="search-recent-section" id="${prefix}-recent-section">
        <div class="search-recent-header">
          <span>🕒 <strong>Tìm gần đây:</strong></span>
          <button type="button" class="btn-clear-recent" id="${prefix}-btn-clear-recent">✕ Xóa</button>
        </div>
        <div class="search-recent-chips">
          ${recent
            .map(
              (r) => `
            <button type="button" class="chip-recent-spot" data-lat="${r.coords.lat}" data-lon="${r.coords.lon}" data-label="${r.label}">
              🕒 ${r.label.split(',')[0]}
            </button>
          `
            )
            .join('')}
        </div>
      </div>
    `;
  }

  private renderLobbyStep2(state: Readonly<GameState>): void {
    const selectedLabel = this.lobbyLocation ? this.lobbyLocation.label : 'Phố Huế, Hai Bà Trưng, Hà Nội';

    this.lobbyModalElement.innerHTML = `
      <div class="lobby-card">
        <div class="lobby-header">
          <span class="lobby-icon">📍</span>
          <h2>CHỌN ĐỊA BÀN MỞ QUÁN</h2>
          <p class="lobby-tagline">Chọn địa điểm thực tế hoặc GPS để tạo lập bản đồ vỉa hè</p>
        </div>

        <div class="lobby-steps-indicator">
          <div class="step-pill completed">✓ ${this.lobbyStallName || 'Quán Cóc'}</div>
          <div class="step-arrow">→</div>
          <div class="step-pill active">Bước 2: Chọn Địa Bàn</div>
        </div>

        <div class="location-picker-inline">
          <div class="search-box">
            <input id="inp-lobby-search" type="text" placeholder="Tìm phố, ngã tư, quận, chợ..." />
            <button id="btn-lobby-search" class="btn btn-primary">Tìm</button>
          </div>

          <button id="btn-lobby-gps" class="btn btn-gps">🧭 Dùng Vị Trí GPS Của Tôi</button>

          ${this.renderRecentSearchesHtml('lobby')}

          <div class="quick-spots-label">🔥 Địa bàn hot nhiều người tìm:</div>
          <div class="quick-spots">
            <button type="button" class="btn-quick-spot" data-lat="21.0580" data-lon="105.8139" data-label="Hồ Tây, Tây Hồ, Hà Nội">Hồ Tây</button>
            <button type="button" class="btn-quick-spot" data-lat="21.0368" data-lon="105.7905" data-label="Cầu Giấy, Cầu Giấy, Hà Nội">Cầu Giấy</button>
            <button type="button" class="btn-quick-spot" data-lat="10.7936" data-lon="106.7218" data-label="Landmark 81, Bình Thạnh, TP.HCM">Landmark 81</button>
            <button type="button" class="btn-quick-spot" data-lat="11.9404" data-lon="108.4377" data-label="Chợ Đà Lạt, Lâm Đồng">Chợ Đà Lạt</button>
            <button type="button" class="btn-quick-spot" data-lat="10.0452" data-lon="105.7469" data-label="Bến Ninh Kiều, Cần Thơ">Bến Ninh Kiều</button>
            <button type="button" class="btn-quick-spot" data-lat="16.0680" data-lon="108.2208" data-label="Cầu Rồng, Hải Châu, Đà Nẵng">Cầu Rồng</button>
            <button type="button" class="btn-quick-spot" data-lat="10.7769" data-lon="106.7009" data-label="Chợ Bến Thành, Quận 1, TP.HCM">Bến Thành</button>
            <button type="button" class="btn-quick-spot" data-lat="21.0285" data-lon="105.8542" data-label="Hồ Hoàn Kiếm, Hoàn Kiếm, Hà Nội">Hồ Gươm</button>
          </div>

          <div id="lobby-search-results" class="results-list"></div>

          <div id="lobby-selected-badge" class="selected-loc-badge ${this.lobbyLocation ? 'active' : ''}">
            ${this.lobbyLocation ? `✓ Đã chọn: <strong>${this.lobbyLocation.label}</strong>` : `📍 Mặc định: <strong>${selectedLabel}</strong>`}
          </div>

          <div class="lobby-actions-row">
            <button id="btn-lobby-back" class="btn btn-secondary">← Đổi Tên</button>
            <button id="btn-start-game" class="btn btn-primary btn-large">Bắt Đầu Lập Nghiệp →</button>
          </div>
          <button type="button" id="btn-lobby-step2-howtoplay" class="btn-outline-restore" style="margin-top: 8px; width: 100%;">
            📖 Chưa rõ luật chơi? Xem Sổ Tay Khởi Nghiệp ➔
          </button>
        </div>
      </div>
    `;

    const updateBadge = (label: string, isEstimated = false) => {
      const badge = document.getElementById('lobby-selected-badge');
      if (badge) {
        if (isEstimated) {
          badge.innerHTML = `⚠️ <strong>${label}</strong><br><small style="font-size:11px;opacity:0.85">Độ chính xác mạng thấp. Nên gõ tên phố vào ô tìm kiếm ở trên để lấy bản đồ chuẩn nhất!</small>`;
          badge.classList.add('active');
        } else {
          badge.innerHTML = `✓ Đã chọn: <strong>${label}</strong>`;
          badge.classList.add('active');
        }
      }
    };

    document.getElementById('btn-lobby-step2-howtoplay')?.addEventListener('click', () => {
      this.openHowToPlayModal('guide');
    });

    document.getElementById('btn-lobby-back')?.addEventListener('click', () => {
      this.lobbyStep = 1;
      this.isLobbyRendered = false;
      this.render(state);
    });

    // Xóa lịch sử tìm gần đây
    document.getElementById('lobby-btn-clear-recent')?.addEventListener('click', () => {
      SearchService.clearRecentSearches();
      const section = document.getElementById('lobby-recent-section');
      if (section) section.remove();
    });

    // Chọn từ danh sách tìm gần đây
    this.lobbyModalElement.querySelectorAll('.chip-recent-spot').forEach((chip) => {
      chip.addEventListener('click', () => {
        const lat = parseFloat(chip.getAttribute('data-lat') || '21.0135');
        const lon = parseFloat(chip.getAttribute('data-lon') || '105.8504');
        const label = chip.getAttribute('data-label') || 'Vị trí đã chọn';
        this.lobbyLocation = { lat, lon, label };
        updateBadge(label);
        SearchService.saveRecentSearch({ lat, lon }, label);
      });
    });

    const gpsBtn = document.getElementById('btn-lobby-gps') as HTMLButtonElement;
    gpsBtn?.addEventListener('click', async () => {
      gpsBtn.textContent = '🧭 Đang định vị GPS & IP...';
      gpsBtn.disabled = true;
      try {
        const loc = await LocationService.getCurrentLocation();
        this.lobbyLocation = {
          lat: loc.coords.lat,
          lon: loc.coords.lon,
          label: loc.label,
        };
        updateBadge(loc.label, loc.isEstimated);
        SearchService.saveRecentSearch({ lat: loc.coords.lat, lon: loc.coords.lon }, loc.label);
      } finally {
        gpsBtn.textContent = '🧭 Dùng Vị Trí GPS Của Tôi';
        gpsBtn.disabled = false;
      }
    });

    this.lobbyModalElement.querySelectorAll('.btn-quick-spot').forEach((btn) => {
      btn.addEventListener('click', () => {
        const lat = parseFloat(btn.getAttribute('data-lat') || '21.0135');
        const lon = parseFloat(btn.getAttribute('data-lon') || '105.8504');
        const label = btn.getAttribute('data-label') || 'Vị trí đã chọn';
        this.lobbyLocation = { lat, lon, label };
        updateBadge(label);
        SearchService.saveRecentSearch({ lat, lon }, label);
      });
    });

    const searchInput = document.getElementById('inp-lobby-search') as HTMLInputElement;
    const searchBtn = document.getElementById('btn-lobby-search') as HTMLButtonElement;

    const handleLobbySearch = async (): Promise<void> => {
      const q = searchInput.value;
      if (!q.trim()) return;

      const resultsList = document.getElementById('lobby-search-results');
      if (searchBtn) {
        searchBtn.disabled = true;
        searchBtn.innerHTML = '<span class="hourglass-anim-btn">⏳</span>';
      }
      if (resultsList) {
        resultsList.innerHTML = `
          <div class="search-hourglass-box">
            <div class="hourglass-anim">⏳</div>
            <div class="search-hourglass-title">Đang lật đồng hồ cát tìm kiếm...</div>
            <small class="search-hourglass-sub">Đang định vị "${q}" trên bản đồ vệ tinh</small>
          </div>
        `;
      }

      const results = await SearchService.searchLocations(q);

      if (searchBtn) {
        searchBtn.disabled = false;
        searchBtn.textContent = 'Tìm';
      }

      if (!resultsList) return;

      if (results.length === 0) {
        resultsList.innerHTML = `
          <div class="search-empty-box">
            <div style="font-size: 24px; margin-bottom: 6px;">🔍</div>
            <div style="font-size: 13px; font-weight: bold; color: #ECEFF1;">Không tìm thấy địa điểm "${q}"</div>
            <small style="color: #90A4AE; font-size: 11px;">Thử tìm tên quận/huyện, thành phố hoặc địa danh nổi tiếng (Cầu Giấy, Hồ Tây, Bến Thành, Landmark 81...)</small>
          </div>
        `;
        return;
      }

      resultsList.innerHTML = results
        .map(
          (r, idx) => `
        <div class="result-item" data-idx="${idx}">
          <strong>${r.label.split(',')[0]}</strong>
          <small>${r.label}</small>
        </div>
      `
        )
        .join('');

      resultsList.querySelectorAll('.result-item').forEach((item) => {
        const selectItem = () => {
          const idx = parseInt(item.getAttribute('data-idx') || '0', 10);
          const chosen = results[idx];
          if (chosen) {
            this.lobbyLocation = {
              lat: chosen.coords.lat,
              lon: chosen.coords.lon,
              label: chosen.label,
            };
            updateBadge(chosen.label);
            SearchService.saveRecentSearch({ lat: chosen.coords.lat, lon: chosen.coords.lon }, chosen.label);
          }
        };
        item.addEventListener('click', selectItem);
        item.addEventListener('touchend', selectItem);
      });
    };

    searchBtn?.addEventListener('click', handleLobbySearch);
    searchInput?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') handleLobbySearch();
    });

    document.getElementById('btn-start-game')?.addEventListener('click', async () => {
      const startBtn = document.getElementById('btn-start-game') as HTMLButtonElement;
      if (startBtn) {
        startBtn.disabled = true;
        startBtn.textContent = 'Đang chuẩn bị địa bàn...';
      }

      const username = this.lobbyUsername || 'Chủ Quán';
      const stallName = this.lobbyStallName || 'Trà Cóc Vỉa Hè';
      this.dispatcher.dispatch({ type: 'SET_PLAYER_PROFILE', username, stallName });

      const loc = this.lobbyLocation ?? {
        lat: 21.0135,
        lon: 105.8504,
        label: 'Phố Huế, Hai Bà Trưng, Hà Nội',
      };

      if (this.neighborhoodLoader) {
        try {
          await this.neighborhoodLoader(loc.lat, loc.lon, loc.label);
        } catch (err) {
          console.warn('[UIManager] Neighborhood loader error in lobby:', err);
        }
      }

      this.dispatcher.dispatch({ type: 'START_GAME' });
      this.lobbyModalElement.style.display = 'none';
      this.lobbyStep = 1;
      this.isLobbyRendered = false;
    });
  }

  private toggleTopMenu(state?: Readonly<GameState>): void {
    if (this.isTopMenuOpen) {
      this.closeTopMenu();
    } else if (state || this.lastState) {
      this.openTopMenu(state ?? this.lastState!);
    }
  }

  private openTopMenu(state: Readonly<GameState>): void {
    this.isTopMenuOpen = true;
    const hasStall = state.player.ownedStalls.length > 0;
    this.popdownMenuElement.innerHTML = `
      <div class="popdown-header">
        <span>⚙️ BẢNG QUẢN LÝ QUÁN</span>
        <button id="btn-close-popdown" class="btn-popdown-close">✕</button>
      </div>
      <div class="popdown-list">
        <button class="popdown-item" id="pop-item-profile">
          <span class="item-icon">👤</span>
          <div class="item-text">
            <strong>Hồ Sơ Quán & Đổi Tên</strong>
            <small>Xem thông tin chủ quán & đổi tên bảng hiệu</small>
          </div>
        </button>

        <button class="popdown-item" id="pop-item-menu">
          <span class="item-icon">📋</span>
          <div class="item-text">
            <strong>Thực Đơn Quán</strong>
            <small>Chọn món mở bán & tùy chỉnh giá</small>
          </div>
        </button>

        <button class="popdown-item" id="pop-item-perks">
          <span class="item-icon">🎒</span>
          <div class="item-text">
            <strong>Đồ Nghề Quán</strong>
            <small>Kỹ năng & đặc quyền sinh tồn vỉa hè</small>
          </div>
        </button>

        <button class="popdown-item" id="pop-item-sales">
          <span class="item-icon">🧾</span>
          <div class="item-text">
            <strong>Sổ Bán Hàng</strong>
            <small>Xem lịch sử giao dịch & lợi nhuận</small>
          </div>
        </button>

        <button class="popdown-item" id="pop-item-trends">
          <span class="item-icon">🔥</span>
          <div class="item-text">
            <strong>Tin Nóng Google Trends</strong>
            <small>Điểm tin tức vỉa hè & mạng xã hội mỗi 30p</small>
          </div>
        </button>

        ${
          hasStall
            ? `
          <button class="popdown-item" id="pop-item-upgrade">
            <span class="item-icon">🔧</span>
            <div class="item-text">
              <strong>Nâng Cấp Quán</strong>
              <small>Mua dù che mưa, biển hiệu, thêm ghế</small>
            </div>
          </button>

          <button class="popdown-item ${state.player.hasStaff ? 'item-active' : ''}" id="pop-item-staff">
            <span class="item-icon">👥</span>
            <div class="item-text">
              <strong>${state.player.hasStaff ? '✓ Đang Thuê Phụ Quán (120k/ngày)' : 'Thuê Phụ Quán (120k/ngày)'}</strong>
              <small>${state.player.hasStaff ? 'Bấm để cho nghỉ tự làm' : 'Tăng tốc phục vụ, tránh quá tải khi đông khách'}</small>
            </div>
          </button>

          <button class="popdown-item" id="pop-item-advance-day">
            <span class="item-icon">📅</span>
            <div class="item-text">
              <strong>Kết Thúc Ngày</strong>
              <small>Chốt doanh thu & xem tổng kết ngày</small>
            </div>
          </button>
          `
            : ''
        }

        <button class="popdown-item" id="pop-item-howtoplay">
          <span class="item-icon">📖</span>
          <div class="item-text">
            <strong>Sổ Tay & Cách Chơi</strong>
            <small>Cẩm nang khởi nghiệp vỉa hè</small>
          </div>
        </button>

        <button class="popdown-item" id="pop-item-toggle-audio">
          <span class="item-icon">${AudioService.getInstance().isEnabled() ? '🔊' : '🔇'}</span>
          <div class="item-text">
            <strong>${AudioService.getInstance().isEnabled() ? 'Âm Thanh & Nhạc Nền: BẬT' : 'Âm Thanh & Nhạc Nền: ĐÃ TẮT'}</strong>
            <small>Chạm để ${AudioService.getInstance().isEnabled() ? 'tắt tiếng' : 'bật tiếng'} (hoặc bấm phím M)</small>
          </div>
        </button>

        <button class="popdown-item" id="pop-item-change-loc">
          <span class="item-icon">📍</span>
          <div class="item-text">
            <strong>Đổi Vị Trí / Địa Bàn</strong>
            <small>Chọn địa điểm mới hoặc định vị GPS</small>
          </div>
        </button>

        <button class="popdown-item" id="pop-item-account">
          <span class="item-icon">🔑</span>
          <div class="item-text">
            <strong>Tài Khoản & Chuyển Máy</strong>
            <small>Sao lưu UID, Secret Key & Khôi phục</small>
          </div>
        </button>

        <button class="popdown-item" id="pop-item-pause">
          <span class="item-icon">⏸️</span>
          <div class="item-text">
            <strong>Tạm Dừng Game</strong>
            <small>Mở bảng tùy chọn, khôi phục ngày & lưu trữ</small>
          </div>
        </button>

        <button class="popdown-item" id="pop-item-install">
          <span class="item-icon">📲</span>
          <div class="item-text">
            <strong>Đưa Ra Màn Hình Chính</strong>
            <small>Cài đặt ứng dụng PWA chơi nhanh mượt</small>
          </div>
        </button>

        <div class="popdown-divider"></div>

        <button class="popdown-item item-danger" id="pop-item-reset">
          <span class="item-icon">🗑️</span>
          <div class="item-text">
            <strong style="color: #FF8A80;">Chơi Lại Từ Đầu</strong>
            <small style="color: rgba(255,205,210,0.7);">Reset toàn bộ và tạo ván mới</small>
          </div>
        </button>
      </div>
    `;
    this.popdownBackdropElement.style.display = 'block';
    this.popdownMenuElement.style.display = 'flex';
    const triggerBtn = document.getElementById('btn-toggle-top-menu');
    if (triggerBtn) {
      triggerBtn.classList.add('active');
      const arrow = triggerBtn.querySelector('.arrow-indicator');
      if (arrow) arrow.textContent = '▲';
    }
  }

  private closeTopMenu(): void {
    this.isTopMenuOpen = false;
    this.popdownBackdropElement.style.display = 'none';
    this.popdownMenuElement.style.display = 'none';
    const triggerBtn = document.getElementById('btn-toggle-top-menu');
    if (triggerBtn) {
      triggerBtn.classList.remove('active');
      const arrow = triggerBtn.querySelector('.arrow-indicator');
      if (arrow) arrow.textContent = '▼';
    }
  }

  private renderHUD(state: Readonly<GameState>): void {
    const heatPct = Math.round(state.player.heat * 100);
    const hasStall = state.player.ownedStalls.length > 0;

    if (state.narrativeQuote !== this.lastQuote) {
      this.lastQuote = state.narrativeQuote;
      this.isLoreDismissed = false;
    }

    if (!this.isHudInitialized) {
      this.isHudInitialized = true;
      this.hudElement.innerHTML = `
        <div class="hud-top-row">
          <button id="btn-hud-home" class="hud-home-btn" title="Về Sảnh Chính (Lobby)">🏠</button>
          
          <div class="hud-bar">
            <div class="hud-item level" title="Cấp độ quán">
              <span class="hud-label">CẤP</span>
              <span class="hud-val" id="hud-shop-level"></span>
            </div>
            <div class="hud-item day">
              <span class="hud-label">NGÀY</span>
              <span class="hud-val" id="hud-val-day"></span>
            </div>
            <div class="hud-item shift">
              <span class="hud-label">CA</span>
              <span class="hud-val" id="hud-val-shift"></span>
            </div>
            <div class="hud-item weather">
              <span class="hud-label">TRỜI</span>
              <span class="hud-val" id="hud-val-weather"></span>
            </div>
            <div class="hud-item money">
              <span class="hud-label">TIỀN</span>
              <span class="hud-val" id="hud-val-money"></span>
            </div>
            <div class="hud-item rank">
              <span class="hud-label">UY TÍN</span>
              <span class="hud-val" id="hud-val-rank"></span>
            </div>
            <div class="hud-item heat">
              <span class="hud-label">LUẬT</span>
              <span class="hud-val" id="hud-val-heat"></span>
            </div>
          </div>

          <button id="btn-toggle-top-menu" class="btn-popdown-trigger" title="Menu quản lý">
            <span>⚙️</span>
          </button>
        </div>

        <div class="hud-vertical-dock">
          <button id="btn-toggle-overtime" class="btn-overtime-toggle" title="Bán ca đêm (+30% tiền)">🌙</button>
        </div>

        <div class="hud-day-progress" id="hud-day-progress" style="display: none;"></div>

        <div class="hud-area-tag" id="hud-area-tag">
          <span class="hud-area-label" id="hud-area-label"></span>
          <span class="tag-real-stalls" id="hud-tag-real-stalls" style="display: none;"></span>
        </div>

        <div class="hud-guide-banner" id="hud-guide-banner" style="display: none;">
          👉 <strong>Chưa mở quán:</strong> Chạm vào các chấm ($) trên bản đồ để chọn góc mở quán! <a href="#" id="link-guide-banner" style="color: #FFD54F; text-decoration: underline; margin-left: 6px; cursor: pointer;">📖 Cách Chơi</a>
        </div>

        <div class="hud-lore" id="hud-lore" style="display: none; cursor: pointer;" title="Chạm để xem chi tiết tin nóng vỉa hè">
          <span class="hud-lore-fire" style="margin-right: 2px;">🔥</span>
          <span id="hud-lore-text"></span>
          <button id="btn-dismiss-lore" class="btn-dismiss-lore" title="Đóng">✕</button>
        </div>
      `;
    }

    // Cập nhật giá trị trực tiếp vào các phần tử (Zero layout churn, zero flickering!)
    const otBtn = document.getElementById('btn-toggle-overtime');
    if (otBtn) {
      if (otBtn.textContent !== '🌙') {
        otBtn.textContent = '🌙';
      }
      otBtn.title = state.isOvertimeActive
        ? 'Ca Đêm đang BẬT (+30% doanh thu, chú ý trật tự)'
        : 'Bật Bán Ca Đêm (+30% doanh thu)';
      if (state.isOvertimeActive) {
        otBtn.classList.add('active');
      } else {
        otBtn.classList.remove('active');
      }
      otBtn.style.display = hasStall ? 'inline-flex' : 'none';
    }

    AudioService.getInstance().setNightMode(state.currentShift === 'night' || state.isOvertimeActive);
    
    const levelEl = document.getElementById('hud-shop-level');
    const stallLevel = state.player.stallLevel ?? 1;
    if (levelEl) {
      const levelText = `${stallLevel}`;
      if (levelEl.textContent !== levelText) {
        levelEl.textContent = levelText;
      }
    }

    const dayEl = document.getElementById('hud-val-day');
    if (dayEl) {
      const dayText = `${state.dayNumber}`;
      if (dayEl.textContent !== dayText) {
        dayEl.textContent = dayText;
        dayEl.title = `${state.dayOfWeek || 'Thứ Hai'} — Ngày ${state.dayNumber}${state.isWeekend ? ' (Cuối tuần - Đông khách dạo chơi!)' : ''}`;
      }
    }

    const shiftEl = document.getElementById('hud-val-shift');
    if (shiftEl) {
      const shiftCfg = (GAME_CONFIG.shifts as Record<string, { name: string; emoji: string } | undefined>)[state.currentShift] ?? { name: 'Sáng Sớm', emoji: '🌅' };
      const shiftText = `${shiftCfg.emoji}`;
      if (shiftEl.textContent !== shiftText) {
        shiftEl.textContent = shiftText;
        shiftEl.title = `Ca ${shiftCfg.name}`;
      }
    }

    const weatherEl = document.getElementById('hud-val-weather');
    if (weatherEl) {
      let weatherIcons = '☀️🌤️';
      if (state.weather) {
        const name = state.weather.name.toLowerCase();
        if (name.includes('nóng')) weatherIcons = '☀️🔥';
        else if (name.includes('mưa')) weatherIcons = '🌧️☔';
        else if (name.includes('gió')) weatherIcons = '💨🍃';
        else if (name.includes('mát') || name.includes('mây')) weatherIcons = '⛅☁️';
        else weatherIcons = '☀️🌤️';
      }
      if (weatherEl.textContent !== weatherIcons) {
        weatherEl.textContent = weatherIcons;
        weatherEl.title = state.weather ? state.weather.description : '';
      }
    }

    const moneyEl = document.getElementById('hud-val-money');
    const moneyK = Math.floor(state.player.money / 1000);
    const compactMoney = moneyK.toLocaleString('vi-VN') + 'k';
    if (moneyEl && moneyEl.textContent !== compactMoney) {
      moneyEl.textContent = compactMoney;
    }

    const rankEl = document.getElementById('hud-val-rank');
    if (rankEl && rankEl.textContent !== `★${state.player.reputation}`) {
      rankEl.textContent = `★${state.player.reputation}`;
    }

    const heatEl = document.getElementById('hud-val-heat');
    if (heatEl) {
      const heatText = `${heatPct}%`;
      if (heatEl.textContent !== heatText) {
        heatEl.textContent = heatText;
        heatEl.style.color = heatPct > 60 ? '#EF5350' : '#66BB6A';
      }
    }

    const progressEl = document.getElementById('hud-day-progress');
    if (progressEl) {
      if (state.dayStats.salesCount > 0) {
        progressEl.style.display = 'block';
        const progText = `Hôm nay: +${state.dayStats.revenue.toLocaleString('vi-VN')}đ | Lời: +${state.dayStats.profit.toLocaleString('vi-VN')}đ | ${state.dayStats.salesCount} đơn`;
        if (progressEl.textContent !== progText) {
          progressEl.textContent = progText;
        }
      } else {
        progressEl.style.display = 'none';
      }
    }

    const areaLabelEl = document.getElementById('hud-area-label');
    if (areaLabelEl) {
      const compactAddr = `🗺️ ${formatCompactAddress(state.neighborhood?.label)}`;
      if (areaLabelEl.textContent !== compactAddr) {
        areaLabelEl.textContent = compactAddr;
        areaLabelEl.title = state.neighborhood?.label ?? '';
      }
    }

    const realStallsEl = document.getElementById('hud-tag-real-stalls');
    if (realStallsEl) {
      if (state.neighborhood) {
        const count = state.neighborhood.pois.filter((p) => p.type === 'real_stall').length;
        realStallsEl.style.display = 'inline-block';
        const stallText = `🏪 ${count} tiệm`;
        if (realStallsEl.textContent !== stallText) {
          realStallsEl.textContent = stallText;
        }
      } else {
        realStallsEl.style.display = 'none';
      }
    }

    const guideEl = document.getElementById('hud-guide-banner');
    if (guideEl) {
      guideEl.style.display = !hasStall ? 'block' : 'none';
    }

    const loreEl = document.getElementById('hud-lore');
    const loreTextEl = document.getElementById('hud-lore-text');
    if (loreEl && loreTextEl) {
      if (hasStall && state.narrativeQuote && !this.isLoreDismissed) {
        if (loreTextEl.textContent !== state.narrativeQuote) {
          loreTextEl.textContent = state.narrativeQuote;
        }
        loreEl.style.display = 'flex';
      } else {
        loreEl.style.display = 'none';
      }
    }
  }

  private renderMenuModal(state: Readonly<GameState>): void {
    const currentMenu = state.neighborhood
      ? getRegionalMenu(state.neighborhood.center.lat, state.neighborhood.center.lon, state.neighborhood.label)
      : SIDEWALK_MENU;

    const activeIds = state.player.activeMenu ?? currentMenu.filter((m) => m.unlocked).map((m) => m.id).slice(0, 2);
    const menuKey = `${activeIds.slice().sort().join(',')}_${state.player.reputation}_${state.neighborhood?.id ?? ''}`;

    if (this.lastMenuModalKey === menuKey && this.menuModalElement.style.display === 'flex') {
      return;
    }
    this.lastMenuModalKey = menuKey;

    this.menuModalElement.style.display = 'flex';
    this.menuModalElement.innerHTML = `
      <div class="dialog-card">
        <div class="dialog-header">
          <h3>📋 Thực Đơn Quán Cóc (${state.neighborhood?.label.split(',')[0] ?? 'Vỉa Hè'})</h3>
          <button id="btn-close-menu" class="btn-close-modal">✕</button>
        </div>
        <p class="dialog-desc">Chọn món muốn mở bán. Giá cả và món đặc sản tự động biến thiên theo địa bàn thực tế!</p>

        <div class="menu-advisor-tip" style="background: rgba(255, 179, 0, 0.12); border-left: 3px solid #FFB300; border-radius: 6px; padding: 8px 10px; margin-bottom: 12px; font-size: 11px; line-height: 1.4; color: #FFF8E1; display: flex; gap: 8px; align-items: flex-start;">
          <span class="tip-icon" style="font-size: 14px; line-height: 1;">💡</span>
          <span class="tip-text"><strong>Mẹo Bà Trà Đá:</strong> Món có ký hiệu <span class="badge-fresh-tag" style="background: #E65100; color: #FFF; padding: 1px 5px; border-radius: 4px; font-weight: bold;">⏱️ Tươi trong ngày</span> (nước mía, nước sâm, cà phê...) nếu cuối ngày không bán hết sẽ bị ôi thiu đổ bỏ. Ngày mưa gió hoặc ít khách, sếp nên tắt bớt đồ tươi để tránh bị trừ tiền hao hụt nhé!</span>
        </div>

        <div class="menu-list">
          ${currentMenu.map((item) => {
            const isUnlocked = item.unlocked || state.player.reputation >= item.requiredRep;
            const isSelling = isUnlocked && activeIds.includes(item.id);

            return `
              <div class="menu-item-row ${isUnlocked ? '' : 'locked'}">
                <span class="item-icon">${item.icon}</span>
                <div class="item-info">
                  <div style="display: flex; align-items: center; gap: 6px; flex-wrap: wrap;">
                    <strong>${item.name}</strong>
                    ${
                      item.shelfLife === 'perishable_drink'
                        ? `<span class="badge-shelf badge-fresh" style="background: rgba(239, 83, 80, 0.2); color: #EF5350; border: 1px solid rgba(239, 83, 80, 0.4); font-size: 9px; padding: 1px 4px; border-radius: 4px;" title="Nước uống pha sẵn / ép tươi: Dễ bị chua thiu cuối ngày nếu ế khách">⏱️ Pha sẵn (Dễ thiu)</span>`
                        : item.shelfLife === 'storable'
                        ? `<span class="badge-shelf badge-storable" style="background: rgba(33, 150, 243, 0.2); color: #64B5F6; border: 1px solid rgba(33, 150, 243, 0.4); font-size: 9px; padding: 1px 4px; border-radius: 4px;" title="Nguyên liệu cất giữ được: Cà phê, sữa, chanh, trà mạn để tủ cất giữ không bị thiu bỏ">🧊 Cất giữ được (0đ hao hụt)</span>`
                        : `<span class="badge-shelf badge-packaged" style="background: rgba(102, 187, 106, 0.2); color: #66BB6A; border: 1px solid rgba(102, 187, 106, 0.4); font-size: 9px; padding: 1px 4px; border-radius: 4px;" title="Đồ khô đóng gói: Để được lâu ngày, an toàn khi quán vắng">📦 Đóng gói (Lâu hỏng)</span>`
                    }
                  </div>
                  <div class="item-prices">
                    <span>Bán: <strong>${item.sellPrice.toLocaleString()}đ</strong></span>
                    <span>Vốn: ${item.costPrice.toLocaleString()}đ</span>
                    <span class="item-profit">Lời: +${item.profit.toLocaleString()}đ</span>
                  </div>
                  <small>${item.desc}</small>
                </div>
                <div class="item-action">
                  ${
                    !isUnlocked
                      ? `<span class="badge-lock">Cần ★${item.requiredRep}</span>`
                      : `
                    <button class="btn btn-toggle-menu ${isSelling ? 'btn-selling' : 'btn-pause'}" data-id="${item.id}">
                      ${isSelling ? 'Đang Bán ✓' : 'Tạm Dừng'}
                    </button>
                  `
                  }
                </div>
              </div>
            `;
          }).join('')}
        </div>
      </div>
    `;
  }

  private renderSalesModal(state: Readonly<GameState>): void {
    const sales = state.recentSales ?? [];
    const salesKey = `${sales.length}_${sales[0]?.time ?? 0}`;
    if (this.lastSalesModalKey === salesKey && this.salesModalElement.style.display === 'flex') {
      return;
    }
    this.lastSalesModalKey = salesKey;

    const totalProfit = sales.reduce((sum, s) => sum + s.profit, 0);
    const totalRevenue = sales.reduce((sum, s) => sum + s.sellPrice, 0);

    this.salesModalElement.style.display = 'flex';
    this.salesModalElement.innerHTML = `
      <div class="dialog-card">
        <div class="dialog-header">
          <h3>🧾 Sổ Bán Hàng & Doanh Thu</h3>
          <button id="btn-close-sales" class="btn-close-modal">✕</button>
        </div>

        <div class="sales-summary-bar">
          <div>Tổng thu: <strong>${totalRevenue.toLocaleString()}đ</strong></div>
          <div>Lợi nhuận gộp: <strong style="color: #66BB6A;">+${totalProfit.toLocaleString()}đ</strong></div>
        </div>

        <p class="dialog-desc">Danh sách 20 giao dịch bán gần nhất khi khách ngồi vào ghế của quán:</p>

        <div class="sales-list">
          ${
            sales.length === 0
              ? `<div class="empty-sales">Chưa có giao dịch nào. Mở quán và đợi khách ghé uống nước nhé sếp!</div>`
              : sales
                  .map((s) => {
                    const timeStr = new Date(s.time).toLocaleTimeString('vi-VN', {
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit',
                    });
                    return `
                  <div class="sale-row">
                    <span class="sr-time">${timeStr}</span>
                    <span class="sr-icon">${s.icon}</span>
                    <div class="sr-title">
                      <strong>${s.itemName} ${s.isTakeaway ? '<span class="badge-takeaway" style="background:#FFB300;color:#000;font-size:9px;padding:1px 4px;border-radius:4px;font-weight:bold;margin-left:4px;">[Mang đi]</span>' : ''}</strong>
                      <small>Bán: ${s.sellPrice.toLocaleString()}đ | Vốn: ${s.costPrice.toLocaleString()}đ</small>
                    </div>
                    <span class="sr-profit">+${s.profit.toLocaleString()}đ</span>
                  </div>
                `;
                  })
                  .join('')
          }
        </div>
      </div>
    `;
  }

  private openRenameModal(state: Readonly<GameState>): void {
    this.renameModalElement.style.display = 'flex';
    this.renameModalElement.innerHTML = `
      <div class="dialog-card">
        <div class="dialog-header">
          <h3>✏️ Đổi Tên Quán & Biệt Danh</h3>
          <button id="btn-close-rename" class="btn-close-modal">✕</button>
        </div>

        <div class="lobby-form" style="margin-top: 12px;">
          <div class="form-group">
            <label>👤 Tên chủ quán:</label>
            <input type="text" id="inp-rename-user" maxlength="20" value="${state.player.username || ''}" />
          </div>

          <div class="form-group">
            <label>🏪 Tên quán vỉa hè:</label>
            <input type="text" id="inp-rename-stall" maxlength="30" value="${state.player.stallName || ''}" />
          </div>

          <div class="form-group">
            <div class="chips-label">Gợi ý tên hay:</div>
            <div class="preset-chips">
              <button type="button" class="chip-btn">Trà Cóc Vỉa Hè 🧋</button>
              <button type="button" class="chip-btn">Trà Chanh Chém Gió 🍋</button>
              <button type="button" class="chip-btn">Cà Phê Bệt Phố Cổ ☕</button>
              <button type="button" class="chip-btn">Nước Mía Siêu Sạch 🎋</button>
              <button type="button" class="chip-btn">Trà Đào Cam Sả 🍑</button>
            </div>
          </div>

          <button id="btn-save-rename" class="btn btn-primary" style="margin-top: 10px;">Lưu Thay Đổi</button>
        </div>
      </div>
    `;

    const userInp = document.getElementById('inp-rename-user') as HTMLInputElement;
    const stallInp = document.getElementById('inp-rename-stall') as HTMLInputElement;

    this.renameModalElement.querySelectorAll('.chip-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (stallInp) stallInp.value = btn.textContent?.replace(/[^\p{L}\p{N}\s]/gu, '').trim() || '';
      });
    });

    document.getElementById('btn-close-rename')?.addEventListener('click', () => {
      this.renameModalElement.style.display = 'none';
    });

    document.getElementById('btn-save-rename')?.addEventListener('click', () => {
      const username = userInp?.value.trim() || 'Chủ Quán';
      const stallName = stallInp?.value.trim() || 'Trà Cóc Vỉa Hè';
      this.dispatcher.dispatch({ type: 'SET_PLAYER_PROFILE', username, stallName });
      this.isLobbyRendered = false;
      this.renameModalElement.style.display = 'none';
    });
  }

  public async openAccountModal(state?: Readonly<GameState>): Promise<void> {
    const curState = state ?? this.lastState;
    let currentSave = SaveStore.load();
    if (!currentSave && curState) {
      SaveStore.save(curState as GameState);
      currentSave = SaveStore.load();
    }

    let uid = currentSave?.player.id ?? curState?.player.id ?? '';
    let secretKey = currentSave?.player.secretKey ?? curState?.player.secretKey ?? '';
    if (!uid || !uid.startsWith('usr_')) {
      uid = CryptoStore.generateUID();
    }
    if (!secretKey) {
      secretKey = CryptoStore.generateSecretKey();
    }

    if (currentSave) {
      currentSave.player.id = uid;
      currentSave.player.secretKey = secretKey;
      currentSave.secretKey = secretKey;
      SaveStore.importSave(currentSave);
    }

    let encryptedToken = '';
    if (currentSave) {
      try {
        encryptedToken = await CryptoStore.encryptSave(currentSave, secretKey);
      } catch (err) {
        console.error('[UIManager] Failed to encrypt save:', err);
      }
    }

    this.accountModalElement.style.display = 'flex';
    this.accountModalElement.innerHTML = `
      <div class="dialog-card account-card">
        <div class="dialog-header">
          <h3>🔑 TÀI KHOẢN & CHUYỂN MÁY</h3>
          <button id="btn-close-account" class="btn-close-modal">✕</button>
        </div>

        <div class="account-tabs">
          <button type="button" class="acc-tab-btn active" id="tab-btn-export">📤 Xuất / Sao Lưu</button>
          <button type="button" class="acc-tab-btn" id="tab-btn-restore">📥 Khôi Phục / Chuyển Máy</button>
        </div>

        <!-- TAB 1: XUẤT / SAO LƯU -->
        <div id="tab-content-export" class="account-tab-panel">
          <div class="account-field">
            <label>🆔 Mã Định Danh Quán (UID):</label>
            <div class="copy-input-group">
              <input type="text" readonly id="inp-acc-uid" value="${uid}" />
              <button type="button" id="btn-copy-uid" class="btn btn-secondary btn-copy">📋 Sao chép</button>
            </div>
          </div>

          <div class="account-field">
            <label>🔐 Khóa Bí Mật (Secret Key):</label>
            <div class="copy-input-group">
              <input type="password" readonly id="inp-acc-secret" value="${secretKey}" />
              <button type="button" id="btn-toggle-secret" class="btn btn-secondary" title="Hiện/Ẩn">👁️</button>
              <button type="button" id="btn-copy-secret" class="btn btn-secondary btn-copy">📋 Sao chép</button>
            </div>
            <div class="security-warning">
              ⚠️ <strong>CỰC KỲ QUAN TRỌNG:</strong> Không chia sẻ Secret Key cho bất kỳ ai! Bạn cần cả UID và Secret Key để phục hồi quán khi đổi máy.
            </div>
          </div>

          <div class="account-field">
            <label>📦 Mã Token Sao Lưu AES-256-GCM (Chống can thiệp):</label>
            <textarea readonly id="txt-acc-token" rows="3" class="token-textarea">${encryptedToken}</textarea>
            <button type="button" id="btn-copy-token" class="btn btn-primary" style="margin-top: 6px; width: 100%;">
              📋 Sao Chép Toàn Bộ Mã Token
            </button>
          </div>

          <div class="account-cloud-box">
            <div class="cloud-info">
              <strong>☁️ Sao Lưu Đám Mây Trực Tiếp (Cloud Sync)</strong>
              <small>Lưu quán trực tiếp lên máy chủ Cloudflare bằng UID & Secret Key của bạn.</small>
            </div>
            <button type="button" id="btn-cloud-backup" class="btn btn-cloud">☁️ Sao Lưu Lên Đám Mây Ngay</button>
            <div id="cloud-backup-status" class="cloud-status"></div>
          </div>
        </div>

        <!-- TAB 2: KHÔI PHỤC / CHUYỂN MÁY -->
        <div id="tab-content-restore" class="account-tab-panel" style="display: none;">
          <!-- Cách 1: Phục hồi Đám Mây -->
          <div class="restore-section">
            <h4>☁️ Cách 1: Tải Trực Tiếp Từ Đám Mây</h4>
            <p>Nếu bạn đã bấm "Sao Lưu Lên Đám Mây" trên máy cũ, chỉ cần nhập UID và Secret Key:</p>
            <div class="form-group" style="margin-bottom: 8px;">
              <label style="font-size: 11px; color: rgba(255,255,255,0.7);">Mã UID (usr_...):</label>
              <input type="text" id="inp-restore-uid" placeholder="Ví dụ: usr_1234abcd" />
            </div>
            <div class="form-group" style="margin-bottom: 10px;">
              <label style="font-size: 11px; color: rgba(255,255,255,0.7);">Khóa Bí Mật (sec_...):</label>
              <input type="password" id="inp-restore-cloud-secret" placeholder="Ví dụ: sec_1234567890abcdef" />
            </div>
            <button type="button" id="btn-restore-cloud" class="btn btn-primary" style="width: 100%;">
              ☁️ Tải Từ Đám Mây & Kiểm Tra
            </button>
          </div>

          <div class="restore-or-divider">— HOẶC DÙNG MÃ TOKEN —</div>

          <!-- Cách 2: Phục hồi bằng Token -->
          <div class="restore-section">
            <h4>📦 Cách 2: Dán Mã Token Sao Lưu</h4>
            <p>Dán chuỗi mã BCVH1... đã sao chép từ máy cũ kèm Secret Key để giải mã:</p>
            <div class="form-group" style="margin-bottom: 8px;">
              <label style="font-size: 11px; color: rgba(255,255,255,0.7);">Mã Token Sao Lưu (BCVH1...):</label>
              <textarea id="inp-restore-token" rows="3" class="token-textarea" placeholder="Dán mã BCVH1... vào đây"></textarea>
            </div>
            <div class="form-group" style="margin-bottom: 10px;">
              <label style="font-size: 11px; color: rgba(255,255,255,0.7);">Khóa Bí Mật (sec_...):</label>
              <input type="password" id="inp-restore-token-secret" placeholder="Ví dụ: sec_1234567890abcdef" />
            </div>
            <button type="button" id="btn-restore-token" class="btn btn-primary" style="width: 100%;">
              🔍 Giải Mã & Kiểm Tra Dữ Liệu
            </button>
          </div>

          <div id="restore-error" class="error-banner" style="display: none;"></div>
          <div id="restore-preview-area" style="display: none;"></div>
        </div>
      </div>
    `;

    const copyToClipboard = async (text: string, btn: HTMLButtonElement): Promise<void> => {
      try {
        await navigator.clipboard.writeText(text);
      } catch {
        const temp = document.createElement('textarea');
        temp.value = text;
        document.body.appendChild(temp);
        temp.select();
        document.execCommand('copy');
        document.body.removeChild(temp);
      }
      const orig = btn.textContent;
      btn.textContent = '✓ Đã chép!';
      setTimeout(() => {
        btn.textContent = orig;
      }, 2000);
    };

    const btnClose = document.getElementById('btn-close-account');
    btnClose?.addEventListener('click', () => {
      this.accountModalElement.style.display = 'none';
    });

    const tabBtnExport = document.getElementById('tab-btn-export');
    const tabBtnRestore = document.getElementById('tab-btn-restore');
    const tabContentExport = document.getElementById('tab-content-export');
    const tabContentRestore = document.getElementById('tab-content-restore');

    tabBtnExport?.addEventListener('click', () => {
      tabBtnExport.classList.add('active');
      tabBtnRestore?.classList.remove('active');
      if (tabContentExport) tabContentExport.style.display = 'block';
      if (tabContentRestore) tabContentRestore.style.display = 'none';
    });

    tabBtnRestore?.addEventListener('click', () => {
      tabBtnRestore.classList.add('active');
      tabBtnExport?.classList.remove('active');
      if (tabContentExport) tabContentExport.style.display = 'none';
      if (tabContentRestore) tabContentRestore.style.display = 'block';
    });

    const btnCopyUid = document.getElementById('btn-copy-uid') as HTMLButtonElement | null;
    btnCopyUid?.addEventListener('click', () => {
      if (btnCopyUid) void copyToClipboard(uid, btnCopyUid);
    });

    const btnCopySecret = document.getElementById('btn-copy-secret') as HTMLButtonElement | null;
    btnCopySecret?.addEventListener('click', () => {
      if (btnCopySecret) void copyToClipboard(secretKey, btnCopySecret);
    });

    const btnToggleSecret = document.getElementById('btn-toggle-secret') as HTMLButtonElement | null;
    const inpSecret = document.getElementById('inp-acc-secret') as HTMLInputElement | null;
    btnToggleSecret?.addEventListener('click', () => {
      if (inpSecret && btnToggleSecret) {
        if (inpSecret.type === 'password') {
          inpSecret.type = 'text';
          btnToggleSecret.textContent = '🙈';
        } else {
          inpSecret.type = 'password';
          btnToggleSecret.textContent = '👁️';
        }
      }
    });

    const btnCopyToken = document.getElementById('btn-copy-token') as HTMLButtonElement | null;
    btnCopyToken?.addEventListener('click', () => {
      if (btnCopyToken) void copyToClipboard(encryptedToken, btnCopyToken);
    });

    const btnCloudBackup = document.getElementById('btn-cloud-backup') as HTMLButtonElement | null;
    const cloudStatus = document.getElementById('cloud-backup-status');
    btnCloudBackup?.addEventListener('click', async () => {
      if (!encryptedToken) {
        if (cloudStatus) {
          cloudStatus.textContent = '❌ Chưa có dữ liệu quán để sao lưu!';
          cloudStatus.className = 'cloud-status error';
        }
        return;
      }
      if (btnCloudBackup) {
        btnCloudBackup.disabled = true;
        btnCloudBackup.textContent = '☁️ Đang đồng bộ...';
      }
      if (cloudStatus) {
        cloudStatus.textContent = 'Đang đồng bộ dữ liệu quán lên máy chủ đám mây...';
        cloudStatus.className = 'cloud-status pending';
      }
      try {
        const res = await fetch(getApiUrl('/api/account/backup'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            uid,
            secretKey,
            encryptedToken,
            saveSeq: currentSave?.saveSeq ?? 1,
            meta: {
              stallName: currentSave?.player.stallName ?? curState?.player.stallName ?? 'Quán Cóc',
              money: currentSave?.player.money ?? curState?.player.money ?? 0,
              dayNumber: currentSave?.dayNumber ?? curState?.dayNumber ?? 1,
            },
          }),
        });

        if (!res.ok) {
          const errData = (await res.json().catch(() => ({}))) as { error?: string };
          throw new Error(errData.error ?? `Lỗi HTTP ${res.status}`);
        }

        const timeStr = new Date().toLocaleTimeString('vi-VN');
        if (cloudStatus) {
          cloudStatus.textContent = `✓ Đã sao lưu lên đám mây thành công lúc ${timeStr}!`;
          cloudStatus.className = 'cloud-status success';
        }
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : 'Lỗi kết nối máy chủ';
        if (cloudStatus) {
          cloudStatus.textContent = `❌ Lỗi sao lưu: ${msg}`;
          cloudStatus.className = 'cloud-status error';
        }
      } finally {
        if (btnCloudBackup) {
          btnCloudBackup.disabled = false;
          btnCloudBackup.textContent = '☁️ Sao Lưu Lên Đám Mây Ngay';
        }
      }
    });

    const errorEl = document.getElementById('restore-error');
    const previewEl = document.getElementById('restore-preview-area');

    const showError = (msg: string) => {
      if (errorEl) {
        errorEl.textContent = `❌ ${msg}`;
        errorEl.style.display = 'block';
      }
      if (previewEl) previewEl.style.display = 'none';
    };

    const hideError = () => {
      if (errorEl) errorEl.style.display = 'none';
    };

    const showPreview = (decrypted: PersistentSave) => {
      hideError();
      if (!previewEl) return;
      const stallTitle = decrypted.player.stallName || 'Trà Cóc Vỉa Hè';
      const bossName = decrypted.player.username || 'Chủ Quán';
      const loc = decrypted.neighborhood?.label.split(',')[0] ?? 'Vỉa hè';

      previewEl.innerHTML = `
        <div class="restore-preview-card">
          <div class="preview-badge">🛡️ ĐÃ XÁC THỰC TOÀN VẸN (CHỐNG GIAN LẬN)</div>
          <h3>Thông tin quán khôi phục:</h3>
          <div class="preview-grid">
            <div>🏪 Quán: <strong>${stallTitle}</strong></div>
            <div>👤 Chủ: <strong>${bossName}</strong></div>
            <div>💰 Tiền: <strong style="color: #FFD54F;">${decrypted.player.money.toLocaleString('vi-VN')}₫</strong></div>
            <div>⭐ Uy tín: <strong>★${decrypted.player.reputation}</strong></div>
            <div>📅 Ngày: <strong>Ngày ${decrypted.dayNumber}</strong></div>
            <div>📍 Địa bàn: <strong>${loc}</strong></div>
          </div>
          <button type="button" id="btn-confirm-import" class="btn btn-confirm-import">
            ✅ XÁC NHẬN NẠP DỮ LIỆU & BẮT ĐẦU CHƠI
          </button>
        </div>
      `;
      previewEl.style.display = 'block';

      document.getElementById('btn-confirm-import')?.addEventListener('click', () => {
        const btn = document.getElementById('btn-confirm-import') as HTMLButtonElement | null;
        if (btn) {
          btn.disabled = true;
          btn.textContent = '🔄 Đang áp dụng dữ liệu quán...';
        }
        const ok = SaveStore.importSave(decrypted);
        if (ok) {
          setTimeout(() => {
            window.location.reload();
          }, 250);
        } else {
          showError('Không thể lưu dữ liệu vào bộ nhớ máy!');
          if (btn) btn.disabled = false;
        }
      });
    };

    // Restore from Cloud
    const btnRestoreCloud = document.getElementById('btn-restore-cloud') as HTMLButtonElement | null;
    btnRestoreCloud?.addEventListener('click', async () => {
      const restoreUid = (document.getElementById('inp-restore-uid') as HTMLInputElement | null)?.value.trim() ?? '';
      const restoreSec = (document.getElementById('inp-restore-cloud-secret') as HTMLInputElement | null)?.value.trim() ?? '';

      if (!restoreUid || !restoreSec) {
        showError('Vui lòng nhập cả UID (usr_...) và Khóa bí mật (sec_...)!');
        return;
      }

      if (btnRestoreCloud) {
        btnRestoreCloud.disabled = true;
        btnRestoreCloud.textContent = '☁️ Đang tải từ đám mây...';
      }
      hideError();

      try {
        const res = await fetch(getApiUrl('/api/account/restore'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ uid: restoreUid, secretKey: restoreSec }),
        });

        if (!res.ok) {
          const errData = (await res.json().catch(() => ({}))) as { error?: string };
          throw new Error(errData.error ?? `Lỗi HTTP ${res.status}`);
        }

        const data = (await res.json()) as { success: boolean; encryptedToken: string };
        if (!data.encryptedToken) {
          throw new Error('Dữ liệu trên đám mây không hợp lệ!');
        }

        const decrypted = await CryptoStore.decryptSave(data.encryptedToken, restoreSec);
        showPreview(decrypted);
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : 'Lỗi kết nối máy chủ';
        showError(msg);
      } finally {
        if (btnRestoreCloud) {
          btnRestoreCloud.disabled = false;
          btnRestoreCloud.textContent = '☁️ Tải Từ Đám Mây & Kiểm Tra';
        }
      }
    });

    // Restore from Token
    const btnRestoreToken = document.getElementById('btn-restore-token') as HTMLButtonElement | null;
    btnRestoreToken?.addEventListener('click', async () => {
      const tokenInput = (document.getElementById('inp-restore-token') as HTMLTextAreaElement | null)?.value.trim() ?? '';
      const secInput = (document.getElementById('inp-restore-token-secret') as HTMLInputElement | null)?.value.trim() ?? '';

      if (!tokenInput || !secInput) {
        showError('Vui lòng nhập cả Mã token và Khóa bí mật (sec_...)!');
        return;
      }

      if (btnRestoreToken) {
        btnRestoreToken.disabled = true;
        btnRestoreToken.textContent = '🔍 Đang giải mã & xác thực...';
      }
      hideError();

      try {
        const decrypted = await CryptoStore.decryptSave(tokenInput, secInput);
        showPreview(decrypted);
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : 'Mã token không hợp lệ hoặc sai Secret Key';
        showError(msg);
      } finally {
        if (btnRestoreToken) {
          btnRestoreToken.disabled = false;
          btnRestoreToken.textContent = '🔍 Giải Mã & Kiểm Tra Dữ Liệu';
        }
      }
    });
  }

  private renderZoomControls(): void {
    this.zoomControlsElement.innerHTML = `
      <button id="btn-ctrl-zoom-in" class="btn-zoom" title="Phóng to">+</button>
      <button id="btn-ctrl-zoom-out" class="btn-zoom" title="Thu nhỏ">−</button>
      <button id="btn-ctrl-zoom-reset" class="btn-zoom" title="Về giữa bản đồ">🎯</button>
    `;

    document.getElementById('btn-ctrl-zoom-in')?.addEventListener('click', () => {
      this.camera?.zoomIn(1.35);
    });

    document.getElementById('btn-ctrl-zoom-out')?.addEventListener('click', () => {
      this.camera?.zoomOut(1.35);
    });

    document.getElementById('btn-ctrl-zoom-reset')?.addEventListener('click', () => {
      this.camera?.resetZoom();
    });
  }

  public showLocationPicker(onSelect: (lat: number, lon: number, label: string) => void): void {
    this.locationModalElement.style.display = 'flex';
    this.locationModalElement.innerHTML = `
      <div class="location-dialog">
        <h2>📍 Chọn Địa Bàn Vỉa Hè</h2>
        <p class="dialog-subtitle">Nhập địa điểm thực tế, dùng GPS hoặc chọn các tụ điểm nổi tiếng để sinh bản đồ</p>
        
        <div class="search-box">
          <input id="inp-search-loc" type="text" placeholder="Tìm phố, ngã tư, quận, chợ..." />
          <button id="btn-do-search" class="btn btn-primary">Tìm</button>
        </div>

        <button id="btn-use-gps" class="btn btn-gps">🧭 Dùng Vị Trí GPS Của Tôi</button>

        ${this.renderRecentSearchesHtml('modal')}

        <div class="quick-spots-label">🔥 Địa bàn hot nhiều người tìm:</div>
        <div class="quick-spots">
          <button type="button" class="btn-quick-spot" data-lat="21.0580" data-lon="105.8139" data-label="Hồ Tây, Tây Hồ, Hà Nội">Hồ Tây</button>
          <button type="button" class="btn-quick-spot" data-lat="21.0368" data-lon="105.7905" data-label="Cầu Giấy, Cầu Giấy, Hà Nội">Cầu Giấy</button>
          <button type="button" class="btn-quick-spot" data-lat="10.7936" data-lon="106.7218" data-label="Landmark 81, Bình Thạnh, TP.HCM">Landmark 81</button>
          <button type="button" class="btn-quick-spot" data-lat="11.9404" data-lon="108.4377" data-label="Chợ Đà Lạt, Lâm Đồng">Chợ Đà Lạt</button>
          <button type="button" class="btn-quick-spot" data-lat="10.0452" data-lon="105.7469" data-label="Bến Ninh Kiều, Cần Thơ">Bến Ninh Kiều</button>
          <button type="button" class="btn-quick-spot" data-lat="16.0680" data-lon="108.2208" data-label="Cầu Rồng, Hải Châu, Đà Nẵng">Cầu Rồng</button>
          <button type="button" class="btn-quick-spot" data-lat="10.7769" data-lon="106.7009" data-label="Chợ Bến Thành, Quận 1, TP.HCM">Bến Thành</button>
          <button type="button" class="btn-quick-spot" data-lat="21.0285" data-lon="105.8542" data-label="Hồ Hoàn Kiếm, Hoàn Kiếm, Hà Nội">Hồ Gươm</button>
        </div>

        <div id="search-results-list" class="results-list"></div>

        <button id="btn-close-loc" class="btn btn-secondary" style="margin-top: 10px;">Đóng</button>
      </div>
    `;

    document.getElementById('btn-close-loc')?.addEventListener('click', () => {
      this.locationModalElement.style.display = 'none';
    });

    // Xóa lịch sử tìm gần đây trong modal
    document.getElementById('modal-btn-clear-recent')?.addEventListener('click', () => {
      SearchService.clearRecentSearches();
      const section = document.getElementById('modal-recent-section');
      if (section) section.remove();
    });

    const triggerSelect = (lat: number, lon: number, label: string) => {
      SearchService.saveRecentSearch({ lat, lon }, label);
      this.locationModalElement.style.display = 'none';
      onSelect(lat, lon, label);
    };

    // Chọn từ danh sách tìm gần đây trong modal
    this.locationModalElement.querySelectorAll('.chip-recent-spot').forEach((chip) => {
      chip.addEventListener('click', () => {
        const lat = parseFloat(chip.getAttribute('data-lat') || '21.0135');
        const lon = parseFloat(chip.getAttribute('data-lon') || '105.8504');
        const label = chip.getAttribute('data-label') || 'Vị trí đã chọn';
        triggerSelect(lat, lon, label);
      });
    });

    document.getElementById('btn-use-gps')?.addEventListener('click', async () => {
      const btn = document.getElementById('btn-use-gps') as HTMLButtonElement;
      if (btn) {
        btn.textContent = '🧭 Đang định vị GPS & IP...';
        btn.disabled = true;
      }
      try {
        const loc = await LocationService.getCurrentLocation();
        triggerSelect(loc.coords.lat, loc.coords.lon, loc.label);
      } finally {
        if (btn) {
          btn.textContent = '🧭 Dùng Vị Trí GPS Của Tôi';
          btn.disabled = false;
        }
      }
    });

    this.locationModalElement.querySelectorAll('.btn-quick-spot').forEach((btn) => {
      btn.addEventListener('click', () => {
        const lat = parseFloat(btn.getAttribute('data-lat') || '21.0135');
        const lon = parseFloat(btn.getAttribute('data-lon') || '105.8504');
        const label = btn.getAttribute('data-label') || 'Vị trí đã chọn';
        triggerSelect(lat, lon, label);
      });
    });

    const searchInput = document.getElementById('inp-search-loc') as HTMLInputElement;
    const searchBtn = document.getElementById('btn-do-search');

    const handleSearch = async (): Promise<void> => {
      const q = searchInput.value;
      if (!q.trim()) return;

      const resultsList = document.getElementById('search-results-list');
      if (searchBtn) {
        searchBtn.innerHTML = '<span class="hourglass-anim-btn">⏳</span>';
      }
      if (resultsList) {
        resultsList.innerHTML = `
          <div class="search-hourglass-box">
            <div class="hourglass-anim">⏳</div>
            <div class="search-hourglass-title">Đang lật đồng hồ cát tìm kiếm...</div>
            <small class="search-hourglass-sub">Đang định vị "${q}" trên bản đồ vệ tinh</small>
          </div>
        `;
      }

      const results = await SearchService.searchLocations(q);

      if (searchBtn) {
        searchBtn.textContent = 'Tìm';
      }

      if (!resultsList) return;

      if (results.length === 0) {
        resultsList.innerHTML = `
          <div class="search-empty-box">
            <div style="font-size: 24px; margin-bottom: 6px;">🔍</div>
            <div style="font-size: 13px; font-weight: bold; color: #ECEFF1;">Không tìm thấy địa điểm "${q}"</div>
            <small style="color: #90A4AE; font-size: 11px;">Thử tìm tên quận/huyện, thành phố hoặc địa danh nổi tiếng (Cầu Giấy, Hồ Tây, Bến Thành, Landmark 81...)</small>
          </div>
        `;
        return;
      }

      resultsList.innerHTML = results
        .map(
          (r, idx) => `
        <div class="result-item" data-idx="${idx}">
          <strong>${r.label.split(',')[0]}</strong>
          <small>${r.label}</small>
        </div>
      `
        )
        .join('');

      resultsList.querySelectorAll('.result-item').forEach((item) => {
        const selectItem = () => {
          const idx = parseInt(item.getAttribute('data-idx') || '0', 10);
          const chosen = results[idx];
          if (chosen) {
            triggerSelect(chosen.coords.lat, chosen.coords.lon, chosen.label);
          }
        };
        item.addEventListener('click', selectItem);
        item.addEventListener('touchend', selectItem);
      });
    };

    searchBtn?.addEventListener('click', handleSearch);
    searchInput?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') handleSearch();
    });
  }

  private renderDayReportModal(state: Readonly<GameState>): void {
    if (this.lastReportDayNumber === state.dayNumber && this.dayReportElement.style.display === 'flex') {
      return;
    }
    this.lastReportDayNumber = state.dayNumber;
    AudioService.getInstance().playCelebration();

    const stats = state.dayStats;
    const depCost = stats.depreciationCost ?? (state.player.ownedStalls.length > 0 ? 25_000 : 0);
    const staffCost = stats.staffCost ?? 0;
    const wasteCost = stats.wasteCost ?? 0;
    const rentCost = stats.rentPaid;
    const rawCost = Math.max(0, stats.cost - depCost - staffCost - wasteCost);
    const netProfit = stats.revenue - rawCost - depCost - staffCost - wasteCost - rentCost;

    this.dayReportElement.style.display = 'flex';
    this.dayReportElement.innerHTML = `
      <div class="dialog-card day-report-card">
        <div class="receipt-header">
          <span class="receipt-icon">📊</span>
          <h3 class="receipt-title">Ngày ${state.dayNumber} — Tổng Kết</h3>
          <small class="receipt-sub">${state.player.stallName || 'Quán Cóc'} • ${state.neighborhood?.label.split(',')[0] ?? 'Vỉa Hè'}</small>
        </div>

        <div class="receipt-divider"></div>

        <div class="receipt-body">
          <div class="receipt-row">
            <span>Thời tiết trong ngày:</span>
            <strong>${state.weather ? `${state.weather.emoji} ${state.weather.name}` : '☀️ Nắng'}</strong>
          </div>
          <div class="receipt-row">
            <span>Đơn hàng đã bán:</span>
            <strong>${stats.salesCount} đơn hàng</strong>
          </div>
          <div class="receipt-row">
            <span>Doanh thu:</span>
            <strong class="receipt-pos">+${stats.revenue.toLocaleString('vi-VN')}₫</strong>
          </div>
          <div class="receipt-row">
            <span>Giá vốn nguyên liệu (COGS):</span>
            <strong class="receipt-neg">-${rawCost.toLocaleString('vi-VN')}₫</strong>
          </div>
          <div class="receipt-row">
            <span>🛠️ Khấu hao vật tư thiết bị:</span>
            <strong class="receipt-neg">-${depCost.toLocaleString('vi-VN')}₫</strong>
          </div>
          <div class="receipt-row">
            <span>Tiền thuê vỉa hè:</span>
            <strong class="receipt-neg">-${rentCost.toLocaleString('vi-VN')}₫</strong>
          </div>
          ${
            staffCost > 0
              ? `
          <div class="receipt-row" style="color: #64B5F6;">
            <span>👥 Lương nhân viên phụ quán:</span>
            <strong class="receipt-neg" style="color: #42A5F5;">-${staffCost.toLocaleString('vi-VN')}₫</strong>
          </div>`
              : ''
          }
          ${
            wasteCost > 0
              ? `
          <div class="receipt-row" style="color: #FF8A80;">
            <span>🗑️ Hao hụt đồ tươi thiu bỏ:</span>
            <strong class="receipt-neg" style="color: #FF5252;">-${wasteCost.toLocaleString('vi-VN')}₫</strong>
          </div>`
              : ''
          }

        <div class="receipt-divider"></div>

          <div class="receipt-row receipt-profit ${netProfit >= 0 ? 'profit-pos' : 'profit-neg'}">
            <span>Lợi nhuận ròng ngày:</span>
            <strong>${netProfit >= 0 ? '+' : ''}${netProfit.toLocaleString('vi-VN')}₫</strong>
          </div>

        <div class="receipt-divider"></div>

          <div class="receipt-summary-alltime">
            <div class="alltime-label">TÍCH LUỸ SỰ NGHIỆP:</div>
            <div class="alltime-stats-grid">
              <div class="at-item"><span>Tổng doanh thu:</span> <strong>${(state.player.totalRevenue ?? 0).toLocaleString('vi-VN')}₫</strong></div>
              <div class="at-item"><span>Tổng lợi nhuận:</span> <strong>${(state.player.totalProfit ?? 0).toLocaleString('vi-VN')}₫</strong></div>
              <div class="at-item"><span>Tổng đơn đã bán:</span> <strong>${(state.player.totalSales ?? 0).toLocaleString('vi-VN')} đơn</strong></div>
            </div>
          </div>

          ${
            netProfit <= 0 || stats.salesCount < 18
              ? `
          <div class="relocate-advisor-card warning" style="background: rgba(244, 67, 54, 0.12); border-left: 3px solid #F44336; border-radius: 6px; padding: 10px; margin: 12px 0 6px 0; text-align: left;">
            <div class="advisor-title" style="color: #FF5252; font-weight: bold; font-size: 12px; margin-bottom: 4px;">⚠️ Góc này ế ẩm / Thua lỗ!</div>
            <div class="advisor-desc" style="font-size: 11px; color: #FFCDD2; margin-bottom: 8px; line-height: 1.35;">Bà Trà Đá: <em>"Bán ở góc vắng thế này thì tiền đâu trả tiền mặt bằng sếp ơi! Hãy sang nhượng quán và chuyển ra Cổng Chợ hoặc Ngã Tư đông đúc hơn!"</em></div>
            <button id="btn-suggest-relocate" class="btn-suggest-relocate" style="background: #D32F2F; color: #FFF; border: none; padding: 6px 12px; border-radius: 4px; font-size: 11px; cursor: pointer; font-weight: bold; width: 100%;">📍 Tìm Điểm Bán Mới Đắc Địa ➔</button>
          </div>
          `
              : netProfit > 100_000 && (state.player.money ?? 0) >= 150_000
              ? `
          <div class="relocate-advisor-card upgrade" style="background: rgba(76, 175, 80, 0.12); border-left: 3px solid #4CAF50; border-radius: 6px; padding: 10px; margin: 12px 0 6px 0; text-align: left;">
            <div class="advisor-title" style="color: #81C784; font-weight: bold; font-size: 12px; margin-bottom: 4px;">🚀 Muốn Đột Phá Doanh Số?</div>
            <div class="advisor-desc" style="font-size: 11px; color: #E8F5E9; line-height: 1.35;">Bà Trà Đá: <em>"Vốn đã dày rồi, sếp có thể chuyển sang Mặt Bằng Vàng (Prime Location) để tăng gấp đôi lượng khách!"</em></div>
          </div>
          `
              : ''
          }
        </div>

        <button id="btn-next-day" class="btn btn-primary btn-large">Sang Ngày ${state.dayNumber + 1} →</button>
      </div>
    `;
  }

  private showResetConfirmation(): void {
    this.confirmModalElement.style.display = 'flex';
    this.confirmModalElement.innerHTML = `
      <div class="dialog-card reset-confirm-card" style="z-index: 1000000; border: 2px solid #EF5350;">
        <div class="dialog-header">
          <h3 style="color: #FFCDD2;">🗑️ Xoá Dữ Liệu & Chơi Lại?</h3>
        </div>
        <p class="dialog-desc" style="color: #FF8A80; font-size: 13px; line-height: 1.5; margin: 12px 0;">
          Toàn bộ tiến trình chơi, quầy quán, doanh thu và danh tiếng sẽ bị xoá vĩnh viễn. Bạn có chắc chắn muốn xóa dữ liệu và bắt đầu lại từ đầu?
        </p>
        <div class="lobby-actions-row" style="margin-top: 20px; display: flex; gap: 10px;">
          <button id="btn-cancel-reset" class="btn btn-secondary" style="min-height: 46px;">Huỷ Bỏ</button>
          <button id="btn-confirm-reset" class="btn btn-danger-action" style="min-height: 46px; background: #D32F2F; color: #FFF; font-weight: bold;">Xác Nhận Reset</button>
        </div>
      </div>
    `;

    const cancelBtn = document.getElementById('btn-cancel-reset');
    const confirmBtn = document.getElementById('btn-confirm-reset');

    const handleCancel = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      this.confirmModalElement.style.display = 'none';
    };

    const handleConfirm = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      this.confirmModalElement.style.display = 'none';
      try {
        localStorage.clear();
      } catch {}
      this.dispatcher.dispatch({ type: 'RESET_GAME' });
      setTimeout(() => {
        window.location.reload();
      }, 150);
    };

    cancelBtn?.addEventListener('click', handleCancel);
    cancelBtn?.addEventListener('touchend', handleCancel);
    confirmBtn?.addEventListener('click', handleConfirm);
    confirmBtn?.addEventListener('touchend', handleConfirm);
  }

  private showWeatherToast(weather: import('../domain/types.ts').WeatherState): void {
    const toast = document.createElement('div');
    toast.className = 'weather-toast';
    toast.innerHTML = `<span>${weather.emoji}</span> <strong>${weather.name}:</strong> <span>${weather.description}</span>`;
    this.container.appendChild(toast);
    setTimeout(() => {
      toast.classList.add('fade-out');
      setTimeout(() => toast.remove(), 400);
    }, 3000);
  }

  private renderUpgradeModal(state: Readonly<GameState>): void {
    const upgKey = `${JSON.stringify(state.player.upgrades)}_${state.player.money}`;
    if (this.lastUpgradeModalKey === upgKey && this.upgradeModalElement.style.display === 'flex') {
      return;
    }
    this.lastUpgradeModalKey = upgKey;

    const upgradeKeys: import('../domain/types.ts').UpgradeId[] = ['umbrella', 'ice_machine', 'signboard', 'speaker'];
    const upgradesList = upgradeKeys.map((upgId) => {
      const def = GAME_CONFIG.upgrades[upgId];
      const alreadyOwned = Boolean(state.player.upgrades[upgId]);
      const canAfford = state.player.money >= def.cost;
      return {
        id: upgId,
        name: def.name,
        emoji: def.emoji,
        description: def.description,
        cost: def.cost,
        trafficBonus: def.trafficBonus,
        reputationBonus: def.reputationBonus,
        alreadyOwned,
        canAfford,
      };
    });

    this.upgradeModalElement.style.display = 'flex';
    this.upgradeModalElement.innerHTML = `
      <div class="dialog-card upgrade-card">
        <div class="dialog-header">
          <h3>🔧 Nâng Cấp Quán (${state.player.stallName || 'Quán Cóc'})</h3>
          <button id="btn-close-upgrade" class="btn-close-modal">✕</button>
        </div>
        <p class="dialog-desc">Trang bị thêm tiện ích vỉa hè để tăng lượng khách và danh tiếng cho quán!</p>

        <div class="upgrade-list">
          ${upgradesList
            .map(
              (u) => `
            <div class="upgrade-item ${u.alreadyOwned ? 'owned' : ''}">
              <div class="upgrade-icon">${u.emoji}</div>
              <div class="upgrade-info">
                <strong>${u.name}</strong>
                <small>${u.description} (★+${u.reputationBonus})</small>
              </div>
              <div class="upgrade-cta">
                ${
                  u.alreadyOwned
                    ? `<span class="badge-owned">✓ Đã có</span>`
                    : `<button class="btn btn-primary btn-buy-upgrade" data-upgrade="${u.id}" ${u.canAfford ? '' : 'disabled'}>
                         ${u.cost.toLocaleString('vi-VN')}₫
                       </button>`
                }
              </div>
            </div>
          `
            )
            .join('')}
        </div>
      </div>
    `;
  }

  private renderMilestoneToast(state: Readonly<GameState>): void {
    if (!state.pendingMilestone) {
      if (!this.currentMilestoneId) {
        this.milestoneElement.style.display = 'none';
      }
      return;
    }

    if (this.currentMilestoneId === state.pendingMilestone.id) {
      return;
    }

    const milestone = state.pendingMilestone;
    this.currentMilestoneId = milestone.id;

    this.milestoneElement.style.display = 'flex';
    this.milestoneElement.innerHTML = `
      <div class="milestone-toast">
        <div class="milestone-emoji">${milestone.emoji}</div>
        <div class="milestone-text">
          <strong>🎖️ Thành Tích Mới!</strong>
          <p>${milestone.message}</p>
        </div>
      </div>
    `;

    if (this.milestoneTimer) {
      clearTimeout(this.milestoneTimer);
    }

    this.milestoneTimer = window.setTimeout(() => {
      this.milestoneElement.style.display = 'none';
      this.currentMilestoneId = null;
      this.dispatcher.dispatch({ type: 'SET_MILESTONE', milestone: null });
    }, 3500);
  }

  public showTrendsToast(topic: string): void {
    if (!this.saleToastElement) return;
    this.saleToastElement.innerHTML = `
      <span class="sale-icon">🔥</span>
      <div class="sale-details">
        <span class="sale-title">Tin Nóng Vỉa Hè (Google Trends VN):</span>
        <small class="sale-meta" style="color: #FFD54F; font-size: 11px; font-weight: bold;">"${topic}"</small>
      </div>
    `;
    this.saleToastElement.style.display = 'flex';
    requestAnimationFrame(() => {
      this.saleToastElement.classList.add('visible');
    });

    if (this.saleToastTimer) window.clearTimeout(this.saleToastTimer);
    this.saleToastTimer = window.setTimeout(() => {
      this.saleToastElement.classList.remove('visible');
      window.setTimeout(() => {
        this.saleToastElement.style.display = 'none';
      }, 300);
    }, 3500);
  }

  public openTrendsModal(): void {
    if (!this.lastState) return;
    const state = this.lastState;
    const items =
      state.trendingItems && state.trendingItems.length > 0
        ? state.trendingItems
        : state.currentTrendingTopic
        ? [
            {
              query: state.currentTrendingTopic,
              headline: state.currentTrendingTopic,
              traffic: '5.000+',
              source: 'Google Trends VN',
              url: 'https://trends.google.com/trending/rss?geo=VN',
            },
          ]
        : [];

    const sourceUrl = 'https://trends.google.com/trending/rss?geo=VN';

    this.trendsModalElement.innerHTML = `
      <div class="dialog-card trends-dialog-card" style="max-width: 520px; width: 92%; max-height: 85vh; display: flex; flex-direction: column;">
        <div class="dialog-header" style="border-bottom: 1px solid rgba(255,255,255,0.1); padding-bottom: 10px;">
          <div style="display: flex; align-items: center; gap: 8px;">
            <span style="font-size: 24px;">🔥</span>
            <div>
              <h3 style="margin: 0; font-size: 16px; color: #FFD54F;">BẢNG TIN VỈA HÈ — GOOGLE TRENDS VN</h3>
              <small style="color: #90A4AE; font-size: 11px;">
                Nguồn tin: <a href="${sourceUrl}" target="_blank" rel="noopener noreferrer" style="color: #4FC3F7; text-decoration: underline;">Google Trends RSS VN</a> • Cập nhật tự động mỗi 30p
              </small>
            </div>
          </div>
          <button id="btn-close-trends" class="btn-close-modal">✕</button>
        </div>

        <div class="trends-scroll-body" style="overflow-y: auto; padding: 12px 2px; display: flex; flex-direction: column; gap: 10px; flex: 1;">
          ${
            items.length === 0
              ? `
            <div style="text-align: center; padding: 25px 10px; color: #90A4AE;">
              <div class="hourglass-anim" style="font-size: 32px; margin-bottom: 8px;">⏳</div>
              <p>Đang kết nối vệ tinh cập nhật Google Trends...</p>
            </div>
          `
              : items
                  .map((item, idx) => {
                    const trafficBadge = item.traffic
                      ? `<span class="trend-traffic-badge" style="background: rgba(255,112,67,0.2); color: #FF7043; border: 1px solid rgba(255,112,67,0.4); font-size: 10px; font-weight: bold; padding: 1px 6px; border-radius: 10px;">🔥 ${item.traffic} tìm kiếm</span>`
                      : '';
                    const sourceBadge = item.source
                      ? `<span style="color: #81C784; font-size: 11px;">📰 ${item.source}</span>`
                      : '';
                    const linkBtn = item.url
                      ? `<a href="${item.url}" target="_blank" rel="noopener noreferrer" class="btn-read-news" style="display: inline-block; padding: 3px 8px; font-size: 10px; background: #263238; border: 1px solid #455A64; color: #ECEFF1; border-radius: 4px; text-decoration: none; margin-left: auto;">Đọc báo ↗</a>`
                      : '';
                    return `
              <div class="trend-card-item" style="background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); border-radius: 8px; padding: 10px; display: flex; gap: 10px; align-items: flex-start;">
                <div style="font-size: 14px; font-weight: bold; color: #FFB300; min-width: 20px;">#${idx + 1}</div>
                <div style="flex: 1; min-width: 0;">
                  <div style="display: flex; align-items: center; gap: 6px; flex-wrap: wrap; margin-bottom: 4px;">
                    <strong style="color: #FFF; font-size: 13px;">${item.query}</strong>
                    ${trafficBadge}
                  </div>
                  ${item.headline ? `<div style="font-size: 12px; color: #CFD8DC; line-height: 1.4; margin-bottom: 6px;">${item.headline}</div>` : ''}
                  <div style="display: flex; align-items: center; gap: 8px;">
                    ${sourceBadge}
                    ${linkBtn}
                  </div>
                </div>
              </div>
            `;
                  })
                  .join('')
          }
        </div>

        <div class="dialog-footer" style="border-top: 1px solid rgba(255,255,255,0.1); padding-top: 10px; display: flex; justify-content: space-between; align-items: center;">
          <small style="color: #78909C; font-size: 10px; max-width: 72%;">
            💬 Khách ngồi quán sẽ tán gẫu các tin nóng này, tăng sinh động và điểm Danh Tiếng vỉa hè!
          </small>
          <button id="btn-dismiss-trends-modal" class="btn btn-secondary" style="padding: 6px 14px; font-size: 12px;">Đóng</button>
        </div>
      </div>
    `;

    this.trendsModalElement.style.display = 'flex';
  }

  public openAudioModal(): void {
    const audio = AudioService.getInstance();
    const isBgmOn = audio.isBgmOn();
    const bgmVolPct = Math.round(audio.getBgmVolume() * 100);
    const isSfxOn = audio.isSfxOn();
    const sfxVolPct = Math.round(audio.getSfxVolume() * 100);

    this.audioModalElement.style.display = 'flex';
    this.audioModalElement.innerHTML = `
      <div class="dialog-card audio-settings-card">
        <div class="audio-settings-header">
          <div style="display: flex; align-items: center; gap: 8px;">
            <span style="font-size: 22px;">🔊</span>
            <strong style="font-size: 16px; color: #FFE082;">CÀI ĐẶT ÂM THANH</strong>
          </div>
          <button id="btn-close-audio-modal" class="btn-popdown-close">✕</button>
        </div>

        <div class="audio-control-group">
          <div class="audio-control-row">
            <div class="audio-label-group">
              <span class="audio-icon">🎵</span>
              <div>
                <strong style="color: #ECEFF1; font-size: 14px;">Nhạc Nền (BGM)</strong>
                <div style="font-size: 11px; color: #90A4AE;">Giai điệu đường phố lofi thư giãn</div>
              </div>
            </div>
            <button id="btn-toggle-bgm" class="btn-audio-pill ${isBgmOn ? 'active' : ''}">
              ${isBgmOn ? '✓ BẬT' : 'TẮT'}
            </button>
          </div>
          <div class="audio-slider-row">
            <span style="font-size: 14px;">🔈</span>
            <input type="range" id="slider-bgm-volume" min="0" max="100" value="${bgmVolPct}" ${!isBgmOn ? 'disabled' : ''} />
            <span class="audio-vol-pct" id="val-bgm-pct">${bgmVolPct}%</span>
          </div>
        </div>

        <div class="audio-control-group">
          <div class="audio-control-row">
            <div class="audio-label-group">
              <span class="audio-icon">🔔</span>
              <div>
                <strong style="color: #ECEFF1; font-size: 14px;">Hiệu Ứng (SFX)</strong>
                <div style="font-size: 11px; color: #90A4AE;">Ting-ting bán hàng, nâng cấp, còi đô thị</div>
              </div>
            </div>
            <button id="btn-toggle-sfx" class="btn-audio-pill ${isSfxOn ? 'active' : ''}">
              ${isSfxOn ? '✓ BẬT' : 'TẮT'}
            </button>
          </div>
          <div class="audio-slider-row">
            <span style="font-size: 14px;">🔈</span>
            <input type="range" id="slider-sfx-volume" min="0" max="100" value="${sfxVolPct}" ${!isSfxOn ? 'disabled' : ''} />
            <span class="audio-vol-pct" id="val-sfx-pct">${sfxVolPct}%</span>
          </div>
        </div>

        <div class="audio-quick-actions">
          <button id="btn-audio-mute-all" class="btn btn-secondary btn-sm" style="flex: 1; padding: 8px;">🔇 Tắt Hết</button>
          <button id="btn-audio-unmute-all" class="btn btn-primary btn-sm" style="flex: 1; padding: 8px;">🔊 Bật Cả Hai</button>
        </div>
      </div>
    `;

    const updateHudIcon = () => {
      const audioBtn = this.hudElement.querySelector('#btn-toggle-audio');
      if (audioBtn) audioBtn.textContent = audio.getHudIcon();
    };

    // Toggle BGM
    const bgmBtn = document.getElementById('btn-toggle-bgm');
    const bgmSlider = document.getElementById('slider-bgm-volume') as HTMLInputElement | null;
    bgmBtn?.addEventListener('click', () => {
      const on = audio.toggleBgm();
      if (bgmBtn) {
        bgmBtn.textContent = on ? '✓ BẬT' : 'TẮT';
        bgmBtn.classList.toggle('active', on);
      }
      if (bgmSlider) bgmSlider.disabled = !on;
      updateHudIcon();
    });

    // Slider BGM
    bgmSlider?.addEventListener('input', () => {
      const val = parseInt(bgmSlider.value, 10);
      audio.setBgmVolume(val / 100);
      const pctEl = document.getElementById('val-bgm-pct');
      if (pctEl) pctEl.textContent = `${val}%`;
    });

    // Toggle SFX
    const sfxBtn = document.getElementById('btn-toggle-sfx');
    const sfxSlider = document.getElementById('slider-sfx-volume') as HTMLInputElement | null;
    sfxBtn?.addEventListener('click', () => {
      const on = audio.toggleSfx();
      if (sfxBtn) {
        sfxBtn.textContent = on ? '✓ BẬT' : 'TẮT';
        sfxBtn.classList.toggle('active', on);
      }
      if (sfxSlider) sfxSlider.disabled = !on;
      updateHudIcon();
    });

    // Slider SFX
    sfxSlider?.addEventListener('input', () => {
      const val = parseInt(sfxSlider.value, 10);
      audio.setSfxVolume(val / 100);
      const pctEl = document.getElementById('val-sfx-pct');
      if (pctEl) pctEl.textContent = `${val}%`;
    });
    sfxSlider?.addEventListener('change', () => {
      audio.playSale();
    });

    // Mute All
    document.getElementById('btn-audio-mute-all')?.addEventListener('click', () => {
      audio.setBgmEnabled(false);
      audio.setSfxEnabled(false);
      this.openAudioModal();
      updateHudIcon();
    });

    // Unmute All
    document.getElementById('btn-audio-unmute-all')?.addEventListener('click', () => {
      audio.setBgmEnabled(true);
      audio.setSfxEnabled(true);
      this.openAudioModal();
      updateHudIcon();
      audio.playTap();
    });
  }

  public isAnyModalOpen(): boolean {
    return (
      this.isPauseMenuOpen ||
      this.isMenuOpen ||
      this.isSalesOpen ||
      this.isUpgradeOpen ||
      this.isTopMenuOpen ||
      this.pauseModalElement.style.display === 'flex' ||
      this.installGuideModalElement.style.display === 'flex' ||
      this.audioModalElement.style.display === 'flex' ||
      this.accountModalElement.style.display === 'flex' ||
      this.howToPlayModalElement.style.display === 'flex' ||
      this.trendsModalElement.style.display === 'flex' ||
      this.celebrationModalElement.style.display === 'flex' ||
      this.dayReportElement.style.display === 'flex' ||
      this.confirmModalElement.style.display === 'flex' ||
      this.locationModalElement.style.display === 'flex' ||
      this.lobbyModalElement.style.display === 'flex' ||
      this.renameModalElement.style.display === 'flex'
    );
  }

  public openPauseMenu(state?: Readonly<GameState>): void {
    const s = state ?? this.lastState;
    this.isPauseMenuOpen = true;
    if (s && s.phase === 'PLAYING') {
      this.dispatcher.dispatch({ type: 'PAUSE_GAME' });
    }
    AudioService.getInstance().pauseMusic();

    const dayNumber = s?.dayNumber ?? 1;

    this.pauseModalElement.innerHTML = `
      <div class="dialog-card pause-card">
        <div class="pause-header">
          <div class="pause-squircle">
            <span class="pause-icon">⏸️</span>
          </div>
          <h3 class="pause-title">TRÒ CHƠI TẠM DỪNG</h3>
          <p class="pause-subtitle">Quán nước tạm nghỉ tay • Thời gian mô phỏng đang dừng lại</p>
        </div>

        <div class="pause-actions-list">
          <button id="btn-resume-game" class="btn btn-primary pause-btn pause-btn-resume">
            <span class="btn-icon">▶️</span>
            <div class="btn-text">
              <strong>Tiếp Tục Bán Hàng</strong>
              <small>Quay lại quầy nước và phục vụ khách</small>
            </div>
          </button>

          <button id="btn-pause-restart-day" class="btn btn-secondary pause-btn">
            <span class="btn-icon">🔄</span>
            <div class="btn-text">
              <strong>Bắt Đầu Lại Ngày Hôm Nay</strong>
              <small>Làm lại ca sáng Ngày ${dayNumber} (khôi phục vốn ngày)</small>
            </div>
          </button>

          <button id="btn-pause-lobby" class="btn btn-secondary pause-btn">
            <span class="btn-icon">🏪</span>
            <div class="btn-text">
              <strong>Về Màn Hình Quán (Lobby)</strong>
              <small>Xem tổng quan bảng tin, thời tiết & quản lý quán</small>
            </div>
          </button>

          <button id="btn-pause-sync" class="btn btn-secondary pause-btn">
            <span class="btn-icon">☁️</span>
            <div class="btn-text">
              <strong>Đồng Bộ & Khôi Phục Tài Khoản</strong>
              <small>Sao lưu đám mây hoặc chuyển tài khoản sang máy khác</small>
            </div>
          </button>

          <button id="btn-pause-install" class="btn btn-secondary pause-btn">
            <span class="btn-icon">📲</span>
            <div class="btn-text">
              <strong>Đưa Ra Màn Hình Chính (PWA)</strong>
              <small>Cài đặt ứng dụng để mở nhanh không cần gõ web</small>
            </div>
          </button>

          <button id="btn-pause-reset" class="btn btn-secondary pause-btn pause-btn-danger">
            <span class="btn-icon">🗑️</span>
            <div class="btn-text">
              <strong>Chơi Lại Từ Đầu</strong>
              <small>Xóa dữ liệu ván chơi này và lập nghiệp lại</small>
            </div>
          </button>
        </div>
      </div>
    `;

    this.pauseModalElement.style.display = 'flex';
  }

  public closePauseMenu(): void {
    this.isPauseMenuOpen = false;
    this.pauseModalElement.style.display = 'none';
    this.dispatcher.dispatch({ type: 'RESUME_GAME' });
    AudioService.getInstance().resumeMusic();
  }

  public async triggerAddToHomeScreen(): Promise<void> {
    if (this.deferredInstallPrompt) {
      try {
        await this.deferredInstallPrompt.prompt();
        const choiceResult = await this.deferredInstallPrompt.userChoice;
        if (choiceResult.outcome === 'accepted') {
          this.deferredInstallPrompt = null;
          return;
        }
      } catch (err) {
        console.warn('[PWA] Prompt error, fallback to guide:', err);
      }
    }
    this.openInstallGuideModal();
  }

  public openInstallGuideModal(): void {
    const isIOS =
      typeof navigator !== 'undefined' &&
      /iPad|iPhone|iPod/.test(navigator.userAgent) &&
      !(window as unknown as { MSStream?: unknown }).MSStream;
    const isAndroid = typeof navigator !== 'undefined' && /Android/.test(navigator.userAgent);

    let platformTitle = 'Máy Tính / Desktop';
    let stepsHtml = '';

    if (isIOS) {
      platformTitle = 'iPhone & iPad (Safari)';
      stepsHtml = `
        <div class="install-step-item">
          <div class="install-step-num">1</div>
          <div class="install-step-desc">
            Nhấn vào biểu tượng <strong>Chia sẻ (Share) 📤</strong> ở thanh công cụ Safari (phía dưới màn hình).
          </div>
        </div>
        <div class="install-step-item">
          <div class="install-step-num">2</div>
          <div class="install-step-desc">
            Cuộn xuống danh sách tính năng và chọn <strong>"Thêm vào MH chính" (Add to Home Screen) ➕</strong>.
          </div>
        </div>
        <div class="install-step-item">
          <div class="install-step-num">3</div>
          <div class="install-step-desc">
            Nhấn <strong>"Thêm" (Add)</strong> ở góc trên bên phải để tạo biểu tượng Bá Chủ Vỉa Hè ngoài màn hình!
          </div>
        </div>
      `;
    } else if (isAndroid) {
      platformTitle = 'Android (Chrome / Cốc Cốc)';
      stepsHtml = `
        <div class="install-step-item">
          <div class="install-step-num">1</div>
          <div class="install-step-desc">
            Nhấn vào biểu tượng <strong>Menu 3 chấm (⋮)</strong> ở góc trên bên phải trình duyệt.
          </div>
        </div>
        <div class="install-step-item">
          <div class="install-step-num">2</div>
          <div class="install-step-desc">
            Chọn <strong>"Cài đặt ứng dụng"</strong> hoặc <strong>"Thêm vào Màn hình chính" 📲</strong>.
          </div>
        </div>
        <div class="install-step-item">
          <div class="install-step-num">3</div>
          <div class="install-step-desc">
            Nhấn <strong>"Cài đặt" (Install)</strong> để xác nhận. Ứng dụng sẽ xuất hiện như app native!
          </div>
        </div>
      `;
    } else {
      stepsHtml = `
        <div class="install-step-item">
          <div class="install-step-num">1</div>
          <div class="install-step-desc">
            Nhấp vào biểu tượng <strong>Cài đặt (Install) 🖥️</strong> ở phía bên phải thanh địa chỉ trình duyệt.
          </div>
        </div>
        <div class="install-step-item">
          <div class="install-step-num">2</div>
          <div class="install-step-desc">
            Hoặc nhấp Menu 3 chấm <strong>⋮ ➔ "Lưu và chia sẻ" ➔ "Cài đặt Bá Chủ Vỉa Hè..."</strong>.
          </div>
        </div>
        <div class="install-step-item">
          <div class="install-step-num">3</div>
          <div class="install-step-desc">
            Bấm <strong>Cài đặt</strong> để chơi game toàn màn hình với hiệu năng tối đa!
          </div>
        </div>
      `;
    }

    this.installGuideModalElement.innerHTML = `
      <div class="dialog-card install-guide-card">
        <div class="dialog-header">
          <div style="display: flex; align-items: center; gap: 8px;">
            <span style="font-size: 24px;">📲</span>
            <div>
              <h3 style="margin: 0; font-size: 16px; color: #64B5F6;">CÀI ĐẶT ỨNG DỤNG (PWA)</h3>
              <small style="color: #90A4AE;">Hướng dẫn cho: <strong>${platformTitle}</strong></small>
            </div>
          </div>
          <button id="btn-close-install-guide" class="btn-close-modal">✕</button>
        </div>

        <p style="margin: 10px 0; font-size: 12px; color: #CFD8DC; line-height: 1.4;">
          Cài đặt <strong>Bá Chủ Vỉa Hè</strong> ra màn hình chính giúp bạn mở game tức thì chỉ bằng 1 chạm, toàn màn hình và mượt mà hơn!
        </p>

        <div class="install-steps-list">
          ${stepsHtml}
        </div>

        <button id="btn-got-it-install" class="btn btn-primary" style="width: 100%; margin-top: 10px; min-height: 42px;">
          ✓ Đã Hiểu Cách Làm
        </button>
      </div>
    `;

    this.installGuideModalElement.style.display = 'flex';
  }

  private renderSaleToast(state: Readonly<GameState>): void {
    if (!state.latestSale) {
      if (this.saleToastElement.style.display !== 'none' && !this.saleToastTimer) {
        this.saleToastElement.classList.remove('visible');
        this.saleToastElement.style.display = 'none';
      }
      return;
    }

    // Nếu giao dịch này đã được render rồi thì bỏ qua, KHÔNG vẽ lại để tránh chớp giật 4Hz mỗi tick!
    if (this.lastRenderedSaleTime === state.latestSale.time) {
      return;
    }

    this.lastRenderedSaleTime = state.latestSale.time;
    AudioService.getInstance().playSale();
    if (this.saleToastTimer) {
      window.clearTimeout(this.saleToastTimer);
      this.saleToastTimer = null;
    }

    const sale = state.latestSale;
    const takeawayTag = sale.isTakeaway
      ? `<span class="badge-takeaway" style="background:#FFB300;color:#000;font-size:9px;padding:1px 4px;border-radius:4px;font-weight:bold;margin-left:4px;">[Mang đi]</span>`
      : '';

    this.saleToastElement.innerHTML = `
      <span class="sale-icon">${sale.icon}</span>
      <div class="sale-details">
        <span class="sale-title">Vừa bán: <strong>${sale.itemName}</strong>${takeawayTag}</span>
        <small class="sale-meta">Bán ${sale.sellPrice.toLocaleString('vi-VN')}đ • Vốn ${sale.costPrice.toLocaleString('vi-VN')}đ</small>
      </div>
      <span class="sale-profit">+${sale.profit.toLocaleString('vi-VN')}đ</span>
    `;

    this.saleToastElement.style.display = 'flex';
    // Kích hoạt transition mượt mà
    requestAnimationFrame(() => {
      this.saleToastElement.classList.add('visible');
    });

    this.saleToastTimer = window.setTimeout(() => {
      this.saleToastElement.classList.remove('visible');
      this.saleToastTimer = window.setTimeout(() => {
        this.saleToastElement.style.display = 'none';
        this.saleToastTimer = null;
      }, 300);
    }, 2200);
  }

  private renderEventBanner(state: Readonly<GameState>): void {
    if (!state.activeEvent) {
      if (this.renderedEventId !== null) {
        this.eventBannerElement.style.display = 'none';
        this.eventBannerElement.innerHTML = '';
        this.renderedEventId = null;
      }
      return;
    }

    const remainingSec = Math.max(
      0,
      Math.round((state.activeEvent.expiresAt - Date.now()) / 1000)
    );

    // Nếu event vẫn là event cũ, CHỈ CẬP NHẬT TIMER, KHÔNG VẼ LẠI DOM (tránh nuốt mất click/touch của người chơi!)
    if (this.renderedEventId === state.activeEvent.id) {
      const timerEl = this.eventBannerElement.querySelector('.event-timer');
      if (timerEl) {
        timerEl.textContent = `${remainingSec}s`;
      }
      return;
    }

    AudioService.getInstance().playAlert();

    const eventTemplate = GAME_EVENTS.find((e) => e.type === state.activeEvent?.type);
    if (!eventTemplate) return;

    const getEventMeta = (type: import('../domain/types.ts').EventType): { title: string; icon: string; desc: string } => {
      switch (type) {
        case 'raid':
          return {
            icon: '🚨',
            title: 'TRẬT TỰ ĐÔ THỊ ĐI KIỂM TRA',
            desc: 'Đội quản lý trật tự đô thị đang tuần tra xử lý vỉa hè!',
          };
        case 'unpaid_bill':
          return {
            icon: '🏃',
            title: 'KHÁCH ĂN QUỴT LẺN ĐI MẤT',
            desc: 'Một vị khách vừa đứng dậy lẻn đi mất không trả tiền nước!',
          };
        case 'rival_sabotage':
          return {
            icon: '🤼',
            title: 'ĐỐI THỦ CHƠI XẤU PHÁ HOẠI',
            desc: 'Quán nước đối diện tung tin đồn và rải rác trước cửa quán của bạn!',
          };
        case 'street_brawl':
          return {
            icon: '💥',
            title: 'ẨU ĐẢ VA QUẸT NGOÀI ĐƯỜNG',
            desc: 'Xô xát bất ngờ xảy ra ngay trước quán làm đổ vỡ bàn ghế!',
          };
        case 'extreme_weather':
          return {
            icon: '🌊',
            title: 'THỜI TIẾT CỰC ĐOAN',
            desc: 'Triều cường ngập lụt / nắng nóng 41°C vỉa hè nóng như thiêu đốt!',
          };
        case 'viral_tiktok':
          return {
            icon: '📹',
            title: 'TIKTOKER REVIEWER GHÉ QUÁN',
            desc: 'Một idol TikTok ẩm thực bất ngờ ghé quán cóc quay video review!',
          };
        case 'price_war':
          return {
            icon: '⚔️',
            title: 'ĐẠI CHIẾN GIÁ CẢ VỈA HÈ',
            desc: 'Đối thủ cạnh tranh xả giá phá đáy để kéo khách khỏi quán của bạn!',
          };
        case 'rain':
        default:
          return {
            icon: '🌧️',
            title: 'MƯA RÀO BẤT CHỢT',
            desc: 'Cơn mưa rào bất chợt đổ xuống phố khiến vỉa hè ướt sũng!',
          };
      }
    };

    const meta = getEventMeta(eventTemplate.type);
    this.renderedEventId = state.activeEvent.id;
    this.eventBannerElement.style.display = 'flex';
    this.eventBannerElement.innerHTML = `
      <div class="event-card">
        <div class="event-title">
          <span>${meta.icon} <strong>${meta.title}</strong></span>
          <span class="event-timer">${remainingSec}s</span>
        </div>
        <p class="event-desc" style="font-size: 11.5px; margin: 4px 0 8px; color: #FFF8E1; opacity: 0.9; line-height: 1.4;">${meta.desc}</p>
        <div class="event-options">
          ${eventTemplate.options
            .map(
              (opt) => `
            <button class="btn btn-event-opt" data-opt="${opt.id}">${opt.label}</button>
          `
            )
            .join('')}
        </div>
      </div>
    `;
  }

  public showMapLoadError(
    label: string,
    onAction: (action: 'retry' | 'repick' | 'procedural') => Promise<void>
  ): void {
    this.mapErrorModalElement.style.display = 'flex';
    this.mapErrorModalElement.innerHTML = `
      <div class="dialog-card map-error-card" style="border: 2px solid #EF5350; z-index: 1000000;">
        <div class="dialog-header">
          <h3 style="color: #FF8A80;">⚠️ Không Thể Tải Bản Đồ Thực Tế</h3>
        </div>
        <p class="dialog-desc" style="color: rgba(255,255,255,0.9); font-size: 13px; line-height: 1.5; margin: 12px 0;">
          Hệ thống đã thử kết nối qua Cloudflare Edge và các trạm vệ tinh OpenStreetMap nhưng chưa nhận được dữ liệu đường phố cho <strong>"${label.split(',')[0]}"</strong> (do nghẽn mạng quốc tế hoặc khu vực chưa có dữ liệu vệ tinh).
        </p>
        <div class="lobby-actions-col" style="margin-top: 16px; display: flex; flex-direction: column; gap: 8px;">
          <button id="btn-map-retry" class="btn btn-primary" style="min-height: 48px; font-weight: 700;">🔄 Thử Kết Nối Lại</button>
          <button id="btn-map-repick" class="btn btn-secondary" style="min-height: 46px;">📍 Chọn Địa Điểm Khác</button>
          <button id="btn-map-procedural" class="btn btn-secondary" style="min-height: 42px; opacity: 0.75; font-size: 12px;">🎲 Tạm Thời Dùng Bản Đồ Mẫu</button>
        </div>
      </div>
    `;

    const closeAndRun = (action: 'retry' | 'repick' | 'procedural') => {
      this.mapErrorModalElement.style.display = 'none';
      onAction(action).catch(console.error);
    };

    const retryBtn = document.getElementById('btn-map-retry');
    const repickBtn = document.getElementById('btn-map-repick');
    const procBtn = document.getElementById('btn-map-procedural');

    retryBtn?.addEventListener('click', () => closeAndRun('retry'));
    retryBtn?.addEventListener('touchend', () => closeAndRun('retry'));

    repickBtn?.addEventListener('click', () => closeAndRun('repick'));
    repickBtn?.addEventListener('touchend', () => closeAndRun('repick'));

    procBtn?.addEventListener('click', () => closeAndRun('procedural'));
    procBtn?.addEventListener('touchend', () => closeAndRun('procedural'));
  }

  private renderActionSheet(state: Readonly<GameState>): void {
    const isInspectingRival = Boolean(state.inspectRivalId);
    const isInspectingNode = Boolean(state.inspectNodeId);

    if (!isInspectingRival && !isInspectingNode) {
      if (this.lastActionSheetKey !== 'empty') {
        this.actionSheetElement.innerHTML = `
          <div class="action-bar-empty">
            <span>👆 Chạm vào điểm ($ / ★) để mở quán & kinh doanh</span>
          </div>
        `;
        this.lastActionSheetKey = 'empty';
      }
      return;
    }

    // 1. Kiểm tra inspect đối thủ (Ghost rival)
    if (isInspectingRival) {
      const ghost = state.ghostStalls.find((g) => g.sid === state.inspectRivalId);
      if (ghost) {
        const rivalKey = `rival_${ghost.sid}_${ghost.level}_${ghost.chairs}`;
        if (this.lastActionSheetKey === rivalKey) return;
        this.lastActionSheetKey = rivalKey;

        this.actionSheetElement.innerHTML = `
          <div class="card-node card-rival">
            <div class="card-header">
              <h3>Đối thủ: ${ghost.name}</h3>
              <span class="badge" style="background:#4FC3F7;color:#000;">Online Ghost</span>
            </div>
            <div class="card-stats">
              <div>Cấp: <strong>${ghost.level}</strong></div>
              <div>Ghế: <strong>${ghost.chairs}</strong></div>
              <div>Giá: <strong>${ghost.price.toLocaleString()}đ</strong></div>
              <div>Uy tín: <strong>★${ghost.rep}</strong></div>
            </div>
            <div class="card-actions">
              <button id="btn-close-inspect" class="btn btn-secondary">Đóng</button>
            </div>
          </div>
        `;
      }
      return;
    }

    // 2. Kiểm tra inspect Business Node
    if (isInspectingNode && state.neighborhood) {
      const node = state.neighborhood.nodes.find((n: BusinessNode) => n.id === state.inspectNodeId);
      if (!node) return;

      const ownedStallId = state.player.ownedStalls.find(
        (sid) => state.stalls[sid]?.nodeId === node.id
      );
      const ownedStall = ownedStallId ? state.stalls[ownedStallId] : null;

      if (ownedStall) {
        const canAddChair = state.player.money >= GAME_CONFIG.economy.stoolCost;
        const trendingTopic = state.currentTrendingTopic || 'Chuyện phiếm vỉa hè';
        const ownedKey = `owned_${node.id}_${ownedStall.id}_${ownedStall.level}_${ownedStall.chairs}_${canAddChair}_${trendingTopic}`;
        if (this.lastActionSheetKey === ownedKey) return;
        this.lastActionSheetKey = ownedKey;

        this.actionSheetElement.innerHTML = `
          <div class="card-node">
            <div class="card-header">
              <h3>Quán của bạn: ${ownedStall.name || state.player.stallName || 'Trà Cóc'} (Cấp ${ownedStall.level})</h3>
              <span class="badge">Đã sở hữu</span>
            </div>
            <div class="card-stats">
              <div>Ghế ngồi: <strong>${ownedStall.chairs} ghế</strong></div>
              <div>Giá bán: <strong>${ownedStall.price.toLocaleString()}đ</strong></div>
              <div>Vị trí: <strong>${formatZoneTag(node.tags[0])}</strong></div>
              <div class="stall-trends-tooltip" style="grid-column: 1 / -1; margin-top: 4px; padding: 4px 8px; background: rgba(255, 213, 79, 0.12); border-left: 3px solid #FFD54F; border-radius: 4px; font-size: 11px; color: #FFF8E1;" title="Tin nóng khách đang bàn tán">
                🔥 <strong>Chủ đề hóng hớt:</strong> ${trendingTopic}
              </div>
            </div>
            <div class="card-actions">
              <button id="btn-upgrade-chair" class="btn btn-primary" data-stall-id="${ownedStall.id}" ${canAddChair ? '' : 'disabled'}>
                ${canAddChair ? 'Thêm 2 Ghế Nhựa (50.000đ)' : 'Thiếu Tiền Thêm Ghế'}
              </button>
              <button id="btn-close-inspect" class="btn btn-secondary">Đóng</button>
            </div>
          </div>
        `;
      } else {
        const rival = state.dummyRivals ? state.dummyRivals[node.id] : undefined;

        if (rival) {
          const hasOldStall = state.player.ownedStalls.length > 0;
          let refundEstimate = 0;
          if (hasOldStall) {
            const oldStallId = state.player.ownedStalls[0]!;
            const oldStall = state.stalls[oldStallId];
            const oldNode = state.neighborhood.nodes.find((n) => n.id === oldStall?.nodeId);
            refundEstimate = Math.round((oldNode ? oldNode.unlockCost * 0.5 : 50_000) + Math.max(0, ((oldStall?.chairs ?? 4) - 4) * 25_000));
          }

          const relocationFee = hasOldStall ? 250_000 : 0;
          const transferFee = rival.transferFee;
          const deposit = node.unlockCost;
          const grossCost = transferFee + relocationFee + deposit;
          const totalAvailable = state.player.money + refundEstimate;
          const canAfford = totalAvailable >= grossCost;
          const actualDeduct = Math.max(0, grossCost - refundEstimate);

          const rivalKey = `rival_node_${node.id}_${rival.id}_${rival.status}_${canAfford}_${actualDeduct}`;
          if (this.lastActionSheetKey === rivalKey) return;
          this.lastActionSheetKey = rivalKey;

          let statusBadge = '';
          let statusText = '';
          if (rival.status === 'booming') {
            statusBadge = '<span class="badge" style="background:#FF9800;color:#000;">🔥 Ăn Nên Làm Ra</span>';
            statusText = `Quán đông nghịt, làm ăn phát đạt. Phí sang quán = <strong>3 tháng lợi nhuận</strong> (${rival.transferFee.toLocaleString()}đ).`;
          } else if (rival.status === 'stable') {
            statusBadge = '<span class="badge" style="background:#4CAF50;color:#FFF;">⚖️ Buôn Bán Ổn Định</span>';
            statusText = `Khách đều đặn, có lãi hàng tháng. Phí sang quán = <strong>3 tháng lợi nhuận</strong> (${rival.transferFee.toLocaleString()}đ).`;
          } else if (rival.status === 'struggling') {
            statusBadge = '<span class="badge" style="background:#78909C;color:#FFF;">📉 Buôn Bán Ế Ẩm</span>';
            statusText = `Khách vắng, chủ quán cần sang gấp thu hồi vốn bàn ghế đồ đạc (${rival.transferFee.toLocaleString()}đ).`;
          } else if (rival.status === 'bankrupt') {
            statusBadge = '<span class="badge" style="background:#E53935;color:#FFF;">💀 Vỡ Nợ Bán Ế</span>';
            statusText = `<strong style="color: #66BB6A;">SANG LẠI 0 ĐỒNG!</strong> Chủ cũ vỡ nợ bỏ chạy, bạn chỉ cần trả tiền xe ba gác dọn đồ và tiền cọc mặt bằng!`;
          }

          this.actionSheetElement.innerHTML = `
            <div class="card-node card-rival-occupied">
              <div class="card-header">
                <h3>Đối thủ: ${rival.name} (Cấp ${rival.level})</h3>
                ${statusBadge}
              </div>
              <div class="card-stats">
                <div>Doanh thu ước tính: <strong>~${rival.dailyRevenue.toLocaleString()}đ/ngày</strong></div>
                <div>Lợi nhuận ròng: <strong>~${rival.monthlyProfit.toLocaleString()}đ/tháng</strong></div>
                <div>Lưu lượng góc đường: <strong>${Math.round(node.trafficBase * 100)}%</strong></div>
                <div>Món đặc sản: <strong>${rival.specialty}</strong></div>
              </div>
              <div class="transfer-intel" style="font-size: 11px; line-height: 1.4; color: #FFF8E1; background: rgba(0,0,0,0.3); padding: 8px 10px; border-radius: 8px; margin: 8px 0; border-left: 3px solid #FFD54F;">
                <div>ℹ️ <strong>Tình trạng:</strong> ${statusText}</div>
                <div style="margin-top: 4px; display: flex; justify-content: space-between; border-top: 1px dashed rgba(255,255,255,0.15); padding-top: 4px;">
                  <span>Tiền sang lại quán:</span>
                  <strong>${rival.transferFee === 0 ? '<span style="color:#66BB6A;">0₫ (Miễn phí)</span>' : `${rival.transferFee.toLocaleString()}đ`}</strong>
                </div>
                <div style="display: flex; justify-content: space-between;">
                  <span>Phí chuyển dọn xe ba gác:</span>
                  <span>+${relocationFee.toLocaleString()}đ</span>
                </div>
                <div style="display: flex; justify-content: space-between;">
                  <span>Tiền cọc mặt bằng:</span>
                  <span>+${deposit.toLocaleString()}đ</span>
                </div>
                ${
                  hasOldStall
                    ? `<div style="display: flex; justify-content: space-between; color: #81C784;">
                        <span>Thu hồi thanh lý quán cũ:</span>
                        <span>-${refundEstimate.toLocaleString()}đ</span>
                       </div>`
                    : ''
                }
                <div style="margin-top: 4px; display: flex; justify-content: space-between; font-weight: bold; border-top: 1px solid rgba(255,255,255,0.2); padding-top: 4px; color: ${canAfford ? '#81C784' : '#EF5350'};">
                  <span>Tổng chi phí cần bù:</span>
                  <span>${actualDeduct.toLocaleString()}đ</span>
                </div>
              </div>
              <div class="card-actions">
                <button id="btn-buy-node" class="btn btn-primary" ${canAfford ? '' : 'disabled'}>
                  ${canAfford ? (rival.status === 'bankrupt' ? '🤝 Nhận Sang Lại 0đ & Dọn Tới' : '🤝 Sang Lại Mặt Bằng & Chuyển Quán') : 'Thiếu Vốn Sang Quán'}
                </button>
                <button id="btn-close-inspect" class="btn btn-secondary">Đóng</button>
              </div>
            </div>
          `;
          return;
        }

        const hasOldStall = state.player.ownedStalls.length > 0;
        let refundEstimate = 0;

        if (hasOldStall) {
          const oldStallId = state.player.ownedStalls[0]!;
          const oldStall = state.stalls[oldStallId];
          const oldNode = state.neighborhood.nodes.find((n) => n.id === oldStall?.nodeId);
          refundEstimate = Math.round((oldNode ? oldNode.unlockCost * 0.5 : 50_000) + Math.max(0, ((oldStall?.chairs ?? 4) - 4) * 25_000));
        }

        const relocationFee = hasOldStall ? 250_000 : 0;
        const deposit = node.unlockCost;
        const grossCost = relocationFee + deposit;
        const totalAvailable = state.player.money + refundEstimate;
        const canAfford = totalAvailable >= grossCost;
        const actualDeduct = Math.max(0, grossCost - refundEstimate);
        const nearbyRealStalls = state.neighborhood.pois.filter(
          (p) => p.type === 'real_stall' && Math.hypot(p.position.x - node.x, p.position.y - node.y) < 150
        ).length;

        const vacantKey = `vacant_${node.id}_${canAfford}_${hasOldStall}_${refundEstimate}_${actualDeduct}`;
        if (this.lastActionSheetKey === vacantKey) return;
        this.lastActionSheetKey = vacantKey;

        this.actionSheetElement.innerHTML = `
          <div class="card-node">
            <div class="card-header">
              <h3>${hasOldStall ? 'Chuyển địa bàn kinh doanh' : 'Mở quán cóc đầu tiên'}</h3>
              <span class="badge badge-vacant">Còn trống ($)</span>
            </div>
            <div class="card-stats">
              <div>Lưu lượng: <strong>${Math.round(node.trafficBase * 100)}%</strong></div>
              <div>Rủi ro trật tự: <strong>${Math.round(node.risk * 100)}%</strong></div>
              <div>Tiền cọc mở quầy: <strong>${node.unlockCost.toLocaleString()}đ</strong></div>
              <div>Tiệm thực tế quanh đây: <strong>${nearbyRealStalls} quán</strong> (${nearbyRealStalls > 0 ? `+${nearbyRealStalls * 5}% khách` : 'yên tĩnh'})</div>
            </div>
            ${
              hasOldStall
                ? `<div style="font-size: 11px; color: #FFD54F; background: rgba(0,0,0,0.3); padding: 6px; border-radius: 6px;">
                    ⚠️ Bạn chỉ được mở 1 quán. Mở tại đây sẽ thanh lý quán cũ (+${refundEstimate.toLocaleString()}đ) và phí xe ba gác (250.000đ). Chi phí bù thêm: <strong>${actualDeduct.toLocaleString()}đ</strong>.
                   </div>`
                : ''
            }
            <div class="card-actions">
              <button id="btn-buy-node" class="btn btn-primary" ${canAfford ? '' : 'disabled'}>
                ${canAfford ? (hasOldStall ? 'Chuyển Quán Tới Đây' : 'Mở Quán Ngay') : 'Không Đủ Tiền'}
              </button>
              <button id="btn-close-inspect" class="btn btn-secondary">Đóng</button>
            </div>
          </div>
        `;
      }
    }
  }

  private renderCelebrationModal(state: Readonly<GameState>): void {
    const celebration = state.pendingCelebration;
    if (!celebration) {
      this.celebrationModalElement.style.display = 'none';
      this.lastCelebrationId = null;
      return;
    }

    const celebrationId = `${celebration.type}_${celebration.levelOrDay}_${celebration.items.map((i) => i.id).join(',')}`;
    if (this.lastCelebrationId === celebrationId && this.celebrationModalElement.style.display === 'flex') {
      return; // Đã render rồi, KHÔNG chạm vào innerHTML để tránh giật/chớp!
    }
    this.lastCelebrationId = celebrationId;
    AudioService.getInstance().playCelebration();

    const isLevel = celebration.type === 'level';
    const tagEmoji = isLevel ? '🌟' : '🎖️';
    const tagClass = isLevel ? 'badge-level' : 'badge-survival';
    const tagText = isLevel ? `CẤP ĐỘ ${celebration.levelOrDay}` : `NGÀY ${celebration.levelOrDay}`;

    const itemsHtml = celebration.items
      .map(
        (item) => `
      <div class="celebration-item-card">
        <div class="celebration-item-icon">${item.icon}</div>
        <div class="celebration-item-info">
          <div class="celebration-item-name">${item.name}</div>
          <div class="celebration-item-desc">${item.desc}</div>
        </div>
        ${
          item.type === 'menu'
            ? (state.player.activeMenu?.includes(item.id)
                ? `<button class="btn-enable-unlocked" disabled style="background-color: rgba(46, 125, 50, 0.4); border-color: #81C784; color: #C8E6C9;" data-item-id="${item.id}">✓ Đang Bán</button>`
                : `<button class="btn-enable-unlocked" data-item-id="${item.id}">+ Bán Ngay</button>`)
            : `<span class="badge-perk-active">Đã Kích Hoạt</span>`
        }
      </div>
    `
      )
      .join('');

    this.celebrationModalElement.innerHTML = `
      <div class="celebration-card animate-pop">
        <div class="celebration-fireworks">✨ 🎆 ✨</div>
        <div class="celebration-badge ${tagClass}">${tagEmoji} ${tagText}</div>
        <h2 class="celebration-title">${celebration.title}</h2>
        <p class="celebration-subtitle">${celebration.subtitle}</p>

        <div class="celebration-items-container">
          <div class="celebration-items-header">🎁 PHẦN THƯỞNG MỚI MỞ KHÓA:</div>
          <div class="celebration-items-list">
            ${itemsHtml}
          </div>
        </div>

        <div class="celebration-actions">
          <button id="btn-dismiss-celebration" class="btn-celebration-continue">
            Tuyệt Vời, Tiếp Tục Bán! 🚀
          </button>
        </div>
      </div>
    `;

    this.celebrationModalElement.style.display = 'flex';
  }

  public openPerksModal(state: Readonly<GameState>): void {
    const unlockedIds = state.player.unlockedPerks ?? [];
    const perksHtml = SURVIVAL_PERKS.map((perk) => {
      const isUnlocked = unlockedIds.includes(perk.id) || state.dayNumber >= perk.day;
      return `
        <div class="perk-card ${isUnlocked ? 'perk-unlocked' : 'perk-locked'}">
          <div class="perk-card-icon">${perk.icon}</div>
          <div class="perk-card-body">
            <div class="perk-card-header">
              <span class="perk-name">${perk.name}</span>
              <span class="perk-status ${isUnlocked ? 'status-unlocked' : 'status-locked'}">
                ${isUnlocked ? '✅ Đã mở khóa' : `🔒 Ngày ${perk.day}`}
              </span>
            </div>
            <div class="perk-desc">${perk.desc}</div>
            <div class="perk-benefit">⚡ <strong>Hiệu quả:</strong> ${perk.benefit}</div>
          </div>
        </div>
      `;
    }).join('');

    this.perksModalElement.innerHTML = `
      <div class="dialog-card perks-modal-card">
        <div class="dialog-header">
          <h3>🎒 ĐỒ NGHỀ QUÁN CÓC</h3>
          <button id="btn-close-perks" class="btn-close-modal">✕</button>
        </div>
        <div class="perks-body">
          <p class="perks-hint">Trụ quán qua nhiều ngày giông bão để mở khóa thêm các đồ nghề và đặc quyền giúp bạn thống trị vỉa hè!</p>
          <div class="perks-grid">
            ${perksHtml}
          </div>
        </div>
        <div class="modal-footer" style="margin-top: 14px; text-align: center;">
          <button id="btn-confirm-perks" class="btn btn-primary" style="width: 100%; min-height: 42px;">Đóng</button>
        </div>
      </div>
    `;

    this.perksModalElement.style.display = 'flex';
  }

  public openHowToPlayModal(tab: 'guide' | 'tips' | 'progression' = 'guide'): void {
    this.currentHowToPlayTab = tab;
    this.renderHowToPlayContent();
    this.howToPlayModalElement.style.display = 'flex';
  }

  public switchHowToPlayTab(tab: 'guide' | 'tips' | 'progression'): void {
    this.currentHowToPlayTab = tab;
    this.renderHowToPlayContent();
  }

  private renderHowToPlayContent(): void {
    const state = this.lastState;
    const region = state?.neighborhood
      ? getRegionFromCoordinates(state.neighborhood.center.lat)
      : 'north';
    const levels = getStallLevels(region);

    let tabBodyHtml = '';

    if (this.currentHowToPlayTab === 'guide') {
      tabBodyHtml = `
        <div class="howtoplay-section">
          <div class="acc-list">
            <div class="acc-item open">
              <div class="acc-head">
                <span class="acc-badge">Bước 1</span>
                <span class="acc-title">📍 Chọn Vị Trí Vàng Mở Quán</span>
                <span class="acc-arrow">▼</span>
              </div>
              <div class="acc-body">
                <p>Bản đồ trong game được tải trực tiếp từ <strong>OpenStreetMap thực tế</strong> tại Hà Nội, Đà Nẵng, TP.HCM.</p>
                <ul>
                  <li>Chạm vào các <strong>chấm vàng ($)</strong> trên bản đồ để xem lưu lượng người qua lại và giá thuê mặt bằng.</li>
                  <li>Nên chọn góc ngã tư, gần trường học, bệnh viện, chợ hoặc phố đi bộ để có lượng khách đông đúc.</li>
                  <li>Góc càng đắc địa thì tiền thuê mặt bằng càng cao, hãy cân đối số vốn khởi nghiệp (500.000₫)!</li>
                </ul>
              </div>
            </div>

            <div class="acc-item">
              <div class="acc-head">
                <span class="acc-badge">Bước 2</span>
                <span class="acc-title">📋 Quản Lý Menu & Độ Tươi Sống</span>
                <span class="acc-arrow">▼</span>
              </div>
              <div class="acc-body">
                <p>Mỗi món ăn, thức uống có hạn sử dụng và biên lợi nhuận riêng biệt:</p>
                <ul>
                  <li><span class="badge-fresh-tag">⏱️ Pha sẵn / Tươi</span>: Nước mía, nước sâm, trà tắc ép tươi... Biên lợi nhuận cao, khách chuộng nhưng <strong>dễ thiu chua cuối ngày</strong> nếu ế khách hoặc gặp mưa dầm.</li>
                  <li><span class="badge-storable-tag">🧊 Cất giữ được</span>: Cà phê phin, sữa đặc, chanh tươi, trà mạn khô... Cất tủ lạnh/kho qua đêm <strong>0đ hao hụt</strong>!</li>
                  <li><span class="badge-pkg-tag">📦 Đóng gói</span>: Hạt hướng dương, kẹo lạc, nước ngọt lon... Hạn dùng dài, an toàn tuyệt đối khi quán vắng.</li>
                </ul>
                <div class="callout-tip">💡 <strong>Mẹo:</strong> Ngày mưa gió hoặc vắng khách, bấm <strong>📋 Thực Đơn</strong> tắt bớt món tươi để tránh bị trừ tiền hao hụt cuối ngày!</div>
              </div>
            </div>

            <div class="acc-item">
              <div class="acc-head">
                <span class="acc-badge">Bước 3</span>
                <span class="acc-title">👥 Thuê Phụ Quán & 🌙 Ca Đêm</span>
                <span class="acc-arrow">▼</span>
              </div>
              <div class="acc-body">
                <p>Tối ưu năng lực phục vụ để tăng doanh số:</p>
                <ul>
                  <li><strong>👥 Thuê Phụ Quán (120k/ngày):</strong> Khi quán bắt đầu đông khách, một mình chủ quán sẽ quá tải khiến khách đợi lâu rồi bỏ đi. Thuê phụ quán giúp phục vụ nhanh gấp đôi!</li>
                  <li><strong>🌙 Bán Ca Đêm:</strong> Bật nút trăng khuyết để mở ca đêm, doanh thu tăng thêm <strong>+30%</strong> cho các bạn trẻ dạo đêm chém gió. Tuy nhiên chỉ số trật tự đô thị sẽ tăng nhanh hơn!</li>
                </ul>
              </div>
            </div>

            <div class="acc-item">
              <div class="acc-head">
                <span class="acc-badge">Bước 4</span>
                <span class="acc-title">📅 Chốt Sổ Cuối Ngày & Khấu Hao</span>
                <span class="acc-arrow">▼</span>
              </div>
              <div class="acc-body">
                <p>Mỗi ngày kéo dài khoảng 5 phút chơi (hoặc bấm 📅 để kết thúc sớm):</p>
                <ul>
                  <li>Hệ thống xuất <strong>Phiếu Thu Chi (Receipt)</strong> tổng kết: Doanh thu bán hàng, Tiền vốn nguyên liệu, Tiền thuê mặt bằng, Tiền công phụ quán (nếu có).</li>
                  <li><strong>Khấu hao bàn ghế đồ nghề:</strong> Cố định 25.000₫/ngày chi phí hao mòn dù bạt, bàn ghế nhựa (chuẩn kế toán đường phố).</li>
                  <li>Theo dõi chỉ số Lời/Lỗ để điều chỉnh chiến lược kinh doanh cho ngày tiếp theo!</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      `;
    } else if (this.currentHowToPlayTab === 'tips') {
      tabBodyHtml = `
        <div class="howtoplay-section">
          <div class="acc-list">
            <div class="acc-item open">
              <div class="acc-head">
                <span class="acc-icon">🎯</span>
                <span class="acc-title">Bán Đúng Khẩu Vị 3 Miền</span>
                <span class="acc-arrow">▼</span>
              </div>
              <div class="acc-body">
                <p>Bá Chủ Vỉa Hè tự động nhận diện vùng miền theo tọa độ thực tế của bạn:</p>
                <ul>
                  <li><strong>Miền Bắc (Hà Nội, Phố Cổ):</strong> Món ruột là Trà Đá Vỉa Hè, Trà Sấu, Nhân Trần, Trà Chanh Chém Gió, Hướng Dương.</li>
                  <li><strong>Miền Trung (Đà Nẵng, Huế):</strong> Chuộng Trà Sâm Dứa, Nước Mía Siêu Sạch, Dừa Xiêm, Nước Ép Trái Cây.</li>
                  <li><strong>Miền Nam (Sài Gòn, Chợ Bến Thành):</strong> Cà Phê Sữa Đá, Bạc Xỉu, Trà Tắc Khổng Lồ, Trà Đào Cam Sả, Rau Má Đậu Xanh.</li>
                </ul>
              </div>
            </div>

            <div class="acc-item">
              <div class="acc-head">
                <span class="acc-icon">🌧️</span>
                <span class="acc-title">Đối Phó Thời Tiết & Giông Bão</span>
                <span class="acc-arrow">▼</span>
              </div>
              <div class="acc-body">
                <p>Thời tiết trong game thay đổi theo chu kỳ tự nhiên:</p>
                <ul>
                  <li><strong>Trời Nắng Ráo:</strong> Khách đông, chuộng nước mía, trà đá, nước ép lạnh.</li>
                  <li><strong>Trời Mưa Rào / Bão:</strong> Khách dạo phố giảm 50-70%. Cần vào menu tắt bớt đồ tươi ngay, chỉ giữ trà nóng và cà phê để tránh thiu hỏng lỗ vốn!</li>
                  <li>Đầu tư <strong>Ô Dù Bạt Che Mưa</strong> ở menu Nâng Cấp để che chắn giữ chân khách khi trời đổ mưa.</li>
                </ul>
              </div>
            </div>

            <div class="acc-item">
              <div class="acc-head">
                <span class="acc-icon">🔀</span>
                <span class="acc-title">Đổi Địa Bàn Khi Bị Ế Khách</span>
                <span class="acc-arrow">▼</span>
              </div>
              <div class="acc-body">
                <p>Nếu bạn thấy doanh thu lẹt đẹt do góc phố vắng người hoặc có quá nhiều quán đối thủ cạnh tranh chia sẻ khách:</p>
                <p>👉 Hãy mở menu <strong>⚙️ Quản lý ➔ Đổi Địa Bàn Mở Quán</strong> để dọn gánh sang ngã tư hoặc phố khác đông khách hơn!</p>
              </div>
            </div>

            <div class="acc-item">
              <div class="acc-head">
                <span class="acc-icon">🔥</span>
                <span class="acc-title">Bắt Sóng Google Trends Thời Sự</span>
                <span class="acc-arrow">▼</span>
              </div>
              <div class="acc-body">
                <p>Server tự động cập nhật xu hướng nóng nhất từ <strong>Google Trends Việt Nam</strong> mỗi 30 phút.</p>
                <p>Bà Bán Trà Đá sẽ liên tục "buôn chuyện" tin nóng trên thanh chữ chạy (🔥). Chạm vào thanh tin nóng để đọc chi tiết!</p>
              </div>
            </div>

            <div class="acc-item">
              <div class="acc-head">
                <span class="acc-icon">🛡️</span>
                <span class="acc-title">Giảm Hao Hụt Đồ Tươi</span>
                <span class="acc-arrow">▼</span>
              </div>
              <div class="acc-body">
                <p>Trụ quán qua <strong>Ngày 3</strong> để mở khóa đặc quyền <strong>"Thùng Xốp Ướp Đá"</strong> — giúp giữ lạnh nguyên liệu, giảm tới <strong>50%</strong> lượng hư hỏng của đồ tươi cuối ngày!</p>
              </div>
            </div>
          </div>
        </div>
      `;
    } else if (this.currentHowToPlayTab === 'progression') {
      const levelsHtml = levels.map((lvl) => {
        const isCurrent = state ? (state.stalls[0]?.level ?? 1) === lvl.level : lvl.level === 1;
        return `
          <div class="prog-level-card ${isCurrent ? 'current-level' : ''}">
            <div class="prog-level-badge">Cấp ${lvl.level}</div>
            <div class="prog-level-content">
              <div class="prog-level-title">
                <strong>${lvl.title}</strong>
                ${isCurrent ? '<span class="tag-current">Đang ở cấp này</span>' : ''}
              </div>
              <div class="prog-level-desc">${lvl.description}</div>
              ${
                lvl.unlockedItems.length > 0
                  ? `<div class="prog-unlocked-items">Mở khóa: ${lvl.unlockedItems.map((i) => `${i.icon} ${i.name}`).join(', ')}</div>`
                  : ''
              }
            </div>
          </div>
        `;
      }).join('');

      tabBodyHtml = `
        <div class="howtoplay-section">
          <div class="prog-header-note">
            Tích lũy số đơn phục vụ và uy tín đường phố để thăng cấp quán từ quầy cóc vỉa hè thành chuỗi kiosk bá chủ đường phố!
          </div>
          <div class="prog-levels-list">
            ${levelsHtml}
          </div>
        </div>
      `;
    }

    this.howToPlayModalElement.innerHTML = `
      <div class="dialog-card howtoplay-modal-card">
        <div class="dialog-header">
          <div class="howtoplay-title-wrapper">
            <span class="howtoplay-icon">📖</span>
            <div>
              <h3>SỔ TAY KHỞI NGHIỆP VỈA HÈ</h3>
              <p class="dialog-desc" style="margin: 0; font-size: 11px;">Cẩm nang kinh doanh đường phố từ Bà Trà Đá</p>
            </div>
          </div>
          <button id="btn-close-howtoplay" class="btn-close-modal">✕</button>
        </div>

        <div class="howtoplay-tabs">
          <button class="howtoplay-tab-btn ${this.currentHowToPlayTab === 'guide' ? 'active' : ''}" data-tab="guide">
            🧭 4 Bước Cơ Bản
          </button>
          <button class="howtoplay-tab-btn ${this.currentHowToPlayTab === 'tips' ? 'active' : ''}" data-tab="tips">
            💡 Mẹo Kiếm Lời
          </button>
          <button class="howtoplay-tab-btn ${this.currentHowToPlayTab === 'progression' ? 'active' : ''}" data-tab="progression">
            ⭐ Cấp Độ Quán
          </button>
        </div>

        <div class="howtoplay-body">
          ${tabBodyHtml}
        </div>

        <div class="modal-footer" style="margin-top: 14px; text-align: center;">
          <button id="btn-confirm-howtoplay" class="btn btn-primary" style="width: 100%; min-height: 42px;">
            Đã Hiểu, Chiến Thôi! 🚀
          </button>
        </div>
      </div>
    `;
  }

  private injectStyles(): void {
    const style = document.createElement('style');
    style.textContent = `
      :root {
        --safe-top: env(safe-area-inset-top, 0px);
        --safe-bottom: env(safe-area-inset-bottom, 0px);
        --safe-left: env(safe-area-inset-left, 0px);
        --safe-right: env(safe-area-inset-right, 0px);
      }

      .hud-container {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        padding: calc(var(--safe-top) + 6px) calc(var(--safe-right) + 8px) 6px calc(var(--safe-left) + 8px);
        display: flex;
        flex-direction: column;
        gap: 5px;
        pointer-events: none;
        z-index: 10;
      }
      .hud-top-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        pointer-events: auto;
        gap: 6px;
        flex-wrap: nowrap;
        width: 100%;
        height: 38px;
        position: relative;
        box-sizing: border-box;
      }
      .hud-home-btn {
        background: rgba(26, 26, 46, 0.9);
        border: 1px solid rgba(255, 255, 255, 0.2);
        border-radius: 18px;
        padding: 4px 8px;
        height: 36px;
        min-width: 36px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 16px;
        cursor: pointer;
        flex-shrink: 0;
        transition: transform 0.15s, background 0.15s;
        -webkit-tap-highlight-color: transparent;
      }
      .hud-home-btn:active {
        transform: scale(0.92);
        background: rgba(255, 255, 255, 0.2);
      }
      .top-level-indicator {
        background: rgba(26, 26, 46, 0.9);
        border: 1px solid rgba(255, 255, 255, 0.2);
        border-radius: 18px;
        padding: 4px 10px;
        height: 36px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex: 0 1 auto;
        min-width: 60px;
        color: #FFF;
        box-sizing: border-box;
      }
      .shop-level-badge {
        background: linear-gradient(135deg, #FFB300, #F57C00);
        color: #000;
        font-size: 11px;
        font-weight: 800;
        padding: 4px 8px;
        border-radius: 10px;
        white-space: nowrap;
        flex-shrink: 0;
        letter-spacing: 0.2px;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
      }
      .hud-top-right {
        display: flex;
        align-items: center;
        gap: 6px;
        flex-shrink: 0;
      }
      .btn-quick-menu {
        background: rgba(30, 40, 70, 0.9);
        border: 1px solid rgba(100, 181, 246, 0.4);
        color: #E3F2FD;
        width: 36px;
        height: 36px;
        border-radius: 18px;
        padding: 0;
        font-size: 15px;
        font-weight: 700;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        -webkit-tap-highlight-color: transparent;
        box-sizing: border-box;
      }
      .btn-quick-menu:active {
        transform: scale(0.92);
        background: rgba(40, 60, 100, 0.95);
      }

      .btn-pause-toggle {
        background: rgba(30, 40, 70, 0.9);
        border: 1px solid rgba(255, 213, 79, 0.4);
        color: #FFD54F;
        width: 36px;
        height: 36px;
        border-radius: 18px;
        padding: 0;
        font-size: 15px;
        font-weight: 700;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        -webkit-tap-highlight-color: transparent;
        box-sizing: border-box;
        transition: transform 0.15s ease, background 0.15s ease;
      }
      .btn-pause-toggle:active {
        transform: scale(0.92);
        background: rgba(40, 60, 100, 0.95);
      }

      .btn-popdown-trigger {
        background: rgba(26, 26, 46, 0.92);
        border: 1px solid rgba(255, 255, 255, 0.25);
        color: #FFF;
        height: 36px;
        border-radius: 18px;
        padding: 0 8px;
        font-size: 14px;
        font-weight: 700;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 2px;
        flex-shrink: 0;
        box-shadow: 0 4px 12px rgba(0,0,0,0.35);
        transition: background 0.15s ease, border-color 0.15s ease;
        -webkit-tap-highlight-color: transparent;
        box-sizing: border-box;
      }
      .btn-popdown-trigger.active {
        background: rgba(255, 215, 0, 0.18);
        border-color: #FFD54F;
        color: #FFE082;
      }
      .btn-popdown-trigger:active {
        transform: scale(0.92);
      }
      .arrow-indicator {
        font-size: 8px;
        opacity: 0.8;
        margin-left: 2px;
      }

      /* Pop-down dropdown floating menu */
      .popdown-backdrop {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 99;
        background: rgba(0, 0, 0, 0.4);
        backdrop-filter: blur(2px);
      }
      .hud-popdown-menu {
        position: absolute;
        top: calc(var(--safe-top) + 46px);
        right: calc(var(--safe-right) + 8px);
        width: 260px;
        max-width: calc(100vw - 20px);
        background: rgba(22, 22, 38, 0.96);
        backdrop-filter: blur(16px);
        border: 1.5px solid rgba(255, 255, 255, 0.18);
        border-radius: 18px;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.6), 0 0 15px rgba(255, 215, 0, 0.12);
        z-index: 100;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        animation: popDownIn 0.2s cubic-bezier(0.16, 1, 0.3, 1);
        pointer-events: auto;
      }
      @keyframes popDownIn {
        from {
          opacity: 0;
          transform: translateY(-8px) scale(0.96);
        }
        to {
          opacity: 1;
          transform: translateY(0) scale(1);
        }
      }
      .popdown-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 10px 14px 8px 14px;
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        font-size: 10.5px;
        font-weight: 700;
        letter-spacing: 0.5px;
        color: #FFE082;
      }
      .btn-popdown-close {
        background: transparent;
        border: none;
        color: rgba(255, 255, 255, 0.6);
        font-size: 14px;
        cursor: pointer;
        padding: 0 4px;
        line-height: 1;
      }
      .popdown-list {
        display: flex;
        flex-direction: column;
        padding: 6px;
        gap: 3px;
      }
      .popdown-item {
        background: transparent;
        border: none;
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 9px 12px;
        border-radius: 12px;
        cursor: pointer;
        text-align: left;
        width: 100%;
        color: #FFF;
        transition: background 0.12s ease;
        -webkit-tap-highlight-color: transparent;
      }
      .popdown-item:active {
        background: rgba(255, 255, 255, 0.12);
        transform: scale(0.98);
      }
      .popdown-item .item-icon {
        font-size: 18px;
        width: 24px;
        text-align: center;
        flex-shrink: 0;
      }
      .popdown-item .item-text {
        display: flex;
        flex-direction: column;
        gap: 1px;
        flex: 1;
      }
      .popdown-item .item-text strong {
        font-size: 12.5px;
        color: #FFF;
      }
      .popdown-item .item-text small {
        font-size: 10px;
        color: rgba(255, 255, 255, 0.65);
      }
      .popdown-divider {
        height: 1px;
        background: rgba(255, 255, 255, 0.1);
        margin: 4px 6px;
      }
      .popdown-item.item-danger {
        border: 1px solid rgba(239, 83, 80, 0.25);
        background: rgba(239, 83, 80, 0.1);
      }
      .popdown-item.item-danger:active {
        background: rgba(239, 83, 80, 0.25);
      }

      .hud-vertical-dock {
        position: absolute;
        right: calc(var(--safe-right) + 10px);
        top: 110px;
        z-index: 12;
        display: flex;
        flex-direction: column;
        gap: 8px;
        pointer-events: auto;
      }

      .btn-overtime-toggle {
        background: rgba(30, 30, 45, 0.85);
        backdrop-filter: blur(8px);
        border: 1px solid rgba(255, 215, 0, 0.4);
        color: #FFE082;
        width: 36px;
        height: 36px;
        border-radius: 18px;
        padding: 0;
        font-size: 16px;
        cursor: pointer;
        display: inline-flex;
        justify-content: center;
        align-items: center;
        flex-shrink: 0;
        transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        -webkit-tap-highlight-color: transparent;
        box-shadow: 0 4px 12px rgba(0,0,0,0.4);
        box-sizing: border-box;
      }
      .btn-overtime-toggle:active {
        transform: scale(0.92);
      }
      .btn-overtime-toggle.active {
        background: linear-gradient(135deg, #FFB300, #F57F17);
        border-color: #FFF3E0;
        color: #000;
        box-shadow: 0 0 16px rgba(255, 179, 0, 0.8), 0 4px 12px rgba(0,0,0,0.5);
      }

      .btn-staff-toggle {
        background: rgba(255, 255, 255, 0.08);
        border: 1px solid rgba(66, 165, 245, 0.4);
        color: #90CAF9;
        width: 36px;
        height: 36px;
        border-radius: 18px;
        padding: 0;
        font-size: 15px;
        font-weight: 600;
        cursor: pointer;
        display: inline-flex;
        justify-content: center;
        align-items: center;
        flex-shrink: 0;
        transition: all 0.2s ease;
        -webkit-tap-highlight-color: transparent;
        box-sizing: border-box;
      }
      .btn-staff-toggle:active {
        transform: scale(0.92);
      }
      .btn-staff-toggle.active {
        background: #1976D2;
        border-color: #64B5F6;
        color: #FFF;
        font-weight: bold;
        box-shadow: 0 0 10px rgba(66, 165, 245, 0.7);
      }

      .hud-bar {
        flex: 1;
        background: rgba(26, 26, 46, 0.88);
        backdrop-filter: blur(8px);
        border: 1px solid rgba(255, 255, 255, 0.15);
        border-radius: 18px;
        padding: 0 4px;
        height: 36px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
        pointer-events: auto;
        min-width: 0;
      }
      .hud-item {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        flex: 1;
        min-width: 0;
      }
      .hud-label {
        font-size: 8.5px;
        text-transform: uppercase;
        letter-spacing: 0.3px;
        color: rgba(255, 255, 255, 0.6);
        white-space: nowrap;
      }
      .hud-val {
        font-size: 11.5px;
        font-weight: 700;
        color: #FFF;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 100%;
      }
      .hud-item.money .hud-val {
        color: #FFD54F;
      }

      .hud-area-tag {
        align-self: flex-start;
        background: rgba(26, 26, 46, 0.75);
        border: 1px solid rgba(255, 255, 255, 0.12);
        border-radius: 8px;
        padding: 3px 8px;
        font-size: 10.5px;
        color: rgba(255, 255, 255, 0.85);
        display: flex;
        align-items: center;
        gap: 6px;
        max-width: 100%;
        box-sizing: border-box;
      }
      .hud-area-label {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 68vw;
      }
      .tag-real-stalls {
        font-size: 9.5px;
        color: #FFE082;
        background: rgba(0,0,0,0.35);
        padding: 1px 5px;
        border-radius: 4px;
        border: 1px solid rgba(255,224,130,0.3);
        white-space: nowrap;
        flex-shrink: 0;
      }

      .hud-guide-banner {
        pointer-events: auto;
        background: rgba(255, 152, 0, 0.9);
        color: #000;
        padding: 6px 10px;
        border-radius: 8px;
        font-size: 11px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
      }
      .hud-lore {
        align-self: flex-start;
        max-width: 95%;
        background: rgba(26, 26, 46, 0.82);
        backdrop-filter: blur(6px);
        border-left: 3px solid #4FC3F7;
        padding: 4px 8px;
        font-size: 10.5px;
        color: #E1F5FE;
        font-style: italic;
        border-radius: 0 6px 6px 0;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        pointer-events: auto;
      }
      .btn-dismiss-lore {
        background: transparent;
        border: none;
        color: rgba(255,255,255,0.6);
        font-size: 12px;
        cursor: pointer;
        padding: 0 4px;
        line-height: 1;
      }
      .btn-dismiss-lore:active {
        color: #FFF;
      }

      .sale-toast-floating {
        position: absolute;
        bottom: calc(var(--safe-bottom) + 95px);
        left: 50%;
        transform: translate(-50%, 10px);
        opacity: 0;
        pointer-events: none;
        background: rgba(18, 48, 22, 0.93);
        backdrop-filter: blur(8px);
        border: 1px solid rgba(102, 187, 106, 0.7);
        border-radius: 24px;
        padding: 6px 16px;
        display: flex;
        align-items: center;
        gap: 10px;
        color: #FFF;
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.45), 0 0 12px rgba(102, 187, 106, 0.25);
        z-index: 25;
        transition: opacity 0.25s ease, transform 0.25s cubic-bezier(0.18, 0.89, 0.32, 1.28);
        max-width: 90vw;
        white-space: nowrap;
      }
      .sale-toast-floating.visible {
        opacity: 1;
        transform: translate(-50%, 0);
      }
      .sale-icon { font-size: 18px; }
      .sale-details { display: flex; flex-direction: column; text-align: left; }
      .sale-title { font-size: 12px; font-weight: 600; color: #FFF; }
      .sale-meta { font-size: 10px; color: rgba(255, 255, 255, 0.75); }
      .sale-profit { font-weight: 800; font-size: 13px; color: #A5D6A7; margin-left: 4px; }

      .event-banner-container {
        position: absolute;
        top: calc(var(--safe-top) + 140px);
        left: calc(var(--safe-left) + 12px);
        right: calc(var(--safe-right) + 12px);
        z-index: 10;
        pointer-events: none;
        display: flex;
        justify-content: center;
      }
      .event-card {
        background: rgba(26, 26, 46, 0.95);
        border: 2px solid #FF7043;
        border-radius: 16px;
        padding: 12px;
        width: 100%;
        max-width: 450px;
        box-shadow: 0 6px 20px rgba(255, 112, 67, 0.3);
        pointer-events: auto;
      }
      .event-title {
        display: flex;
        justify-content: space-between;
        font-size: 12px;
        color: #FFAB91;
        margin-bottom: 8px;
      }
      .event-options {
        display: flex;
        flex-direction: column;
        gap: 6px;
      }

      .zoom-controls-container {
        position: absolute;
        right: calc(var(--safe-right) + 10px);
        bottom: calc(var(--safe-bottom) + 75px);
        display: flex;
        flex-direction: column;
        gap: 6px;
        z-index: 12;
      }
      .btn-zoom {
        width: 38px;
        height: 38px;
        border-radius: 19px;
        background: rgba(26, 26, 46, 0.88);
        backdrop-filter: blur(8px);
        border: 1px solid rgba(255, 255, 255, 0.22);
        color: #FFF;
        font-size: 16px;
        font-weight: 700;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 4px 12px rgba(0,0,0,0.4);
        -webkit-tap-highlight-color: transparent;
      }
      .btn-zoom:active {
        transform: scale(0.9);
      }

      .actionsheet-container {
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        padding: 6px calc(var(--safe-right) + 10px) calc(var(--safe-bottom) + 10px) calc(var(--safe-left) + 10px);
        z-index: 10;
        pointer-events: none;
      }
      .action-bar-empty {
        background: rgba(26, 26, 46, 0.82);
        backdrop-filter: blur(8px);
        border: 1px solid rgba(255, 255, 255, 0.15);
        border-radius: 18px;
        padding: 7px 14px;
        text-align: center;
        font-size: 11.5px;
        color: rgba(255, 255, 255, 0.85);
        pointer-events: auto;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .card-node {
        background: rgba(26, 26, 46, 0.95);
        backdrop-filter: blur(12px);
        border: 1px solid rgba(255, 255, 255, 0.2);
        border-radius: 20px;
        padding: 16px;
        display: flex;
        flex-direction: column;
        gap: 12px;
        box-shadow: 0 8px 30px rgba(0, 0, 0, 0.5);
        pointer-events: auto;
      }
      .card-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
      .card-header h3 {
        font-size: 15px;
        font-weight: 700;
        color: #FFF;
        margin: 0;
      }
      .badge {
        font-size: 11px;
        padding: 3px 8px;
        border-radius: 12px;
        background: #2E7D32;
        color: #FFF;
      }
      .badge-vacant {
        background: #EF6C00;
      }
      .card-stats {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 8px;
        font-size: 12px;
        color: rgba(255, 255, 255, 0.8);
      }
      .card-stats strong {
        color: #FFF;
      }
      .card-actions {
        display: flex;
        gap: 8px;
      }

      .btn {
        flex: 1;
        min-height: 48px;
        padding: 10px 16px;
        border-radius: 12px;
        border: none;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: transform 0.1s, opacity 0.1s;
        -webkit-tap-highlight-color: transparent;
      }
      .btn:active {
        transform: scale(0.97);
      }
      .btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
      .btn-primary {
        background: #2E7D32;
        color: #FFF;
      }
      .btn-secondary {
        background: rgba(255, 255, 255, 0.12);
        color: #FFF;
      }
      .btn-large {
        font-size: 16px;
        font-weight: 700;
        min-height: 52px;
      }

      .modal-backdrop {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.78);
        backdrop-filter: blur(8px);
        z-index: 99999;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 16px;
        touch-action: pan-y;
      }
      .dialog-card, .location-dialog, .lobby-card {
        background: #1a1a2e;
        border: 1px solid rgba(255, 255, 255, 0.2);
        border-radius: 20px;
        padding: 20px;
        width: 100%;
        max-width: 440px;
        max-height: 85vh;
        overflow-y: auto;
        color: #FFF;
        display: flex;
        flex-direction: column;
        gap: 12px;
        box-shadow: 0 12px 40px rgba(0,0,0,0.6);
        touch-action: pan-y;
      }
      .dialog-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
      .btn-close-modal {
        background: transparent;
        border: none;
        color: rgba(255,255,255,0.7);
        font-size: 18px;
        cursor: pointer;
        padding: 4px 8px;
      }
      .dialog-desc {
        font-size: 12px;
        color: rgba(255, 255, 255, 0.65);
        line-height: 1.4;
      }

      .lobby-header {
        text-align: center;
      }
      .lobby-icon { font-size: 36px; }
      .lobby-tagline { font-size: 12px; color: #FFE082; margin-top: 4px; }
      .lobby-form { display: flex; flex-direction: column; gap: 12px; }
      .form-group { display: flex; flex-direction: column; gap: 6px; }
      .form-group label { font-size: 12px; color: rgba(255,255,255,0.85); font-weight: 600; }
      .form-group input, .search-box input {
        padding: 12px;
        border-radius: 10px;
        border: 1px solid rgba(255, 255, 255, 0.2);
        background: rgba(255, 255, 255, 0.08);
        color: #FFF;
        font-size: 14px;
        touch-action: manipulation !important;
        user-select: text !important;
        -webkit-user-select: text !important;
        outline: none;
      }
      .form-group input:focus, .search-box input:focus {
        border-color: #4FC3F7;
        background: rgba(255, 255, 255, 0.14);
      }
      .chips-label { font-size: 11px; color: rgba(255,255,255,0.6); }
      .preset-chips { display: flex; flex-wrap: wrap; gap: 6px; }
      .chip-btn {
        background: rgba(255,255,255,0.1);
        border: 1px solid rgba(255,255,255,0.2);
        color: #FFF;
        font-size: 11px;
        padding: 6px 10px;
        border-radius: 14px;
        cursor: pointer;
      }
      .chip-btn:active { background: rgba(255, 215, 0, 0.3); }
      .lobby-capital {
        display: flex;
        justify-content: space-between;
        background: rgba(255,255,255,0.06);
        padding: 10px 14px;
        border-radius: 10px;
        font-size: 13px;
      }
      .lobby-capital strong { color: #FFD54F; font-size: 15px; }

      .menu-list, .sales-list, .results-list {
        display: flex;
        flex-direction: column;
        gap: 8px;
        max-height: 50vh;
        overflow-y: auto;
      }
      .menu-item-row {
        background: rgba(255,255,255,0.06);
        border: 1px solid rgba(255,255,255,0.1);
        border-radius: 12px;
        padding: 10px;
        display: flex;
        align-items: center;
        gap: 10px;
      }
      .menu-item-row.locked { opacity: 0.6; }
      .item-icon { font-size: 24px; }
      .item-info { flex: 1; display: flex; flex-direction: column; gap: 2px; }
      .item-prices { display: flex; gap: 8px; font-size: 11px; color: rgba(255,255,255,0.7); }
      .item-profit { color: #81C784; font-weight: 700; }
      .btn-toggle-menu {
        padding: 6px 12px;
        border-radius: 10px;
        font-size: 11px;
        font-weight: 700;
        border: none;
        cursor: pointer;
      }
      .btn-selling { background: #2E7D32; color: #FFF; }
      .btn-pause { background: rgba(255,255,255,0.15); color: #FFF; }
      .badge-lock { font-size: 10px; color: #FFA726; background: rgba(0,0,0,0.3); padding: 4px 6px; border-radius: 6px; }

      .sales-summary-bar {
        display: flex;
        justify-content: space-between;
        background: rgba(255,255,255,0.08);
        padding: 10px 12px;
        border-radius: 10px;
        font-size: 13px;
      }
      .sale-row {
        background: rgba(255,255,255,0.05);
        border-radius: 10px;
        padding: 8px 12px;
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 12px;
      }
      .sr-time { font-size: 10px; color: rgba(255,255,255,0.5); }
      .sr-icon { font-size: 18px; }
      .sr-title { flex: 1; display: flex; flex-direction: column; }
      .sr-profit { font-weight: 800; color: #81C784; }
      .empty-sales { text-align: center; color: rgba(255,255,255,0.5); padding: 20px 0; font-size: 13px; }

      .quick-spots-label { font-size: 11px; color: rgba(255,255,255,0.6); margin-top: 6px; }
      .quick-spots { display: flex; flex-wrap: wrap; gap: 6px; }
      .btn-quick-spot {
        background: rgba(255,255,255,0.08);
        border: 1px solid rgba(255,255,255,0.15);
        color: #FFF;
        font-size: 11px;
        padding: 6px 10px;
        border-radius: 10px;
        cursor: pointer;
      }
      .btn-quick-spot:active { background: #2E7D32; }

      .search-box { display: flex; gap: 8px; }
      .btn-gps { background: #1565C0; color: #FFF; font-size: 13px; font-weight: 700; }
      .result-item {
        background: rgba(255, 255, 255, 0.08);
        padding: 10px 12px;
        border-radius: 10px;
        cursor: pointer;
        display: flex;
        flex-direction: column;
        min-height: 48px;
        justify-content: center;
      }
      .result-item:hover, .result-item:active { background: rgba(255, 255, 255, 0.2); }
      .result-item small { color: rgba(255, 255, 255, 0.5); font-size: 10px; }

      .loading-overlay {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.8);
        z-index: 150;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .loading-card {
        background: #1a1a2e;
        border: 1px solid rgba(255, 255, 255, 0.2);
        padding: 24px;
        border-radius: 16px;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 12px;
        color: #FFF;
      }
      .loading-spinner {
        width: 36px;
        height: 36px;
        border: 4px solid rgba(255, 255, 255, 0.2);
        border-top-color: #4FC3F7;
        border-radius: 50%;
        animation: spin 0.8s linear infinite;
      }
      @keyframes spin {
        to { transform: rotate(360deg); }
      }

      .btn-danger {
        border: 1px solid rgba(239, 83, 80, 0.5) !important;
        color: #EF5350 !important;
      }
      .btn-danger:hover, .btn-danger:active {
        background: rgba(183, 28, 28, 0.4) !important;
      }
      .btn-danger-action {
        background: #D32F2F;
        color: #FFF;
        font-weight: 700;
      }
      .btn-danger-action:active {
        background: #B71C1C;
      }

      .hud-item.day .hud-val {
        color: #4FC3F7;
      }

      .hud-day-progress {
        background: rgba(26, 26, 46, 0.85);
        backdrop-filter: blur(8px);
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 10px;
        padding: 4px 10px;
        font-size: 11px;
        color: #81C784;
        text-align: center;
        pointer-events: auto;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
      }

      .lobby-steps-indicator {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        margin-bottom: 14px;
      }
      .step-pill {
        font-size: 11px;
        padding: 4px 10px;
        border-radius: 12px;
        background: rgba(255, 255, 255, 0.08);
        color: rgba(255, 255, 255, 0.6);
      }
      .step-pill.active {
        background: #1976D2;
        color: #FFF;
        font-weight: 700;
      }
      .step-pill.completed {
        background: rgba(76, 175, 80, 0.25);
        color: #81C784;
      }
      .step-arrow {
        color: rgba(255, 255, 255, 0.4);
        font-size: 12px;
      }

      .location-picker-inline {
        display: flex;
        flex-direction: column;
        gap: 10px;
      }
      .selected-loc-badge {
        background: rgba(255, 255, 255, 0.06);
        border: 1px dashed rgba(255, 255, 255, 0.2);
        padding: 8px 12px;
        border-radius: 10px;
        font-size: 12px;
        color: rgba(255, 255, 255, 0.8);
      }
      .selected-loc-badge.active {
        border: 1px solid #4CAF50;
        background: rgba(76, 175, 80, 0.15);
        color: #A5D6A7;
      }
      .lobby-actions-row {
        display: flex;
        gap: 8px;
        justify-content: space-between;
        margin-top: 8px;
      }

      .day-report-card {
        max-width: 380px;
        width: 90%;
        background: #1f1f33;
        border: 1px solid rgba(255, 215, 0, 0.35);
        border-radius: 16px;
        padding: 20px;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.6);
      }
      .receipt-header {
        text-align: center;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 4px;
      }
      .receipt-icon { font-size: 32px; }
      .receipt-title { margin: 0; font-size: 18px; color: #FFE082; }
      .receipt-sub { color: rgba(255, 255, 255, 0.6); font-size: 12px; }
      .receipt-divider {
        height: 1px;
        margin: 12px 0;
        border-top: 1px dashed rgba(255, 255, 255, 0.2);
      }
      .receipt-body {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }
      .receipt-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 13px;
        color: rgba(255, 255, 255, 0.85);
      }
      .receipt-pos { color: #81C784; font-weight: 700; }
      .receipt-neg { color: #E57373; font-weight: 700; }
      .receipt-profit {
        font-size: 15px;
        font-weight: 800;
        padding: 6px 0;
      }
      .receipt-profit.profit-pos strong { color: #66BB6A; font-size: 17px; }
      .receipt-profit.profit-neg strong { color: #EF5350; font-size: 17px; }
      .receipt-summary-alltime {
        background: rgba(0, 0, 0, 0.25);
        border-radius: 10px;
        padding: 10px;
        margin-top: 4px;
      }
      .alltime-label {
        font-size: 10px;
        text-transform: uppercase;
        color: rgba(255, 255, 255, 0.5);
        letter-spacing: 0.5px;
        margin-bottom: 6px;
      }
      .alltime-stats-grid {
        display: flex;
        flex-direction: column;
        gap: 4px;
        font-size: 11px;
        color: rgba(255, 255, 255, 0.7);
      }
      .alltime-stats-grid strong { color: #FFF; }

      .reset-confirm-card {
        max-width: 360px;
        width: 90%;
        background: #261616;
        border: 1px solid rgba(239, 83, 80, 0.4);
        border-radius: 16px;
        padding: 20px;
      }

      .hud-item.weather .hud-val {
        color: #FFE082;
      }

      .weather-toast {
        position: fixed;
        top: 70px;
        left: 50%;
        transform: translateX(-50%);
        background: rgba(26, 26, 46, 0.95);
        border: 1px solid rgba(255, 215, 0, 0.4);
        border-radius: 20px;
        padding: 8px 18px;
        display: flex;
        gap: 8px;
        align-items: center;
        color: #FFF;
        font-size: 13px;
        z-index: 1000;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.5);
        animation: fadeInDown 0.3s ease-out;
        transition: opacity 0.4s ease;
      }
      .weather-toast.fade-out { opacity: 0; }
      @keyframes fadeInDown {
        from { opacity: 0; transform: translate(-50%, -10px); }
        to { opacity: 1; transform: translate(-50%, 0); }
      }

      .upgrade-card {
        max-width: 420px;
        width: 90%;
      }
      .upgrade-list {
        display: flex;
        flex-direction: column;
        gap: 10px;
        margin-top: 10px;
        max-height: 55vh;
        overflow-y: auto;
      }
      .upgrade-item {
        background: rgba(255, 255, 255, 0.06);
        border: 1px solid rgba(255, 255, 255, 0.12);
        border-radius: 12px;
        padding: 12px;
        display: flex;
        align-items: center;
        gap: 12px;
      }
      .upgrade-item.owned {
        border: 1px solid rgba(76, 175, 80, 0.4);
        background: rgba(76, 175, 80, 0.1);
      }
      .upgrade-icon { font-size: 28px; }
      .upgrade-info { flex: 1; display: flex; flex-direction: column; gap: 3px; }
      .upgrade-info strong { font-size: 13px; color: #FFF; }
      .upgrade-info small { font-size: 11px; color: rgba(255, 255, 255, 0.65); }
      .badge-owned {
        font-size: 12px;
        font-weight: 700;
        color: #81C784;
        background: rgba(76, 175, 80, 0.2);
        padding: 4px 8px;
        border-radius: 8px;
      }

      .milestone-container {
        position: fixed;
        bottom: 95px;
        right: 16px;
        z-index: 9999;
        pointer-events: none;
      }
      @keyframes slideInRight {
        from { transform: translateX(120%); opacity: 0; }
        to   { transform: translateX(0);    opacity: 1; }
      }
      .milestone-toast {
        background: linear-gradient(135deg, #FFD700, #FF8C00);
        color: #1a1a2e;
        border-radius: 16px;
        padding: 14px 18px;
        display: flex;
        gap: 12px;
        align-items: center;
        animation: slideInRight 0.4s ease-out;
        max-width: 300px;
        box-shadow: 0 8px 32px rgba(255, 215, 0, 0.4);
      }
      .milestone-emoji { font-size: 2rem; }
      .milestone-text strong { display: block; font-size: 13px; font-weight: 800; }
      .milestone-text p { margin: 2px 0 0 0; font-size: 12px; line-height: 1.3; font-weight: 600; }

      /* Account Modal styles */
      .account-card {
        width: 92%;
        max-width: 480px;
        max-height: 85vh;
        overflow-y: auto;
      }
      .account-tabs {
        display: flex;
        gap: 8px;
        margin: 12px 0 16px 0;
        border-bottom: 1px solid rgba(255, 255, 255, 0.15);
        padding-bottom: 8px;
      }
      .acc-tab-btn {
        flex: 1;
        background: rgba(255, 255, 255, 0.08);
        border: 1px solid rgba(255, 255, 255, 0.15);
        border-radius: 8px;
        padding: 8px 12px;
        color: rgba(255, 255, 255, 0.7);
        font-size: 12px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s;
      }
      .acc-tab-btn.active {
        background: #FFD54F;
        border-color: #FFB300;
        color: #1A1A2E;
        font-weight: 700;
      }
      .account-field {
        margin-bottom: 14px;
      }
      .account-field label {
        display: block;
        font-size: 11.5px;
        color: rgba(255, 255, 255, 0.8);
        margin-bottom: 5px;
        font-weight: 600;
      }
      .copy-input-group {
        display: flex;
        gap: 6px;
      }
      .copy-input-group input {
        flex: 1;
        background: rgba(0, 0, 0, 0.35);
        border: 1px solid rgba(255, 255, 255, 0.2);
        border-radius: 8px;
        padding: 8px 12px;
        color: #FFF;
        font-family: monospace;
        font-size: 13px;
      }
      .btn-copy {
        padding: 8px 12px;
        font-size: 11.5px;
        white-space: nowrap;
      }
      .security-warning {
        margin-top: 6px;
        font-size: 11px;
        color: #FFB74D;
        background: rgba(255, 152, 0, 0.12);
        border: 1px solid rgba(255, 152, 0, 0.3);
        border-radius: 6px;
        padding: 6px 10px;
        line-height: 1.4;
      }
      .token-textarea {
        width: 100%;
        box-sizing: border-box;
        background: rgba(0, 0, 0, 0.45);
        border: 1px solid rgba(255, 255, 255, 0.2);
        border-radius: 8px;
        padding: 8px 10px;
        color: #81C784;
        font-family: monospace;
        font-size: 11px;
        resize: vertical;
        word-break: break-all;
      }
      .account-cloud-box {
        margin-top: 16px;
        background: rgba(33, 150, 243, 0.1);
        border: 1px solid rgba(33, 150, 243, 0.3);
        border-radius: 10px;
        padding: 12px;
      }
      .cloud-info strong {
        display: block;
        font-size: 13px;
        color: #90CAF9;
        margin-bottom: 2px;
      }
      .cloud-info small {
        display: block;
        font-size: 11px;
        color: rgba(255, 255, 255, 0.65);
        margin-bottom: 10px;
        line-height: 1.35;
      }
      .btn-cloud {
        background: #1976D2;
        color: #FFF;
        border: none;
        border-radius: 8px;
        padding: 9px 14px;
        font-size: 12px;
        font-weight: 600;
        width: 100%;
        cursor: pointer;
      }
      .btn-cloud:hover {
        background: #2196F3;
      }
      .cloud-status {
        margin-top: 8px;
        font-size: 11px;
        line-height: 1.4;
      }
      .cloud-status.success { color: #81C784; }
      .cloud-status.error { color: #EF5350; }
      .cloud-status.pending { color: #FFD54F; }

      .restore-section {
        background: rgba(255, 255, 255, 0.04);
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 10px;
        padding: 12px;
        margin-bottom: 12px;
      }
      .restore-section h4 {
        margin: 0 0 6px 0;
        font-size: 13px;
        color: #FFD54F;
      }
      .restore-section p {
        margin: 0 0 10px 0;
        font-size: 11px;
        color: rgba(255, 255, 255, 0.6);
        line-height: 1.35;
      }
      .restore-or-divider {
        text-align: center;
        margin: 10px 0;
        position: relative;
        font-size: 11px;
        color: rgba(255, 255, 255, 0.4);
        text-transform: uppercase;
        font-weight: 700;
      }
      .restore-preview-card {
        margin-top: 14px;
        background: rgba(46, 125, 50, 0.15);
        border: 1px solid rgba(129, 199, 132, 0.4);
        border-radius: 10px;
        padding: 12px;
      }
      .preview-badge {
        font-size: 10px;
        font-weight: 700;
        color: #81C784;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-bottom: 6px;
      }
      .restore-preview-card h3 {
        margin: 0 0 8px 0;
        font-size: 13px;
        color: #FFF;
      }
      .preview-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 6px 12px;
        font-size: 11.5px;
        color: rgba(255, 255, 255, 0.85);
        margin-bottom: 12px;
      }
      .btn-confirm-import {
        width: 100%;
        background: #2E7D32;
        color: #FFF;
        border: none;
        border-radius: 8px;
        padding: 11px 16px;
        font-size: 13px;
        font-weight: 700;
        cursor: pointer;
        box-shadow: 0 4px 12px rgba(46, 125, 50, 0.4);
      }
      .btn-confirm-import:hover {
        background: #388E3C;
      }
      .error-banner {
        background: rgba(211, 47, 47, 0.2);
        border: 1px solid rgba(239, 83, 80, 0.5);
        color: #FFCDD2;
        padding: 8px 12px;
        border-radius: 8px;
        font-size: 11.5px;
        margin-top: 10px;
        line-height: 1.4;
      }
      .btn-outline-restore {
        width: 100%;
        background: transparent;
        border: 1px dashed rgba(255, 213, 79, 0.6);
        color: #FFD54F;
        border-radius: 8px;
        padding: 10px;
        font-size: 12px;
        font-weight: 600;
        cursor: pointer;
        margin-top: 10px;
        transition: all 0.2s;
      }
      .btn-outline-restore:hover {
        background: rgba(255, 213, 79, 0.1);
        border-style: solid;
      }

      /* Celebration Modal Styles */
      .celebration-backdrop {
        position: fixed;
        inset: 0;
        background: rgba(10, 10, 20, 0.88);
        backdrop-filter: blur(8px);
        z-index: 10000;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 16px;
      }
      .celebration-card {
        background: linear-gradient(145deg, #1e1e38, #121224);
        border: 2px solid #FFD54F;
        border-radius: 20px;
        box-shadow: 0 12px 40px rgba(0, 0, 0, 0.8), 0 0 30px rgba(255, 213, 79, 0.35);
        max-width: 420px;
        width: 100%;
        padding: 24px 20px;
        text-align: center;
        color: #FFF;
        position: relative;
        animation: celebrationPop 0.35s cubic-bezier(0.175, 0.885, 0.32, 1.275);
      }
      @keyframes celebrationPop {
        0% { transform: scale(0.8); opacity: 0; }
        100% { transform: scale(1); opacity: 1; }
      }
      .celebration-fireworks {
        font-size: 28px;
        letter-spacing: 4px;
        margin-bottom: 6px;
        animation: floatPulse 1.8s ease-in-out infinite;
      }
      @keyframes floatPulse {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-4px); }
      }
      .celebration-badge {
        display: inline-block;
        padding: 3px 12px;
        border-radius: 12px;
        font-size: 11px;
        font-weight: 800;
        text-transform: uppercase;
        letter-spacing: 1px;
        margin-bottom: 10px;
      }
      .celebration-badge.badge-level {
        background: linear-gradient(90deg, #FFB300, #FF6F00);
        color: #000;
      }
      .celebration-badge.badge-survival {
        background: linear-gradient(90deg, #4FC3F7, #0288D1);
        color: #000;
      }
      .celebration-title {
        font-size: 18px;
        font-weight: 800;
        color: #FFD54F;
        margin: 0 0 6px 0;
        line-height: 1.3;
      }
      .celebration-subtitle {
        font-size: 13px;
        color: rgba(255, 255, 255, 0.85);
        margin: 0 0 16px 0;
        line-height: 1.4;
      }
      .celebration-items-container {
        background: rgba(0, 0, 0, 0.35);
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 14px;
        padding: 12px;
        margin-bottom: 18px;
        text-align: left;
      }
      .celebration-items-header {
        font-size: 10.5px;
        font-weight: 700;
        color: #FFE082;
        letter-spacing: 0.5px;
        margin-bottom: 8px;
      }
      .celebration-items-list {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }
      .celebration-item-card {
        display: flex;
        align-items: center;
        gap: 10px;
        background: rgba(255, 255, 255, 0.06);
        border-radius: 10px;
        padding: 8px 10px;
      }
      .celebration-item-icon {
        font-size: 24px;
        flex-shrink: 0;
        width: 32px;
        text-align: center;
      }
      .celebration-item-info {
        flex: 1;
        min-width: 0;
      }
      .celebration-item-name {
        font-size: 13px;
        font-weight: 700;
        color: #FFF;
      }
      .celebration-item-desc {
        font-size: 11px;
        color: rgba(255, 255, 255, 0.65);
        line-height: 1.3;
      }
      .btn-enable-unlocked {
        background: linear-gradient(135deg, #43A047, #2E7D32);
        color: #FFF;
        border: none;
        border-radius: 8px;
        padding: 6px 10px;
        font-size: 11px;
        font-weight: 700;
        cursor: pointer;
        flex-shrink: 0;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
      }
      .btn-enable-unlocked:active {
        transform: scale(0.95);
      }
      .badge-perk-active {
        background: rgba(33, 150, 243, 0.2);
        border: 1px solid #42A5F5;
        color: #90CAF9;
        font-size: 10px;
        font-weight: 700;
        padding: 3px 8px;
        border-radius: 6px;
        flex-shrink: 0;
      }
      .btn-celebration-continue {
        width: 100%;
        background: linear-gradient(135deg, #FFB300, #F57C00);
        color: #000;
        border: none;
        border-radius: 12px;
        padding: 12px 18px;
        font-size: 14px;
        font-weight: 800;
        cursor: pointer;
        box-shadow: 0 4px 15px rgba(245, 124, 0, 0.4);
        transition: transform 0.1s;
      }
      .btn-celebration-continue:active {
        transform: scale(0.97);
      }

      /* Perks Modal Styles */
      .perks-modal-card {
        max-width: 480px;
        width: 95%;
        max-height: 85vh;
        display: flex;
        flex-direction: column;
      }
      .perks-body {
        overflow-y: auto;
        padding: 6px 0;
      }
      .perks-hint {
        font-size: 11.5px;
        color: rgba(255, 255, 255, 0.7);
        margin: 0 0 12px 0;
        line-height: 1.4;
      }
      .perks-grid {
        display: flex;
        flex-direction: column;
        gap: 10px;
      }
      .perk-card {
        display: flex;
        gap: 12px;
        background: rgba(255, 255, 255, 0.05);
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 12px;
        padding: 10px 12px;
        align-items: flex-start;
      }
      .perk-card.perk-unlocked {
        border-color: rgba(76, 175, 80, 0.5);
        background: rgba(76, 175, 80, 0.08);
      }
      .perk-card.perk-locked {
        opacity: 0.55;
        filter: grayscale(0.5);
      }
      .perk-card-icon {
        font-size: 26px;
        line-height: 1;
        flex-shrink: 0;
        padding-top: 2px;
      }
      .perk-card-body {
        flex: 1;
        min-width: 0;
      }
      .perk-card-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 3px;
      }
      .perk-name {
        font-size: 13px;
        font-weight: 700;
        color: #FFF;
      }
      .perk-status {
        font-size: 10px;
        font-weight: 700;
        padding: 2px 6px;
        border-radius: 6px;
      }
      .status-unlocked {
        background: rgba(76, 175, 80, 0.2);
        color: #81C784;
      }
      .status-locked {
        background: rgba(255, 255, 255, 0.1);
        color: rgba(255, 255, 255, 0.5);
      }
      .perk-desc {
        font-size: 11px;
        color: rgba(255, 255, 255, 0.75);
        line-height: 1.35;
        margin-bottom: 4px;
      }
      .perk-benefit {
        font-size: 11px;
        color: #FFE082;
        line-height: 1.35;
      }

      /* ================= How To Play Modal Styles ================= */
      .btn-quick-howtoplay {
        background: rgba(255, 179, 0, 0.15);
        color: #FFD54F;
        border: 1px solid rgba(255, 179, 0, 0.35);
        border-radius: 8px;
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        font-size: 15px;
        transition: all 0.15s ease;
      }
      .btn-quick-howtoplay:hover {
        background: rgba(255, 179, 0, 0.28);
        border-color: #FFD54F;
        transform: scale(1.05);
      }
      .howtoplay-modal-card {
        max-width: 480px;
        width: 95%;
        max-height: 85vh;
        display: flex;
        flex-direction: column;
        background: #1A1A2E;
        border: 1px solid rgba(255, 215, 0, 0.25);
        box-shadow: 0 10px 30px rgba(0,0,0,0.6);
        border-radius: 14px;
        padding: 16px;
      }
      .howtoplay-title-wrapper {
        display: flex;
        align-items: center;
        gap: 10px;
      }
      .howtoplay-icon {
        font-size: 26px;
        line-height: 1;
      }
      .howtoplay-tabs {
        display: flex;
        gap: 6px;
        margin: 12px 0 10px 0;
        background: rgba(0, 0, 0, 0.35);
        padding: 4px;
        border-radius: 10px;
      }
      .howtoplay-tab-btn {
        flex: 1;
        background: transparent;
        border: none;
        color: rgba(255, 255, 255, 0.65);
        font-size: 11px;
        font-weight: 600;
        padding: 8px 4px;
        border-radius: 7px;
        cursor: pointer;
        transition: all 0.15s ease;
        text-align: center;
        white-space: nowrap;
      }
      .howtoplay-tab-btn.active {
        background: #FFB300;
        color: #1A1A2E;
        font-weight: 700;
        box-shadow: 0 2px 6px rgba(0,0,0,0.3);
      }
      .howtoplay-body {
        flex: 1;
        overflow-y: auto;
        padding-right: 4px;
        margin-top: 4px;
      }
      .howtoplay-body::-webkit-scrollbar {
        width: 4px;
      }
      .howtoplay-body::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.2);
        border-radius: 2px;
      }
      .acc-list {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }
      .acc-item {
        background: rgba(255, 255, 255, 0.04);
        border: 1px solid rgba(255, 255, 255, 0.08);
        border-radius: 10px;
        overflow: hidden;
        transition: border-color 0.15s ease;
      }
      .acc-item.open {
        border-color: rgba(255, 179, 0, 0.35);
        background: rgba(255, 179, 0, 0.04);
      }
      .acc-head {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 10px 12px;
        cursor: pointer;
        user-select: none;
      }
      .acc-badge {
        font-size: 10px;
        font-weight: 700;
        background: #FFB300;
        color: #1A1A2E;
        padding: 2px 6px;
        border-radius: 5px;
        flex-shrink: 0;
      }
      .acc-icon {
        font-size: 16px;
        flex-shrink: 0;
      }
      .acc-title {
        flex: 1;
        font-size: 13px;
        font-weight: 600;
        color: #FFF;
      }
      .acc-arrow {
        font-size: 10px;
        color: rgba(255, 255, 255, 0.4);
        transition: transform 0.2s ease;
      }
      .acc-item.open .acc-arrow {
        transform: rotate(180deg);
        color: #FFB300;
      }
      .acc-body {
        display: none;
        padding: 0 12px 12px 12px;
        font-size: 12px;
        line-height: 1.45;
        color: rgba(255, 255, 255, 0.82);
        border-top: 1px dashed rgba(255, 255, 255, 0.08);
        margin-top: 2px;
        padding-top: 8px;
      }
      .acc-item.open .acc-body {
        display: block;
      }
      .acc-body ul {
        margin: 6px 0 6px 16px;
        padding: 0;
      }
      .acc-body li {
        margin-bottom: 4px;
      }
      .callout-tip {
        background: rgba(255, 179, 0, 0.12);
        border-left: 3px solid #FFB300;
        border-radius: 6px;
        padding: 8px 10px;
        margin-top: 8px;
        font-size: 11px;
        line-height: 1.4;
        color: #FFF8E1;
      }
      .badge-fresh-tag {
        background: rgba(239, 83, 80, 0.25);
        color: #EF5350;
        border: 1px solid rgba(239, 83, 80, 0.4);
        padding: 1px 5px;
        border-radius: 4px;
        font-size: 10px;
        font-weight: bold;
      }
      .badge-storable-tag {
        background: rgba(33, 150, 243, 0.25);
        color: #64B5F6;
        border: 1px solid rgba(33, 150, 243, 0.4);
        padding: 1px 5px;
        border-radius: 4px;
        font-size: 10px;
        font-weight: bold;
      }
      .badge-pkg-tag {
        background: rgba(102, 187, 106, 0.25);
        color: #81C784;
        border: 1px solid rgba(102, 187, 106, 0.4);
        padding: 1px 5px;
        border-radius: 4px;
        font-size: 10px;
        font-weight: bold;
      }
      .prog-header-note {
        font-size: 12px;
        color: rgba(255, 255, 255, 0.8);
        background: rgba(255, 255, 255, 0.05);
        padding: 8px 10px;
        border-radius: 8px;
        margin-bottom: 10px;
        line-height: 1.4;
      }
      .prog-levels-list {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }
      .prog-level-card {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        background: rgba(255, 255, 255, 0.04);
        border: 1px solid rgba(255, 255, 255, 0.08);
        border-radius: 10px;
        padding: 10px 12px;
      }
      .prog-level-card.current-level {
        border-color: #4CAF50;
        background: rgba(76, 175, 80, 0.08);
      }
      .prog-level-badge {
        font-size: 11px;
        font-weight: 700;
        background: rgba(255, 255, 255, 0.1);
        color: #FFD54F;
        padding: 3px 8px;
        border-radius: 6px;
        flex-shrink: 0;
      }
      .prog-level-content {
        flex: 1;
      }
      .prog-level-title {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 3px;
        font-size: 13px;
        color: #FFF;
      }
      .tag-current {
        font-size: 9px;
        background: #4CAF50;
        color: #FFF;
        padding: 2px 6px;
        border-radius: 4px;
        font-weight: 700;
      }
      .prog-level-desc {
        font-size: 11px;
        color: rgba(255, 255, 255, 0.7);
        line-height: 1.35;
      }
      .prog-unlocked-items {
        margin-top: 4px;
        font-size: 10px;
        color: #FFE082;
        font-weight: 600;
      }

      /* Hourglass and Location Search Animations */
      @keyframes hourglass-flip {
        0% { transform: rotate(0deg) scale(1); }
        40% { transform: rotate(180deg) scale(1.08); }
        50% { transform: rotate(180deg) scale(1); }
        90% { transform: rotate(360deg) scale(1.08); }
        100% { transform: rotate(360deg) scale(1); }
      }
      .hourglass-anim {
        display: inline-block;
        animation: hourglass-flip 1.8s infinite ease-in-out;
        will-change: transform;
      }
      .hourglass-anim-btn {
        display: inline-block;
        animation: hourglass-flip 1.8s infinite ease-in-out;
        font-size: 13px;
        vertical-align: middle;
      }
      .search-hourglass-box {
        text-align: center;
        padding: 24px 12px;
        background: rgba(255, 213, 79, 0.05);
        border: 1px dashed rgba(255, 213, 79, 0.25);
        border-radius: 12px;
        margin: 8px 0;
      }
      .search-hourglass-title {
        font-size: 13px;
        font-weight: 700;
        color: #FFE082;
        margin-bottom: 4px;
      }
      .search-hourglass-sub {
        font-size: 11px;
        color: #90A4AE;
      }
      .search-empty-box {
        text-align: center;
        padding: 20px 10px;
        background: rgba(255, 255, 255, 0.03);
        border: 1px solid rgba(255, 255, 255, 0.08);
        border-radius: 12px;
        margin: 8px 0;
      }

      /* Recent Searches & Chips */
      .search-recent-section {
        margin: 10px 0 8px 0;
        padding: 8px 10px;
        background: rgba(30, 35, 48, 0.65);
        border: 1px solid rgba(255, 255, 255, 0.08);
        border-radius: 8px;
        text-align: left;
      }
      .search-recent-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 6px;
        font-size: 11px;
        color: #B0BEC5;
      }
      .btn-clear-recent {
        background: none;
        border: none;
        color: #EF5350;
        font-size: 11px;
        cursor: pointer;
        padding: 2px 6px;
        border-radius: 4px;
        opacity: 0.8;
        transition: opacity 0.2s, background 0.2s;
      }
      .btn-clear-recent:hover {
        opacity: 1;
        background: rgba(239, 83, 80, 0.15);
      }
      .search-recent-chips {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
      }
      .chip-recent-spot {
        background: rgba(255, 213, 79, 0.12);
        border: 1px solid rgba(255, 213, 79, 0.3);
        color: #FFE082;
        padding: 4px 10px;
        border-radius: 14px;
        font-size: 11px;
        font-weight: 500;
        cursor: pointer;
        transition: background 0.15s, transform 0.15s, border-color 0.15s;
        max-width: 100%;
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
      }
      .chip-recent-spot:hover, .chip-recent-spot:active {
        background: rgba(255, 213, 79, 0.25);
        border-color: #FFD54F;
        transform: translateY(-1px);
      }

      /* Google Trends Card styles */
      .trends-dialog-card {
        background: #1e1e30;
        border: 1px solid rgba(255, 213, 79, 0.3);
        box-shadow: 0 12px 40px rgba(0, 0, 0, 0.7);
      }
      .trend-card-item {
        transition: transform 0.15s ease, background 0.15s ease;
      }
      .trend-card-item:hover {
        background: rgba(255, 255, 255, 0.08) !important;
        transform: translateY(-1px);
      }
      .btn-read-news:hover {
        background: #37474F !important;
        border-color: #607D8B !important;
      }

      /* Audio Toggle Button */
      .btn-audio-toggle {
        background: rgba(255, 255, 255, 0.08);
        border: 1px solid rgba(255, 255, 255, 0.15);
        color: #ECEFF1;
        width: 32px;
        height: 32px;
        border-radius: 8px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
        cursor: pointer;
        transition: background 0.15s, transform 0.1s, border-color 0.15s;
        padding: 0;
      }
      .btn-audio-toggle:hover {
        background: rgba(255, 255, 255, 0.18);
        border-color: rgba(255, 213, 79, 0.4);
        transform: translateY(-1px);
      }
      .btn-audio-toggle:active {
        transform: scale(0.95);
      }

      /* Audio Settings Modal Styles */
      .audio-settings-card {
        background: #1e1e30;
        border: 1px solid rgba(255, 213, 79, 0.35);
        border-radius: 16px;
        box-shadow: 0 16px 48px rgba(0, 0, 0, 0.75);
        width: 90%;
        max-width: 380px;
        padding: 18px 20px;
        animation: scale-up 0.2s ease;
      }
      .audio-settings-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 16px;
        padding-bottom: 10px;
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);
      }
      .audio-control-group {
        background: rgba(255, 255, 255, 0.04);
        border: 1px solid rgba(255, 255, 255, 0.08);
        border-radius: 12px;
        padding: 12px 14px;
        margin-bottom: 12px;
      }
      .audio-control-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 10px;
      }
      .audio-label-group {
        display: flex;
        align-items: center;
        gap: 10px;
      }
      .audio-icon {
        font-size: 22px;
        background: rgba(255, 255, 255, 0.06);
        width: 36px;
        height: 36px;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .btn-audio-pill {
        background: #37474F;
        border: 1px solid rgba(255, 255, 255, 0.2);
        color: #B0BEC5;
        font-size: 11px;
        font-weight: 700;
        padding: 4px 12px;
        border-radius: 16px;
        cursor: pointer;
        transition: all 0.2s;
      }
      .btn-audio-pill.active {
        background: #4CAF50;
        border-color: #81C784;
        color: #FFFFFF;
        box-shadow: 0 2px 8px rgba(76, 175, 80, 0.4);
      }
      .audio-slider-row {
        display: flex;
        align-items: center;
        gap: 10px;
      }
      .audio-slider-row input[type="range"] {
        flex: 1;
        accent-color: #FFD54F;
        cursor: pointer;
      }
      .audio-vol-pct {
        font-size: 12px;
        font-weight: 700;
        color: #FFE082;
        width: 38px;
        text-align: right;
        font-family: monospace;
      }
      .audio-quick-actions {
        display: flex;
        gap: 10px;
        margin-top: 14px;
      }

      /* ==========================================================================
         LOBBY DASHBOARD, PAUSE MODAL & PWA INSTALL GUIDE STYLES
         Design System: Dark Navy (#1a1a2e) + Accent Gold (#FFD54F)
         ========================================================================== */

      @keyframes floatCup {
        0%, 100% {
          transform: translateY(0px) rotate(0deg);
        }
        50% {
          transform: translateY(-7px) rotate(2deg);
        }
      }

      .lobby-dashboard-card {
        background: linear-gradient(180deg, rgba(26, 26, 46, 0.98) 0%, rgba(18, 22, 34, 0.98) 100%) !important;
        border: 1.5px solid rgba(255, 213, 79, 0.45) !important;
        box-shadow: 0 16px 40px rgba(0, 0, 0, 0.75), 0 0 25px rgba(255, 213, 79, 0.15) !important;
        border-radius: 16px !important;
        max-width: 460px !important;
        width: 92% !important;
        padding: 16px 18px !important;
        box-sizing: border-box;
      }

      .dash-top-bar {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 6px;
        width: 100%;
        padding-bottom: 10px;
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        box-sizing: border-box;
      }
      .dash-pill {
        background: rgba(255, 255, 255, 0.05);
        border: 1px solid rgba(255, 255, 255, 0.12);
        border-radius: 14px;
        padding: 3px 8px;
        font-size: 11px;
        font-weight: 600;
        color: #ECEFF1;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        gap: 4px;
        white-space: nowrap;
        transition: background 0.15s ease, border-color 0.15s ease;
      }
      .dash-pill:hover {
        background: rgba(255, 255, 255, 0.1);
      }

      .dash-hero {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 6px;
        margin: 12px 0 10px 0;
        text-align: center;
      }
      .dash-squircle {
        width: 62px;
        height: 62px;
        border-radius: 18px;
        background: linear-gradient(135deg, rgba(255, 213, 79, 0.18), rgba(255, 179, 0, 0.06));
        border: 1.5px solid rgba(255, 213, 79, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        animation: floatCup 3.5s ease-in-out infinite;
        box-shadow: 0 4px 18px rgba(255, 213, 79, 0.22);
      }
      .dash-squircle-icon {
        font-size: 32px;
        filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.4));
      }
      .dash-title-row {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
      }
      .dash-title {
        margin: 0;
        font-size: 18px;
        font-weight: 800;
        color: #FFD54F;
        letter-spacing: 0.3px;
        text-shadow: 0 2px 8px rgba(0, 0, 0, 0.6);
      }
      .dash-btn-pen {
        background: rgba(255, 255, 255, 0.08);
        border: 1px solid rgba(255, 213, 79, 0.3);
        border-radius: 50%;
        width: 26px;
        height: 26px;
        cursor: pointer;
        color: #FFD54F;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 11px;
        transition: transform 0.15s ease, background 0.15s ease;
      }
      .dash-btn-pen:hover {
        background: rgba(255, 213, 79, 0.2);
        transform: scale(1.08);
      }
      .dash-tagline {
        font-size: 11px;
        color: #B0BEC5;
      }

      .dash-weather-card {
        background: rgba(255, 255, 255, 0.04);
        border: 1px solid rgba(255, 213, 79, 0.22);
        border-radius: 10px;
        padding: 8px 12px;
        display: flex;
        align-items: center;
        gap: 10px;
        margin-bottom: 12px;
        width: 100%;
        box-sizing: border-box;
      }

      .dash-stats-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 6px;
        margin-bottom: 12px;
        width: 100%;
        box-sizing: border-box;
      }
      .dash-stat-item {
        background: rgba(255, 255, 255, 0.04);
        border: 1px solid rgba(255, 255, 255, 0.08);
        border-radius: 10px;
        padding: 8px 4px;
        text-align: center;
        display: flex;
        flex-direction: column;
        gap: 2px;
      }
      .dash-stat-label {
        font-size: 9px;
        color: #90A4AE;
        text-transform: uppercase;
        letter-spacing: 0.3px;
      }
      .dash-stat-val {
        font-size: 13px;
        font-weight: 700;
        color: #FFD54F;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .dash-stat-sub {
        font-size: 10px;
        color: #B0BEC5;
      }

      .dash-actions-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 6px;
        margin-bottom: 14px;
        width: 100%;
        box-sizing: border-box;
      }
      .dash-action-btn {
        background: rgba(30, 40, 70, 0.75);
        border: 1px solid rgba(100, 181, 246, 0.3);
        border-radius: 10px;
        padding: 9px 4px;
        color: #E3F2FD;
        font-size: 11px;
        font-weight: 600;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 4px;
        cursor: pointer;
        transition: transform 0.15s ease, background 0.15s ease, border-color 0.15s ease;
      }
      .dash-action-btn:hover {
        background: rgba(45, 60, 100, 0.9);
        border-color: rgba(255, 213, 79, 0.5);
        color: #FFD54F;
        transform: translateY(-1px);
      }
      .dash-action-btn:active {
        transform: scale(0.96);
      }
      .dash-action-icon {
        font-size: 18px;
      }

      .dash-continue-btn {
        width: 100%;
        padding: 13px 16px;
        font-size: 14px;
        font-weight: 800;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        background: linear-gradient(135deg, #FFB300 0%, #F57C00 100%);
        color: #1a1a2e;
        border: none;
        border-radius: 12px;
        cursor: pointer;
        box-shadow: 0 4px 15px rgba(255, 179, 0, 0.4);
        transition: transform 0.15s ease, box-shadow 0.15s ease;
        box-sizing: border-box;
      }
      .dash-continue-btn:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(255, 179, 0, 0.55);
      }
      .dash-continue-btn:active {
        transform: translateY(1px);
        box-shadow: 0 2px 8px rgba(255, 179, 0, 0.3);
      }

      .dash-secondary-row {
        display: flex;
        justify-content: space-between;
        gap: 6px;
        margin-top: 10px;
        width: 100%;
        box-sizing: border-box;
      }
      .dash-secondary-btn {
        flex: 1;
        padding: 8px 4px;
        font-size: 10.5px;
        font-weight: 600;
        background: transparent;
        border: 1px solid rgba(255, 255, 255, 0.15);
        border-radius: 8px;
        color: #B0BEC5;
        cursor: pointer;
        transition: color 0.15s ease, border-color 0.15s ease, background 0.15s ease;
        white-space: nowrap;
        text-align: center;
      }
      .dash-secondary-btn:hover {
        border-color: rgba(255, 213, 79, 0.4);
        color: #FFD54F;
        background: rgba(255, 255, 255, 0.03);
      }

      /* Pause Modal */
      .pause-backdrop {
        backdrop-filter: blur(8px) !important;
        background: rgba(10, 12, 20, 0.75) !important;
      }
      .pause-card {
        max-width: 400px !important;
        width: 90% !important;
        border: 1.5px solid rgba(255, 213, 79, 0.4) !important;
        background: linear-gradient(180deg, rgba(26, 26, 46, 0.98) 0%, rgba(18, 22, 34, 0.98) 100%) !important;
        box-shadow: 0 20px 50px rgba(0, 0, 0, 0.8), 0 0 25px rgba(255, 213, 79, 0.15) !important;
        border-radius: 16px !important;
        padding: 18px !important;
        box-sizing: border-box;
      }
      .pause-header {
        text-align: center;
        margin-bottom: 16px;
      }
      .pause-squircle {
        width: 56px;
        height: 56px;
        border-radius: 16px;
        background: linear-gradient(135deg, rgba(255, 213, 79, 0.18), rgba(255, 179, 0, 0.06));
        border: 1.5px solid rgba(255, 213, 79, 0.5);
        margin: 0 auto 8px auto;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 4px 16px rgba(255, 213, 79, 0.25);
      }
      .pause-icon {
        font-size: 26px;
      }
      .pause-title {
        margin: 0 0 4px 0;
        font-size: 16px;
        font-weight: 800;
        color: #FFD54F;
        letter-spacing: 0.5px;
      }
      .pause-subtitle {
        margin: 0;
        font-size: 11px;
        color: #90A4AE;
      }
      .pause-actions-list {
        display: flex;
        flex-direction: column;
        gap: 8px;
        width: 100%;
        box-sizing: border-box;
      }
      .pause-btn {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 10px 14px;
        border-radius: 10px;
        text-align: left;
        cursor: pointer;
        border: 1px solid rgba(255, 255, 255, 0.12);
        background: rgba(255, 255, 255, 0.04);
        color: #ECEFF1;
        transition: all 0.15s ease;
        box-sizing: border-box;
        width: 100%;
      }
      .pause-btn:hover {
        background: rgba(255, 255, 255, 0.08);
        border-color: rgba(255, 213, 79, 0.4);
        transform: translateX(2px);
      }
      .pause-btn-resume {
        background: linear-gradient(135deg, #FFB300 0%, #F57C00 100%) !important;
        border: none !important;
        color: #1a1a2e !important;
      }
      .pause-btn-resume:hover {
        transform: translateY(-1px) !important;
        box-shadow: 0 4px 14px rgba(255, 179, 0, 0.4) !important;
      }
      .pause-btn-resume .btn-text strong {
        color: #1a1a2e !important;
      }
      .pause-btn-resume .btn-text small {
        color: rgba(26, 26, 46, 0.8) !important;
      }
      .pause-btn .btn-icon {
        font-size: 20px;
        flex-shrink: 0;
      }
      .pause-btn .btn-text {
        display: flex;
        flex-direction: column;
        gap: 2px;
      }
      .pause-btn .btn-text strong {
        font-size: 12.5px;
        font-weight: 700;
        color: #ECEFF1;
      }
      .pause-btn .btn-text small {
        font-size: 10.5px;
        color: #90A4AE;
      }
      .pause-btn-danger {
        border-color: rgba(239, 83, 80, 0.3) !important;
      }
      .pause-btn-danger:hover {
        background: rgba(239, 83, 80, 0.12) !important;
        border-color: #EF5350 !important;
      }
      .pause-btn-danger .btn-text strong {
        color: #FF8A80 !important;
      }

      /* PWA Install Guide Modal */
      .install-guide-card {
        max-width: 420px !important;
        width: 90% !important;
        border-radius: 16px !important;
        background: linear-gradient(180deg, rgba(26, 26, 46, 0.98) 0%, rgba(18, 22, 34, 0.98) 100%) !important;
        border: 1.5px solid rgba(100, 181, 246, 0.4) !important;
        box-shadow: 0 20px 50px rgba(0, 0, 0, 0.8) !important;
        padding: 18px !important;
        box-sizing: border-box;
      }
      .install-steps-list {
        display: flex;
        flex-direction: column;
        gap: 8px;
        margin: 12px 0;
        width: 100%;
        box-sizing: border-box;
      }
      .install-step-item {
        display: flex;
        gap: 10px;
        align-items: flex-start;
        background: rgba(255, 255, 255, 0.04);
        border: 1px solid rgba(255, 255, 255, 0.08);
        border-radius: 10px;
        padding: 8px 10px;
        box-sizing: border-box;
      }
      .install-step-num {
        background: #1976D2;
        color: #FFF;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 11px;
        font-weight: bold;
        flex-shrink: 0;
      }
      .install-step-desc {
        font-size: 11.5px;
        color: #ECEFF1;
        line-height: 1.4;
      }
    `;
    document.head.appendChild(style);
  }
}
